<template>
  <div class="dash-root">

    <!-- ══ HEADER ══ -->
    <div class="dash-header">
      <div class="dash-header-left">
        <div class="dash-logo">H&F</div>
        <div>
          <h1>
            Intakes <span class="dash-count">{{ total }}</span>
            <!-- Exportar / Estadísticas recorren todas las páginas: mientras
                 tanto se muestra por dónde va -->
            <span v-if="bulkLoading" class="dash-loading-more" :title="`${bulkLabel} (${bulkLoaded} de ${total})`">
              <span class="dash-loading-dot"></span>
              {{ bulkLabel }}: {{ bulkLoaded }} de {{ total }}
            </span>
            <span v-else-if="bulkError" class="dash-loading-more dash-bulk-error" :title="bulkError">
              ⚠ {{ bulkError }}
            </span>
          </h1>
          <p class="dash-sub">Panel de leads y shortlists</p>
        </div>
      </div>
      <div class="header-actions">
        <button class="btn-generate" @click="openDiscordModal" title="Administrar usuarios de Discord">🔔 Discord</button>
        <button class="btn-generate" @click="showPartners = true" title="Partners oficiales que aparecen en el formulario">🤝 Partners</button>
        <button v-if="isFullAccessAdmin" class="btn-generate" @click="openRepartoModal" title="Reparto de leads por % y horario, por campaña">📊 Reparto</button>
        <button class="btn-generate" @click="openStats" title="Ver estadísticas">📊 Estadísticas</button>
        <button class="btn-icon" :class="{ spinning: loading || bulkLoading }" @click="reloadIntakes" title="Actualizar">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 4v6h-6"/><path d="M1 20v-6h6"/><path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/></svg>
        </button>
        <button class="btn-generate" @click="openGenerateModal" :disabled="!intakes.length" title="Generar link con meta">
          Generar
        </button>
        <!-- El listado trae una sola página: el Excel se arma recorriendo el
             resto en el momento, con los filtros que estén puestos -->
        <button
          class="btn-export"
          @click="exportToExcel"
          :disabled="!total || bulkLoading"
          :title="bulkLoading ? 'Esperá a que termine' : 'Exportar a Excel (todos los intakes que cumplen los filtros)'"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Exportar
        </button>
      </div>
    </div>

    <!-- ══ SEARCH ══ -->
    <div class="search-bar">
      <div class="search-input-wrap">
        <svg class="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Buscar por nombre, email, ciudad, ID alojamiento..."
          class="search-input"
        />
        <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">×</button>
      </div>
      <div class="search-filters">
        <select v-model="filterTier" class="filter-select">
          <option value="">Todos los tiers</option>
          <option value="hot">🔥 Hot</option>
          <option value="warm">⚡ Warm</option>
          <option value="cool">🔎 Cool</option>
          <option value="cold">❄️ Cold</option>
        </select>
        <!-- Etapa del tablero de Mensajería en la que está el chat del lead -->
        <select v-model="filterEtapa" class="filter-select">
          <option value="">Todas las etapas</option>
          <option v-for="opt in etapaOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <select v-model="filterUrgencia" class="filter-select">
          <option value="">Toda urgencia</option>
          <option value="urgente">Urgente</option>
          <option value="pronto">Pronto</option>
          <option value="explorando">Explorando</option>
        </select>

        <select v-model="filterCodigo" class="filter-select">
          <option value="">Todos los códigos</option>
          <option v-for="opt in codigoOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>
        <select v-model="filterAdmin" class="filter-select">
          <option value="">Todos los admins</option>
          <option v-for="opt in adminOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
        </select>

        <!-- Filtro por fecha de registro (created_at) -->
        <div class="filter-date-group">
          <span class="filter-date-label">Registro</span>
          <input type="date" v-model="filterFechaDesde" class="filter-date" title="Registrados desde" />
          <span class="filter-date-sep">→</span>
          <input type="date" v-model="filterFechaHasta" class="filter-date" title="Registrados hasta" />
          <button
            v-if="filterFechaDesde || filterFechaHasta"
            class="filter-date-clear"
            @click="clearFechaRegistro"
            title="Limpiar fechas"
          >×</button>
        </div>
      </div>
    </div>

    <!-- ══ LOADING ══
         Solo en la primera carga. Al cambiar de página o de filtro la tabla se
         queda en pantalla atenuada (.is-refreshing) en vez de desaparecer. -->
    <HyFLoader v-if="loading && !intakes.length" variant="inline" />

    <!-- ══ ERROR ══ -->
    <div v-else-if="error" class="error-banner">
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      {{ error }}
    </div>

    <!-- ══ TABLE ══ -->
    <div v-else class="table-wrap" :class="{ 'is-refreshing': loading }">
      <table class="dash-table">
        <!-- Los anchos se fijan por clase (ver .th-*): la tabla es table-layout
             fixed, así que lo que declara el <th> manda sobre todas las filas. -->
        <thead>
          <tr>
            <th class="th-id"><strong>ID</strong></th>
            <th class="th-lead"><strong>Lead</strong></th>
            <th class="th-destino"><strong>Destino</strong></th>
            <th class="th-fechas"><strong>Estadía</strong></th>
            <th class="th-req"><strong>Requerimiento</strong></th>
            <th class="th-etapa"><strong>Etapa</strong></th>
            <th class="th-score"><strong>Score</strong></th>
            <th class="th-admin"><strong>Admin</strong></th>
            <th class="th-registro"><strong>Registro</strong></th>
            <th class="th-acciones"></th>
          </tr>
        </thead>
        <tbody v-for="group in pagedIntakes" :key="group.representative.id">

            <!-- Fila principal (representante del grupo o intake simple) -->
            <tr
              class="intake-row"
              :id="`intake-row-${group.representative.id}`"
              :class="[{ expanded: group.isGroup ? expandedGroupPhone === group.key : expandedId === getActiveIntake(group).id }, `tier-row-${leadTier(getActiveIntake(group))}`]"
              :style="getGroupRowStyle(group)"
              @click="group.isGroup ? toggleGroup(group.key) : toggleExpand(getActiveIntake(group).id)"
            >
              <td class="td-id" data-label="ID">
                <span class="id-badge">#{{ getActiveIntake(group).id }}</span>
              </td>

              <!-- Lead: identidad y contacto en una sola columna. Antes eran dos
                   columnas angostas y el nombre largo se montaba encima del mail
                   (la tabla es table-layout:fixed). Ahora cada línea se recorta
                   con puntos suspensivos y el valor entero se lee en el tooltip. -->
              <td class="td-lead" data-label="Lead">
                <div class="lead-head">
                  <span class="lead-nombre cell-clip" :title="getActiveIntake(group).nombre || ''">{{ getActiveIntake(group).nombre || '—' }}</span>
                  <span
                    v-if="group.isGroup"
                    class="badge-group-count"
                    :title="`${group.members.length} intakes de la misma persona`"
                  >×{{ group.members.length }}</span>
                </div>
                <div class="lead-mail cell-clip" :title="getActiveIntake(group).email || ''">{{ getActiveIntake(group).email || '—' }}</div>
                <div class="lead-foot" v-if="tieneLineaContacto(getActiveIntake(group))">
                  <span v-if="getActiveIntake(group).whatsapp" class="lead-phone">{{ getActiveIntake(group).whatsapp }}</span>
                  <span v-if="leadOrigen(getActiveIntake(group))" class="lead-origen">{{ leadOrigen(getActiveIntake(group)) }}</span>
                  <!-- El badge chico es para el número malo al que TODAVÍA no se
                       le pidió reemplazo. Si ya se le pidió, en su lugar va el
                       otro badge, que explica el estado completo en el tooltip. -->
                  <span
                    v-if="hasIncompletePhone(getActiveIntake(group)) && !awaitingPhoneByEmail(getActiveIntake(group))"
                    class="badge-phone-bad"
                    :title="incompletePhoneTitle(getActiveIntake(group))"
                  >⚠ nº incompleto</span>
                  <span
                    v-if="awaitingPhoneByEmail(getActiveIntake(group))"
                    class="badge-phone-mailed"
                    :title="phoneEmailFullText(getActiveIntake(group))"
                  >✉ {{ phoneEmailBadge(getActiveIntake(group)) }}</span>
                </div>
              </td>

              <td class="td-destino" data-label="Destino">
                <span class="badge-ciudad cell-clip" :title="getActiveIntake(group).ciudad_destino || ''">{{ getActiveIntake(group).ciudad_destino || '—' }}</span>
                <div
                  v-if="zonasTexto(getActiveIntake(group))"
                  class="cell-sub cell-clip"
                  :title="zonasTexto(getActiveIntake(group))"
                >{{ zonasTexto(getActiveIntake(group)) }}</div>
              </td>

              <td class="td-fechas" data-label="Estadía">
                <div class="stay-in cell-clip">{{ formatDate(getActiveIntake(group).fecha_inicio) }}</div>
                <div class="stay-out cell-clip">→ {{ formatDate(getActiveIntake(group).fecha_fin) }}</div>
              </td>

              <!-- Presupuesto y tipo de alojamiento juntos: son el "qué busca"
                   y se leen de una sola pasada. -->
              <td class="td-req" data-label="Requerimiento">
                <div class="req-precio cell-clip" :title="presupuestoTitle(getActiveIntake(group))">
                  <span v-if="getActiveIntake(group).presupuesto_usd">USD {{ formatMoney(getActiveIntake(group).presupuesto_usd) }}</span>
                  <span v-else class="req-precio--vacio">—</span>
                </div>
                <div class="cell-sub cell-clip" :title="requerimientoTexto(getActiveIntake(group))">{{ requerimientoTexto(getActiveIntake(group)) }}</div>
              </td>

              <!-- Etapa: columna del tablero de Mensajería donde está el chat del lead -->
              <td class="td-etapa" data-label="Etapa">
                <span
                  :class="['badge-etapa', {
                    'badge-etapa--email': awaitingPhoneByEmail(getActiveIntake(group)),
                    'badge-etapa--none': !getActiveIntake(group).chat_stage_nombre && !awaitingPhoneByEmail(getActiveIntake(group))
                  }]"
                  :title="chatStageTitle(getActiveIntake(group))"
                >{{ chatStageLabel(getActiveIntake(group)) }}</span>
              </td>

              <!-- Lead Score con ring -->
              <td class="td-score" data-label="Score">
                <div class="score-wrap">
                  <div class="score-ring-wrap">
                    <svg viewBox="0 0 36 36" class="score-svg">
                      <circle class="score-bg-circle" cx="18" cy="18" r="15.9"/>
                      <circle
                        class="score-fill-circle"
                        cx="18" cy="18" r="15.9"
                        :stroke="tierColor(leadTier(getActiveIntake(group)))"
                        :stroke-dasharray="`${scoreArc(getActiveIntake(group))} 100`"
                      />
                    </svg>
                    <span class="score-num">{{ leadScoreVal(getActiveIntake(group)) }}</span>
                  </div>
                  <span class="score-tier-label" :class="leadTier(getActiveIntake(group))">
                    {{ tierEmoji(leadTier(getActiveIntake(group))) }} {{ leadTier(getActiveIntake(group)) || '—' }}
                  </span>
                </div>
              </td>

              <!-- Admin Intake en tabla principal (select para cambiar) -->
              <td class="td-admin" data-label="Admin" @click.stop>
                <select
                  class="admin-select"
                  :class="{ filled: getActiveIntake(group).admin_intake }"
                  :value="getActiveIntake(group).admin_intake"
                  title="Cambiar admin"
                  @click.stop
                  @change.stop="updateAdminIntake(getActiveIntake(group), $event.target.value)"
                >
                  <option value="">—</option>
                  <option v-for="opt in adminOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                  <option
                    v-if="getActiveIntake(group).admin_intake && !adminOptions.some(o => o.value === getActiveIntake(group).admin_intake)"
                    :value="getActiveIntake(group).admin_intake"
                  >{{ capitalizeFirst(getActiveIntake(group).admin_intake) }}</option>
                </select>
                <!-- En celular el <select> nativo se abre como diálogo a pantalla
                     completa: ahí se usa este botón, que abre un panel inferior. -->
                <button
                  type="button"
                  class="admin-picker-btn"
                  :class="{ filled: getActiveIntake(group).admin_intake }"
                  @click.stop="openAdminPicker(getActiveIntake(group))"
                >
                  <span class="admin-picker-val">{{ adminLabel(getActiveIntake(group)) }}</span>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                </button>
              </td>

              <!-- Fecha arriba y hora abajo: entra en menos ancho y la fecha,
                   que es lo que se mira, queda sola en su línea. -->
              <td class="td-registro" data-label="Registro" :title="formatDateTime(getActiveIntake(group).created_at)">
                <div class="reg-fecha cell-clip">{{ formatDate(getActiveIntake(group).created_at) }}</div>
                <div class="reg-hora cell-clip">{{ formatTime(getActiveIntake(group).created_at) }}</div>
              </td>

              <!-- Las tres acciones de la fila juntas. Editar estaba suelto al
                   lado del ID: ahí obligaba a la primera columna a ser el doble
                   de ancha y dejaba los botones repartidos en dos puntas. -->
              <td class="td-acciones">
                <div class="acciones-wrap">
                  <button
                    class="btn-expand"
                    :disabled="actionLoading === `edit-${getActiveIntake(group).id}`"
                    @click.stop="openEditModal(getActiveIntake(group))"
                    title="Editar intake"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/>
                      <path d="M20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </button>

                  <!-- Abre en Mensajería el chat de Sophi creado para este lead -->
                  <button
                    class="btn-chat"
                    :disabled="chatLoadingId === getActiveIntake(group).id"
                    @click.stop="openChat(getActiveIntake(group))"
                    title="Ver el chat de este lead en Mensajería"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
                    </svg>
                  </button>

                  <!-- Copia el link directo a este intake (?intake=ID): quien lo
                       abre ve la fila ya expandida, sin tener que buscarla. -->
                  <button
                    class="btn-share"
                    :class="{ 'is-ok': sharedId === getActiveIntake(group).id }"
                    @click.stop="shareIntake(getActiveIntake(group))"
                    :title="sharedId === getActiveIntake(group).id ? 'Link copiado' : 'Copiar link para compartir este intake'"
                    :aria-label="`Copiar link del intake #${getActiveIntake(group).id}`"
                  >
                    <svg v-if="sharedId === getActiveIntake(group).id" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                  </button>

                  <button
                    class="btn-delete"
                    :disabled="actionLoading === `delete-${getActiveIntake(group).id}`"
                    @click.stop="deleteIntake(getActiveIntake(group), group)"
                    title="Borrar intake"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>

            <!-- Sub-panel selector de miembros del grupo (si es grupo y está expandido) -->
            <tr v-if="group.isGroup && expandedGroupPhone === group.key" class="group-selector-row">
              <td colspan="10">
                <div class="group-selector-panel">
                  <div v-for="(m, idx) in group.members" :key="m.id" class="group-member-card" :class="{ active: group.selectedMember && group.selectedMember.id === m.id }" :style="{ background: getMemberColor(idx).bg, borderColor: getMemberColor(idx).border }" @click.stop="selectGroupMember(m, group)">
                    <span class="id-badge">#{{ m.id }}</span>
                    <div class="lead-nombre">{{ m.nombre }}</div>
                    <span class="badge-ciudad">{{ m.ciudad_destino }}</span>
                    <div class="score-wrap" style="align-items: flex-start; margin-top: 6px;">
                      <div class="score-ring-wrap" style="width: 28px; height: 28px;">
                        <svg viewBox="0 0 36 36" class="score-svg" style="width: 28px; height: 28px;">
                          <circle class="score-bg-circle" cx="18" cy="18" r="15.9"/>
                          <circle
                            class="score-fill-circle"
                            cx="18" cy="18" r="15.9"
                            :stroke="tierColor(leadTier(m))"
                            :stroke-dasharray="`${scoreArc(m)} 100`"
                          />
                        </svg>
                        <span class="score-num" style="font-size: 10px;">{{ leadScoreVal(m) }}</span>
                      </div>
                      <span class="score-tier-label" :class="leadTier(m)" style="font-size: 9px;">
                        {{ tierEmoji(leadTier(m)) }} {{ leadTier(m) || '—' }}
                      </span>
                    </div>
                  </div>
                </div>
              </td>
            </tr>

            <!-- Fila expandida -->
            <tr v-if="expandedId && (group.representative.id === expandedId || group.members.some(m => m.id === expandedId))" class="shortlist-row">
              <td colspan="10">
                <div class="shortlist-panel">
                  <!-- Detalles extra -->
                  <div class="detail-grid">
                    <div class="detail-block" v-if="getExpandedIntake().universidad">
                      <div class="detail-label detail-label-strong">Universidad origen</div>
                      <div class="detail-val">{{ getExpandedIntake().universidad }}</div>
                    </div>
                    <div class="detail-block" v-if="getExpandedIntake().universidad_destino">
                      <div class="detail-label detail-label-strong">Universidad destino</div>
                      <div class="detail-val">{{ getExpandedIntake().universidad_destino }}</div>
                    </div>
                    <div class="detail-block" v-if="getExpandedIntake().tipo_programa">
                      <div class="detail-label detail-label-strong">Programa</div>
                      <div class="detail-val">{{ getExpandedIntake().tipo_programa }}</div>
                    </div>
                    <div class="detail-block" v-if="getExpandedIntake().va_en_pareja !== null && getExpandedIntake().va_en_pareja !== undefined">
                      <div class="detail-label detail-label-strong">Va en pareja</div>
                      <div class="detail-val">
                        <span v-if="getExpandedIntake().va_en_pareja === true">Sí</span>
                        <span v-else-if="getExpandedIntake().va_en_pareja === false">No</span>
                      </div>
                    </div>
                    <div class="detail-block" v-if="formatArray(getExpandedIntake().zonas_cercanas) !== '—'">
                      <div class="detail-label detail-label-strong">Zonas cercanas</div>
                      <div class="detail-val">{{ formatArray(getExpandedIntake().zonas_cercanas) }}</div>
                    </div>
                    <div class="detail-block" v-if="getExpandedIntake().partner_nombre || getExpandedIntake().meta">
                      <div class="detail-label detail-label-strong"><strong>Partner</strong></div>
                      <div class="detail-val">{{ getExpandedIntake().partner_nombre || '—' }}</div>
                    </div>

                    <div class="detail-block" v-if="getExpandedIntake().meta !== undefined">
                      <div class="detail-label detail-label-strong">Código</div>
                      <div class="detail-val">{{ parseMetaParts(getExpandedIntake().meta).combined || (getExpandedIntake().meta || 'ninguno') }}</div>
                    </div>


                    <div class="detail-block detail-full" v-if="getExpandedIntake().comentario_libre">
                      <div class="detail-label detail-label-strong">Mensaje huésped</div>
                      <div class="detail-val">{{ getExpandedIntake().comentario_libre }}</div>
                    </div>
                  </div>

                  <!-- ══ ADMIN E INTAKE COMENTARIO ══ -->
                  <div class="intake-controls">

                    <div class="control-group">
                      <label class="control-label">Admin Intake</label>
                      <select
                        :value="getExpandedIntake().admin_intake"
                        class="control-input admin-select-desktop"
                        @change="updateAdminIntake(getExpandedIntake(), $event.target.value)"
                      >
                        <option value="">Seleccionar admin...</option>
                        <option v-for="opt in adminOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                        <option
                          v-if="getExpandedIntake().admin_intake && !adminOptions.some(o => o.value === getExpandedIntake().admin_intake)"
                          :value="getExpandedIntake().admin_intake"
                        >{{ capitalizeFirst(getExpandedIntake().admin_intake) }}</option>
                      </select>
                      <!-- Equivalente táctil del select (ver .admin-picker-btn) -->
                      <button
                        type="button"
                        class="admin-picker-btn"
                        :class="{ filled: getExpandedIntake().admin_intake }"
                        @click.stop="openAdminPicker(getExpandedIntake())"
                      >
                        <span class="admin-picker-val">{{ adminLabel(getExpandedIntake()) }}</span>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </button>
                    </div>

                    <!-- Anotación admin — popup -->
                    <div class="control-group control-group-wide">
                      <label class="control-label">Anotación admin</label>
                      <div class="comment-trigger" @click="editComment(getExpandedIntake())">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="comment-trigger-icon"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                        <span class="comment-trigger-text" :class="{ empty: !getExpandedIntake().intake_comentario }">
                          {{ getExpandedIntake().intake_comentario || 'Agregar anotación…' }}
                        </span>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="comment-trigger-edit"><path d="M11 4H4a2 2 0 00-2 2v14l4-4h14a2 2 0 002-2v-5"/><path d="M18 2l4 4-9 9H9v-4l9-9z"/></svg>
                      </div>
                    </div>

                  </div>

                  <!-- ══ POPUP ANOTACIÓN ══ -->
                  <transition name="popup-fade">
                    <div v-if="editingCommentId === getExpandedIntake().id" class="comment-popup-overlay" @click.self="cancelComment">
                      <div class="comment-popup">
                        <div class="comment-popup-header">
                          <span class="comment-popup-title">Anotación admin</span>
                          <button class="comment-popup-close" @click="cancelComment">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          </button>
                        </div>
                        <textarea
                          :value="getExpandedIntake().intake_comentario"
                          class="comment-popup-textarea"
                          placeholder="Escribir anotación sobre este lead..."
                          @input="getExpandedIntake().intake_comentario = $event.target.value"
                          @keyup.escape="cancelComment"
                          autofocus
                          rows="5"
                        ></textarea>
                        <div class="comment-popup-footer">
                          <button class="comment-popup-cancel" @click="cancelComment">Cancelar</button>
                          <button class="comment-popup-save" @click="saveComment(getExpandedIntake())">Guardar</button>
                        </div>
                      </div>
                    </div>
                  </transition>

                  <!-- Score breakdown -->
                  <div class="score-breakdown" v-if="getExpandedIntake().lead_score">
                    <div class="sb-header">
                      <span class="sb-title">Desglose Lead Score</span>
                      <span class="sb-total" :class="leadTier(getExpandedIntake())">{{ leadScoreVal(getExpandedIntake()) }}/100 · {{ tierEmoji(leadTier(getExpandedIntake())) }} {{ leadTier(getExpandedIntake()) }}</span>
                    </div>
                    <div class="sb-bars">
                      <div class="sb-item" v-for="item in scoreBreakdown(getExpandedIntake())" :key="item.label">
                        <span class="sb-label">{{ item.icon }} {{ item.label }}</span>
                        <div class="sb-track"><div class="sb-fill" :style="{ width: item.pct, background: tierColor(leadTier(getExpandedIntake())) }"></div></div>
                        <span class="sb-pts">{{ item.val }}/{{ item.max }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Shortlist -->
                  <div class="sl-header-row">
                    <span class="sl-section-title">Shortlist</span>
                    <span class="sl-count-badge" v-if="getVisibleShortlists(getExpandedIntake()).length">{{ getVisibleShortlists(getExpandedIntake()).length }}</span>
                  </div>

                  <div v-if="!getVisibleShortlists(getExpandedIntake()).length" class="sl-empty">
                    Sin shortlist generada para este intake.
                  </div>

                  <!-- ══ SCROLL CONTAINER CORREGIDO ══ -->
                  <div v-else :ref="`shortlist-scroll-${getExpandedIntake().id}`" class="sl-cards-outer">
                    <div class="sl-cards">
                      <div v-for="(sl, index) in getVisibleShortlists(getExpandedIntake())" :key="sl.id" class="sl-card" :class="[`tier-${sl.tier}`, { 'sl-card-elegido': sl.es_elegido_erasmus }]" @click="focusShortlistCard($event, getExpandedIntake().id)">

                        <div class="sl-card-header">
                          <!-- Alojamiento seleccionado de la campaña: badge naranja + link, sin tier/score/estado -->
                          <template v-if="sl.es_elegido_erasmus">
                            <span class="sl-erasmus-badge">ALOJAMIENTO ERASMUS</span>
                            <a
                              :href="accommodationUrl(sl.alojamiento_id)"
                              target="_blank"
                              rel="noopener"
                              class="sl-aloj-link sl-erasmus-link"
                              @click.stop
                            >#{{ sl.alojamiento_id }} ↗</a>
                          </template>
                          <template v-else>
                            <span class="sl-tier-label">{{ tierEmojiShortlist(sl.tier) }} {{ sl.tier }}</span>
                            <span class="sl-match-badge">{{ sl.match_score_total }}/100</span>
                            <span class="sl-estado-badge" :class="sl.estado">{{ sl.estado }}</span>
                          </template>
                        </div>

                        <div class="sl-card-body">
                          <div class="sl-aloj-row">
                            <span class="sl-aloj-label">Alojamiento</span>
                            <a
                              :href="accommodationUrl(sl.alojamiento_id)"
                              target="_blank"
                              rel="noopener"
                              class="sl-aloj-link"
                              @click.stop
                            >#{{ sl.alojamiento_id }} ↗</a>
                          </div>
                          <div class="sl-data-row"><span>Ranking</span><strong>#{{ sl.ranking_position }}</strong></div>
                          <div class="sl-data-row"><span>Registrado</span><strong>{{ formatDate(sl.date_register) }}</strong></div>
                          <div class="sl-data-row"><span>Enviado</span><strong>{{ sl.date_send ? formatDateTime(sl.date_send) : '—' }}</strong></div>
                          <div class="sl-data-row"><span>Abierto</span><strong>{{ sl.date_open ? formatDateTime(sl.date_open) : '—' }}</strong></div>
                        </div>

                        <div class="sl-card-actions">
                          <button
                            class="btn-discard"
                            :disabled="actionLoading === `discard-${sl.id}` || sl.estado === 'descartada'"
                            @click.stop="discardShortlist(sl)"
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            {{ actionLoading === `discard-${sl.id}` ? '...' : 'Descartar' }}
                          </button>
                        </div>

                        <div v-if="actionFeedback[sl.id]" class="sl-feedback" :class="actionFeedback[sl.id].type">
                          {{ actionFeedback[sl.id].msg }}
                        </div>

                      </div>
                      <!-- Spacer para garantizar scroll hasta el último elemento -->
                      <div class="sl-cards-spacer" aria-hidden="true"></div>
                    </div>
                  </div>

                  <!-- ══ BOTONES COPIAR Y AGREGAR ══ -->
                  <div class="sl-actions" v-if="getExpandedIntake().short_list && getExpandedIntake().short_list.length">
                    <button class="btn-copy" @click="copyMessageToClipboard(getExpandedIntake())">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2"/>
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"/>
                      </svg>
                      Copiar mensaje
                    </button>
                    <button class="btn-add" @click="openAddAlojamientoModal(getExpandedIntake())">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <line x1="12" y1="5" x2="12" y2="19"/>
                        <line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      Agregar alojamiento
                    </button>
                  </div>

                  <!-- Feedback para copiar -->
                  <div v-if="copyFeedback[getExpandedIntake().id]" class="copy-feedback" :class="copyFeedback[getExpandedIntake().id].type">
                    {{ copyFeedback[getExpandedIntake().id].msg }}
                  </div>

                </div>
              </td>
            </tr>
        </tbody>

        <tbody v-if="!loading && pagedIntakes.length === 0">
          <tr>
            <td colspan="10" class="td-empty">No se encontraron intakes con ese criterio.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ══ PAGINATION ══ (server-side: cada botón pide su página al backend) -->
    <div v-if="!error && totalPages > 1" class="pagination">
      <button class="btn-page" :disabled="page === 1 || loading" @click="goToPage(page - 1)">← Anterior</button>
      <div class="page-dots">
        <span
          v-for="p in visiblePages"
          :key="p"
          class="page-dot"
          :class="{ active: p === page, ellipsis: p === '…', disabled: loading }"
          @click="p !== '…' && goToPage(p)"
        >{{ p }}</span>
      </div>
      <button class="btn-page" :disabled="page === totalPages || loading" @click="goToPage(page + 1)">Siguiente →</button>
    </div>

    <!-- ══ MODAL: EDITAR INTAKE ══
         Vive acá, al lado del resto de los modales. Antes estaba anidado entre
         los <select> de la barra de filtros, que es un contenedor flex. -->
    <transition name="modal-fade">
      <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
        <div class="modal-box">
          <div class="modal-header">
            <h2>Editar Intake</h2>
            <button class="modal-close" @click="closeEditModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>

          <div class="modal-body" v-if="editingIntake">
            <label class="modal-label">Nombre</label>
            <input v-model="editingIntake.nombre" type="text" class="modal-input" />

            <label class="modal-label">Email</label>
            <input v-model="editingIntake.email" type="email" class="modal-input" />

            <label class="modal-label">WhatsApp</label>
            <input v-model="editingIntake.whatsapp" type="text" class="modal-input" />

            <label class="modal-label">Ciudad destino</label>
            <input v-model="editingIntake.ciudad_destino" type="text" class="modal-input" />

            <div style="display:flex; gap:10px;">
              <div style="flex:1">
                <label class="modal-label">Fecha inicio</label>
                <input v-model="editingIntake.fecha_inicio" type="date" class="modal-input" />
              </div>
              <div style="flex:1">
                <label class="modal-label">Fecha fin</label>
                <input v-model="editingIntake.fecha_fin" type="date" class="modal-input" />
              </div>
            </div>

            <label class="modal-label">Presupuesto USD</label>
            <input v-model.number="editingIntake.presupuesto_usd" type="number" class="modal-input" />

            <label class="modal-label">Tipo alojamiento</label>
            <input v-model="editingIntake.tipo_alojamiento" type="text" class="modal-input" />

            <label class="modal-label">Cantidad personas</label>
            <input v-model.number="editingIntake.cantidad_personas" type="number" class="modal-input" />

            <label class="modal-label">Mensaje huésped</label>
            <textarea v-model="editingIntake.comentario_libre" class="modal-input" rows="4"></textarea>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeEditModal">Cancelar</button>
            <button class="btn-confirm" :disabled="savingEdit" @click="saveEditIntake">{{ savingEdit ? 'Guardando...' : 'Guardar' }}</button>
          </div>

          <div v-if="editFeedback" class="modal-feedback" :class="editFeedback.type">
            {{ editFeedback.msg }}
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ PANEL: ASIGNAR ADMIN (celular) ══
         Reemplaza al <select> nativo, que en el celular se abre como un diálogo
         a pantalla completa del sistema. -->
    <transition name="sheet-fade">
      <div v-if="adminPicker.open" class="sheet-overlay" @click.self="closeAdminPicker">
        <div class="sheet">
          <div class="sheet-head">
            <span class="sheet-title">Asignar admin</span>
            <span class="sheet-sub" v-if="adminPicker.intake">Intake #{{ adminPicker.intake.id }}</span>
          </div>
          <div class="sheet-list">
            <button
              type="button"
              class="sheet-opt"
              :class="{ active: !adminPickerCurrent }"
              @click="pickAdmin('')"
            >Sin asignar</button>
            <button
              v-for="opt in adminOptions"
              :key="opt.value"
              type="button"
              class="sheet-opt"
              :class="{ active: opt.value === adminPickerCurrent }"
              @click="pickAdmin(opt.value)"
            >{{ opt.label }}</button>
          </div>
          <button type="button" class="sheet-cancel" @click="closeAdminPicker">Cancelar</button>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: ELIMINAR INTAKE ══
         Cuando el contacto tiene varios intakes agrupados hay que preguntar si
         se borra solo el que se está viendo o todos los del grupo. -->
    <transition name="modal-fade">
      <div v-if="deleteModal.open" class="modal-overlay" @click.self="closeDeleteModal">
        <div class="modal-box">
          <div class="modal-header">
            <h2>Eliminar intake</h2>
            <button class="modal-close" @click="closeDeleteModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body" v-if="deleteModal.intake">
            <!-- Un solo intake: confirmación simple -->
            <template v-if="deleteModal.members.length < 2">
              <p class="del-question">
                ¿Confirmás la eliminación del intake
                <strong>#{{ deleteModal.intake.id }}</strong>{{ deleteModal.intake.nombre ? ` · ${deleteModal.intake.nombre}` : '' }}?
              </p>
            </template>

            <!-- Grupo: elegir alcance -->
            <template v-else>
              <p class="del-question">
                Este contacto tiene <strong>{{ deleteModal.members.length }} intakes</strong> agrupados.
                ¿Qué querés eliminar?
              </p>

              <div class="del-opt del-opt--pick" :class="{ active: deleteModal.scope === 'one' }">
                <label class="del-opt-head">
                  <input type="radio" value="one" v-model="deleteModal.scope" />
                  <span class="del-opt-body">
                    <span class="del-opt-title">Solo un intake</span>
                    <span class="del-opt-sub">Elegí cuál querés eliminar</span>
                  </span>
                </label>
                <!-- Por defecto viene el que se estaba viendo en la fila -->
                <select
                  class="del-opt-select"
                  v-model.number="deleteModal.selectedId"
                  @change="deleteModal.scope = 'one'"
                >
                  <option v-for="m in deleteModal.members" :key="m.id" :value="m.id">
                    #{{ m.id }} · {{ m.nombre || 'Sin nombre' }}{{ m.ciudad_destino ? ` · ${m.ciudad_destino}` : '' }}
                  </option>
                </select>
              </div>

              <label class="del-opt" :class="{ active: deleteModal.scope === 'all' }">
                <input type="radio" value="all" v-model="deleteModal.scope" />
                <span class="del-opt-body">
                  <span class="del-opt-title">Los {{ deleteModal.members.length }} intakes del contacto</span>
                  <span class="del-opt-sub">
                    <span v-for="m in deleteModal.members" :key="m.id" class="del-opt-chip">#{{ m.id }}</span>
                  </span>
                </span>
              </label>
            </template>
          </div>

          <div v-if="deleteModal.error" class="modal-feedback err">{{ deleteModal.error }}</div>

          <div class="modal-footer">
            <button class="btn-cancel" :disabled="deleteModal.deleting" @click="closeDeleteModal">Cancelar</button>
            <button
              class="btn-confirm btn-confirm--danger"
              :disabled="deleteModal.deleting"
              @click="confirmDeleteIntake"
            >{{ deleteModal.deleting ? 'Eliminando...' : deleteConfirmLabel }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: AGREGAR ALOJAMIENTO ══ -->
    <transition name="modal-fade">
      <div v-if="showAddAlojModal" class="modal-overlay" @click.self="closeAddAlojamientoModal">
        <div class="modal-box">
          <div class="modal-header">
            <h2>Agregar alojamiento a shortlist</h2>
            <button class="modal-close" @click="closeAddAlojamientoModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <label class="modal-label">ID del Alojamiento</label>
            <input
              v-model.number="newAlojId"
              type="number"
              placeholder="Ingresa el ID del alojamiento"
              class="modal-input"
              @keyup.enter="addAlojamientoToShortlist"
            />
            <p class="modal-hint">Ej: 12345</p>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeAddAlojamientoModal">Cancelar</button>
            <button
              class="btn-confirm"
              :disabled="!newAlojId || addingAlojamiento"
              @click="addAlojamientoToShortlist"
            >
              {{ addingAlojamiento ? 'Buscando...' : 'Agregar' }}
            </button>
          </div>

          <div v-if="addAlojFeedback" class="modal-feedback" :class="addAlojFeedback.type">
            {{ addAlojFeedback.msg }}
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: SECCIÓN DEL ADMIN NO EXISTE EN SOPHI ══ -->
    <transition name="modal-fade">
      <div v-if="showSectionErrorModal" class="modal-overlay" @click.self="closeSectionErrorModal">
        <div class="modal-box">
          <div class="modal-header">
            <h2>No se pudo asignar el intake</h2>
            <button class="modal-close" @click="closeSectionErrorModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <p style="margin:0; color:#2d3436; line-height:1.5;">{{ sectionErrorMsg }}</p>
            <p class="modal-hint">Creá la sección de ese admin en Sophi (Mensajería) y volvé a intentar.</p>
          </div>

          <div class="modal-footer">
            <button class="btn-confirm" @click="closeSectionErrorModal">Entendido</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: EL LEAD TODAVÍA NO TIENE CHAT EN SOPHI ══ -->
    <transition name="modal-fade">
      <div v-if="showNoChatModal" class="modal-overlay" @click.self="closeNoChatModal">
        <div class="modal-box">
          <div class="modal-header">
            <h2>Sin chat todavía</h2>
            <button class="modal-close" @click="closeNoChatModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <p style="margin:0; color:#2d3436; line-height:1.5;">{{ noChatMsg }}</p>
            <p class="modal-hint">El chat se crea al asignar el intake a un admin. Asignalo y volvé a intentar.</p>
          </div>

          <div class="modal-footer">
            <button class="btn-confirm" @click="closeNoChatModal">Entendido</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: GENERAR LINK META ══ -->
    <transition name="modal-fade">
      <div v-if="showGenerateModal" class="modal-overlay" @click.self="closeGenerateModal">
        <div class="modal-box">
          <div class="modal-header">
            <h2>Generar link exclusivo</h2>
            <button class="modal-close" @click="closeGenerateModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <label class="modal-label">Escribe algún código de referencia (opcional)</label>
            <input v-model="customMeta" type="text" placeholder="Dejar vacío para generar aleatorio" class="modal-input" />
            <p class="modal-hint">Ej: ABC123</p>

            <label class="modal-label" style="margin-top:8px">Nombre de variable (clave query)</label>
            <input v-model="metaParamName" type="text" placeholder="meta" class="modal-input" />
            <p class="modal-hint">Ej: meta o google — se usará como clave en la URL y se incluirá tal cual.</p>

            <div v-if="generatedLink" style="margin-top:12px; display:flex; gap:8px; align-items:center;">
              <input type="text" :value="generatedLink" readonly class="modal-input" style="flex:1" />
              <button class="btn-copy" @click="copyGeneratedLink">Copiar</button>
            </div>

            <div v-if="generateFeedback" class="modal-feedback" style="margin-top:8px">
              {{ generateFeedback }}
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeGenerateModal">Cerrar</button>
            <button class="btn-confirm" :disabled="generating" @click="generateMeta">{{ generating ? '...' : 'Generar' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: USUARIOS DE DISCORD ══ -->
    <transition name="modal-fade">
      <div v-if="showDiscordModal" class="modal-overlay" @click.self="closeDiscordModal">
        <div class="modal-box">
          <div class="modal-header">
            <h2>Usuarios de Discord</h2>
            <button class="modal-close" @click="closeDiscordModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <p class="modal-hint" style="margin-bottom:10px">
              Estos usuarios se usan para mencionar (@ping) en Discord al asignar un admin a un intake.
              El <strong>nombre</strong> debe coincidir con el admin (ej: <code>emma</code>, <code>maru</code>).
            </p>

            <!-- Alta rápida -->
            <div class="discord-add-row">
              <input v-model="newDiscordUser.nombre" type="text" placeholder="Nombre (ej: emma)" class="modal-input" />
              <input v-model="newDiscordUser.correo" type="email" placeholder="Correo (ej: emma@houseandflats.com)" class="modal-input" />
              <input v-model="newDiscordUser.discord_id" type="text" placeholder="ID de Discord (ej: 123456789012345678)" class="modal-input" />
              <input v-model="newDiscordUser.handle" type="text" placeholder="Handle (opcional, ej: @emmabarra)" class="modal-input" />
              <button
                class="btn-confirm"
                :disabled="!newDiscordUser.nombre || !newDiscordUser.correo || !newDiscordUser.discord_id || savingDiscord"
                @click="saveDiscordUser"
              >
                {{ savingDiscord ? '...' : (editingDiscordId ? 'Guardar cambios' : '+ Agregar') }}
              </button>
              <button
                v-if="editingDiscordId"
                class="btn-cancel"
                :disabled="savingDiscord"
                @click="cancelEditDiscord"
              >
                Cancelar
              </button>
            </div>

            <div v-if="discordFeedback" class="modal-feedback" :class="discordFeedback.type" style="margin-top:8px">
              {{ discordFeedback.msg }}
            </div>

            <!-- Listado -->
            <div class="discord-list" style="margin-top:14px">
              <div v-if="loadingDiscord" class="modal-hint">Cargando...</div>
              <div v-else-if="!discordUsers.length" class="modal-hint">Aún no hay usuarios cargados.</div>
              <div v-else v-for="u in discordUsers" :key="u.id" class="discord-list-item" :class="{ editing: editingDiscordId === u.id }">
                <div class="discord-list-info">
                  <span class="discord-list-name">{{ capitalizeFirst(u.nombre) }}</span>
                  <span class="discord-list-handle" v-if="u.correo">{{ u.correo }}</span>
                  <span class="discord-list-handle" v-if="u.handle">{{ u.handle }}</span>
                  <span class="discord-list-id">ID: {{ u.discord_id }}</span>
                </div>
                <div class="discord-list-actions">
                  <button
                    class="btn-edit"
                    :disabled="savingDiscord"
                    @click="editDiscordUser(u)"
                    title="Editar usuario"
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z"/>
                      <path d="M20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                  </button>
                  <button
                    class="btn-delete"
                    :disabled="deletingDiscordId === u.id"
                    @click="deleteDiscordUser(u)"
                    title="Eliminar usuario"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                      <polyline points="3 6 5 6 21 6"/>
                      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                      <path d="M10 11v6M14 11v6M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeDiscordModal">Cerrar</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: REPARTO DE LEADS POR % Y POR CAMPAÑA ══ -->
    <transition name="modal-fade">
      <div v-if="showRepartoModal" class="modal-overlay" @click.self="closeRepartoModal">
        <div class="modal-box modal-box--wide modal-box--reparto">
          <div class="modal-header">
            <h2>Reparto de leads</h2>
            <button class="modal-close" @click="closeRepartoModal">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body rep-body">
            <!-- ── 1. Campaña ── -->
            <section class="rep-sec">
              <header class="rep-sec-head">
                <span class="rep-sec-num">1</span>
                <div class="rep-sec-titles">
                  <h3 class="rep-sec-title">Campaña</h3>
                  <p class="rep-sec-sub">El reparto se define por código de ads: cada campaña tiene el suyo.</p>
                </div>
              </header>
              <div class="rep-sec-body">
                <div class="rep-codigo-row">
                  <select v-model="repartoCodigo" class="modal-input" @change="onRepartoCodigoChange">
                    <option value="">— Elegí una campaña —</option>
                    <option v-for="c in repartoCodigoOptions" :key="c.value" :value="c.value">
                      {{ c.label }}
                    </option>
                  </select>
                  <input
                    v-model="nuevoCodigoAds"
                    type="text"
                    class="modal-input"
                    placeholder="…o escribí un código nuevo"
                    @keydown.enter.prevent="crearCodigoReparto"
                  />
                  <button class="btn-cancel" :disabled="!nuevoCodigoAds" @click="crearCodigoReparto">+ Usar</button>
                </div>
              </div>
            </section>

            <template v-if="repartoCodigo">
              <!-- ── 2. Brokers, porcentaje y horario ── -->
              <section class="rep-sec">
                <header class="rep-sec-head">
                  <span class="rep-sec-num">2</span>
                  <div class="rep-sec-titles">
                    <h3 class="rep-sec-title">Brokers</h3>
                    <p class="rep-sec-sub">
                      Cada lead va a <strong>uno solo</strong>, y se decide así (hora de Chile):
                      <strong>1)</strong> solo participan los que están en horario en ese momento.
                      <strong>2)</strong> si alguno de ellos tiene un horario cargado, el lead es suyo:
                      los que están <em>sin restricción</em> (24/7) no compiten contra él — sus horas
                      son para cubrir el resto de la semana. <strong>3)</strong> entre los que quedan,
                      le toca <strong>por turno</strong> al que hace más que no recibe, no al azar: con
                      el mismo % es uno a cada uno, y con el doble de % le toca el doble de seguido.
                      Por eso <strong>no hace falta que sumen 100</strong>. Debajo de cada fila va el
                      <strong>% real</strong> que le termina tocando, y al lado la cuenta al revés:
                      decile cuánto querés que se lleve y <strong>el % lo calcula solo</strong>. Si a
                      esa hora no hay nadie, el lead espera y se asigna apenas alguien entra en turno.
                    </p>
                  </div>
                  <span
                    class="rep-sec-badge"
                    :class="repartoTotalClass"
                    title="Suma de los % activos. Va de referencia: no tiene que dar 100."
                  >{{ repartoTotal }}%</span>
                </header>

                <div class="rep-sec-body">
                  <div class="rep-rows">
                    <div v-if="loadingReparto" class="rep-vacio">Cargando…</div>
                    <template v-else>
                      <div v-if="!repartoRows.length" class="rep-vacio">
                        Esta campaña todavía no tiene reparto.<br />
                        Agregá brokers abajo y ponele su % a cada uno.
                      </div>

                      <div
                        v-for="(row, i) in repartoRows"
                        :key="i"
                        class="rep-row"
                        :class="{ inactive: !row.activo, sophi: esSophi(row) }"
                      >
                        <!-- Identidad + peso -->
                        <div class="rep-row-top">
                          <span class="rep-ava" :title="esSophi(row) ? 'Sophi, el bot' : 'Broker'">
                            {{ esSophi(row) ? '🤖' : (row.nombres || '·').charAt(0).toUpperCase() }}
                          </span>

                          <div class="rep-row-main">
                            <input v-model="row.nombres" type="text" class="modal-input rep-input-name" placeholder="broker (ej: emma)" />
                            <input v-model="row.correo" type="email" class="modal-input rep-input-mail" placeholder="correo@houseandflats.com" />
                            <div class="rep-pct-wrap">
                              <input
                                v-model.number="row.porcentaje"
                                type="number" min="0" max="999.99" step="0.01"
                                class="modal-input rep-input-pct"
                                title="Cada cuánto le vuelve a tocar el turno. Solo importa en relación al de los otros que compiten a la misma hora: 50 y 50 es uno a cada uno, igual que 10 y 10; el doble de número es el doble de seguido. No hace falta que sumen 100."
                              />
                              <span class="rep-pct-sign">%</span>
                            </div>
                          </div>

                          <div class="rep-row-actions">
                            <label class="rep-activo" :title="row.activo ? 'Recibe leads de esta campaña' : 'Fuera del reparto: no recibe leads'">
                              <input type="checkbox" v-model="row.activo" />
                              <span class="rep-activo-track"><span class="rep-activo-dot"></span></span>
                              <span class="rep-activo-txt">{{ row.activo ? 'Activo' : 'Fuera' }}</span>
                            </label>
                            <button class="btn-delete" title="Quitar del reparto" @click="removeRepartoRow(i)">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                                <path d="M10 11v6M14 11v6M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                              </svg>
                            </button>
                          </div>
                        </div>

                        <!-- Lo que se lleva de verdad en una semana, contando su
                             horario y el de los demás -->
                        <div class="rep-share" :title="repartoShareTitle(row, i)">
                          <div class="rep-share-bar">
                            <span class="rep-share-fill" :style="{ width: repartoShare(row, i) + '%' }"></span>
                          </div>
                          <span class="rep-share-txt">{{ repartoShareTexto(row, i) }}</span>
                        </div>

                        <!-- La cuenta al revés: en vez de cargar un número y ver
                             qué sale, se pide la tajada y el % lo despeja el
                             dashboard. Es la única forma práctica de cargar a
                             alguien de pocas horas, porque su número no se
                             parece en nada a lo que termina recibiendo. -->
                        <div class="rep-objetivo">
                          <span class="rep-objetivo-label">🎯 Que se lleve</span>
                          <div class="rep-objetivo-num">
                            <input
                              v-model.number="row.objetivo"
                              type="number" min="0" max="100" step="0.5"
                              class="modal-input rep-objetivo-input"
                              placeholder="20"
                              :disabled="!row.activo"
                              @keydown.enter.prevent="aplicarObjetivo(row, i)"
                            />
                            <span class="rep-objetivo-sign">%</span>
                          </div>
                          <select v-model="row.objetivoModo" class="modal-input rep-objetivo-modo" :disabled="!row.activo">
                            <option value="semana">de la semana</option>
                            <option value="franja">de su franja</option>
                          </select>
                          <button
                            class="btn-cancel btn-cancel--mini"
                            :disabled="!objetivoListo(row)"
                            title="Despeja el % que hay que cargarle —contando el horario de todos— y lo escribe arriba"
                            @click="aplicarObjetivo(row, i)"
                          >Calcular %</button>
                          <span
                            v-if="row.objetivoMsg"
                            class="rep-objetivo-msg"
                            :class="row.objetivoMsgTipo"
                          >{{ row.objetivoMsg }}</span>
                        </div>

                        <!-- Disponibilidad: qué días y en qué franja recibe leads.
                             Siempre en hora de Chile. -->
                        <div class="rep-horario" :class="{ abierto: row.horarioAbierto }">
                          <div class="rep-horario-head">
                            <span class="rep-horario-ico">🕐</span>
                            <span class="rep-horario-resumen" :class="{ libre: !horarioTieneDias(row) }">
                              {{ horarioResumen(row) }}
                            </span>
                            <button
                              class="rep-horario-btn"
                              :aria-expanded="String(!!row.horarioAbierto)"
                              @click="row.horarioAbierto = !row.horarioAbierto"
                            >{{ row.horarioAbierto ? 'Listo' : 'Editar horario' }}</button>
                          </div>

                          <div v-if="row.horarioAbierto" class="rep-horario-body">
                            <!-- Días: se prenden y apagan como chips -->
                            <div class="rep-chips-wrap">
                              <span class="rep-mini-label">Días</span>
                              <div class="rep-chips">
                                <button
                                  v-for="d in row.horarioDias"
                                  :key="d.dia"
                                  class="rep-chip"
                                  :class="{ on: d.on }"
                                  :title="d.label"
                                  :aria-pressed="String(!!d.on)"
                                  @click="d.on = !d.on"
                                >{{ d.corto }}</button>
                              </div>
                            </div>

                            <!-- Horas, solo de los días prendidos -->
                            <div class="rep-dias">
                              <div v-for="d in diasEncendidos(row)" :key="d.dia" class="rep-dia">
                                <span class="rep-dia-label">{{ d.label }}</span>
                                <input v-model="d.desde" type="time" class="modal-input rep-hora" />
                                <span class="rep-dia-sep">→</span>
                                <input v-model="d.hasta" type="time" class="modal-input rep-hora" />
                                <span v-if="cruzaMedianoche(d)" class="rep-dia-nota" title="El turno termina al día siguiente">+1 día</span>
                                <span v-else-if="d.desde === d.hasta" class="rep-dia-nota">24 h</span>
                                <button class="rep-dia-x" title="Sacar este día" @click="d.on = false">✕</button>
                              </div>

                              <p v-if="!horarioTieneDias(row)" class="rep-dias-vacio">
                                Sin días marcados: <strong>recibe leads a cualquier hora</strong> (24/7).
                              </p>
                            </div>

                            <div class="rep-horario-presets">
                              <button class="btn-cancel btn-cancel--mini" @click="presetHorario(row, 'lv')">L a V 09–19</button>
                              <button class="btn-cancel btn-cancel--mini" @click="presetHorario(row, 'todos')">Todos 09–19</button>
                              <button
                                class="btn-cancel btn-cancel--mini"
                                title="Deja los días marcados de 00:00 a 00:00, o sea el día entero"
                                @click="presetHorario(row, 'full')"
                              >Días marcados 24 h</button>
                              <button class="btn-cancel btn-cancel--mini" @click="copiarHorarioAlResto(row)">Copiar 1er día al resto</button>
                              <button class="btn-cancel btn-cancel--mini" @click="presetHorario(row, 'libre')">Sin restricción</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>

                  <!-- Altas rápidas -->
                  <div class="rep-add-row">
                    <select v-model="repartoAddPick" class="modal-input" @change="addBrokerFromPick">
                      <option value="">+ Agregar broker…</option>
                      <option v-for="u in repartoBrokerOptions" :key="u.correo" :value="u.correo">
                        {{ capitalizeFirst(u.nombre) }} · {{ u.correo }}
                      </option>
                    </select>
                    <button class="btn-cancel" @click="addRepartoRow()">+ Fila vacía</button>
                    <button class="btn-cancel" :disabled="repartoTieneSophi" @click="addSophiRow">🤖 + Sophi</button>
                  </div>

                  <!-- Es referencia, no una meta: el sorteo normaliza sobre los
                       que están en horario, así que este número no tiene que dar
                       100. Lo que frena el guardado se avisa al lado. -->
                  <div class="rep-total" :class="repartoTotalClass">
                    <span>Total activo</span>
                    <strong>{{ repartoTotal }}%</strong>
                    <span class="rep-total-msg">{{ repartoTotalMsg }}</span>
                  </div>
                </div>
              </section>

              <!-- ── 3. Cobertura de la semana ── -->
              <section v-if="repartoRows.length" class="rep-sec">
                <header class="rep-sec-head">
                  <span class="rep-sec-num">3</span>
                  <div class="rep-sec-titles">
                    <h3 class="rep-sec-title">Cobertura de la semana</h3>
                    <p class="rep-sec-sub">
                      Hora de Chile. Las horas <strong>sin nadie disponible</strong> van rayadas:
                      los leads que entren ahí quedan en espera y se reparten cuando el primer
                      broker entra en turno, así que llegan con demora.
                    </p>
                  </div>
                </header>

                <div class="rep-sec-body">
                  <div class="rep-heat" role="img" aria-label="Cobertura por día y hora">
                    <div class="rep-heat-hours">
                      <span class="rep-heat-daylabel"></span>
                      <span v-for="h in [0, 6, 12, 18]" :key="h" class="rep-heat-hour">{{ h }}h</span>
                    </div>
                    <div v-for="fila in coberturaGrid" :key="fila.dia" class="rep-heat-row">
                      <span class="rep-heat-daylabel">{{ fila.corto }}</span>
                      <span
                        v-for="celda in fila.horas"
                        :key="celda.hora"
                        class="rep-heat-cell"
                        :class="'n' + celda.nivel"
                        :title="celda.titulo"
                      ></span>
                    </div>
                    <div class="rep-heat-leyenda">
                      <span class="rep-heat-key"><i class="rep-heat-cell n0"></i> nadie</span>
                      <span class="rep-heat-key"><i class="rep-heat-cell n1"></i> 1</span>
                      <span class="rep-heat-key"><i class="rep-heat-cell n2"></i> 2</span>
                      <span class="rep-heat-key"><i class="rep-heat-cell n3"></i> 3 o más</span>
                    </div>
                  </div>

                  <!-- El detalle en texto: el mismo dato que el mapa, para leerlo
                       sin depender del color. -->
                  <div v-if="repartoHuecos.length" class="rep-cobertura">
                    <div class="rep-cobertura-head">⚠ Tramos sin cobertura</div>
                    <ul class="rep-cobertura-list">
                      <li v-for="h in repartoHuecos" :key="h.dia">
                        <strong>{{ h.label }}</strong>
                        <span>{{ h.detalle }}</span>
                      </li>
                    </ul>
                  </div>
                  <div v-else class="rep-cobertura ok">
                    ✓ La semana queda cubierta: a cualquier hora hay al menos un broker disponible.
                  </div>
                </div>
              </section>
            </template>

            <div v-if="repartoFeedback" class="modal-feedback" :class="repartoFeedback.type" style="margin-top:10px">
              {{ repartoFeedback.msg }}
            </div>
          </div>

          <div class="modal-footer">
            <button
              v-if="repartoCodigo && repartoRows.length"
              class="btn-cancel"
              :disabled="savingReparto"
              @click="deleteReparto"
            >Borrar reparto</button>
            <!-- Copiar estos brokers, % y horarios a otras campañas. Solo con el
                 reparto en condiciones: duplicar algo que ni siquiera se puede
                 guardar no sirve de nada. -->
            <button
              v-if="repartoCodigo && repartoRows.length"
              class="btn-cancel"
              :disabled="!puedeDuplicarReparto"
              :title="puedeDuplicarReparto
                ? 'Copiar estos brokers, porcentajes y horarios a otras campañas'
                : 'Terminá de completar el reparto para poder duplicarlo'"
              @click="openDupReparto"
            >⧉ Duplicar</button>
            <button class="btn-cancel" @click="closeRepartoModal">Cerrar</button>
            <button
              class="btn-confirm"
              :disabled="!repartoCodigo || savingReparto || !repartoValido"
              @click="saveReparto"
            >{{ savingReparto ? 'Guardando...' : 'Guardar reparto' }}</button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MODAL: DUPLICAR EL REPARTO EN OTRAS CAMPAÑAS ══ -->
    <transition name="modal-fade">
      <div
        v-if="showRepartoModal && dupReparto.open"
        class="modal-overlay modal-overlay--top"
        @click.self="closeDupReparto"
      >
        <div class="modal-box modal-box--wide">
          <div class="modal-header">
            <h2>Duplicar reparto</h2>
            <button class="modal-close" :disabled="dupReparto.saving" @click="closeDupReparto">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>

          <div class="modal-body">
            <!-- Resumen de lo que se va a copiar: se ve en los dos pasos -->
            <div class="dup-preview">
              <div class="dup-preview__head">
                <span class="dup-preview__codigo">{{ repartoCodigo }}</span>
                <span class="dup-preview__total">{{ repartoTotal }}%</span>
              </div>
              <ul class="dup-preview__list">
                <li v-for="(r, i) in repartoRows" :key="i" :class="{ inactive: !r.activo }">
                  <span class="dup-preview__name">{{ r.nombres || '—' }}</span>
                  <span class="dup-preview__horario">
                    {{ horarioTieneDias(r) ? horarioResumen(r) : '24/7' }}
                  </span>
                  <span class="dup-preview__pct">{{ r.porcentaje }}%</span>
                  <span v-if="!r.activo" class="dup-preview__off">inactivo</span>
                </li>
              </ul>
            </div>

            <!-- Paso 1: confirmar -->
            <template v-if="dupReparto.step === 'confirm'">
              <p class="modal-hint" style="margin-top:14px">
                ¿Querés duplicar esta configuración en otras campañas?
                Los brokers, los porcentajes y los horarios de arriba van a reemplazar
                el reparto que tengan hoy las campañas que elijas.
              </p>
            </template>

            <!-- Paso 2: tildar las campañas destino -->
            <template v-else>
              <div class="dup-pick-head">
                <label class="modal-label" style="margin:0">Campañas a actualizar</label>
                <button class="btn-cancel btn-cancel--mini" @click="toggleDupTodas">
                  {{ dupTodasSeleccionadas ? 'Ninguna' : 'Todas' }}
                </button>
              </div>

              <div v-if="!dupCodigoOptions.length" class="modal-hint" style="margin:10px 0">
                No hay otras campañas para actualizar.
              </div>

              <div v-else class="dup-list">
                <label v-for="c in dupCodigoOptions" :key="c.value" class="dup-item">
                  <input type="checkbox" :value="c.value" v-model="dupReparto.seleccion" />
                  <span class="dup-item__label">{{ c.label }}</span>
                </label>
              </div>

              <p class="modal-hint" style="margin-top:10px">
                Se guarda también en <strong>{{ repartoCodigo }}</strong>,
                así queda todo con la misma configuración.
              </p>

              <div v-if="dupReparto.errores.length" class="modal-feedback err" style="margin-top:10px">
                No se pudo aplicar en:
                <strong>{{ dupReparto.errores.map(e => e.codigo).join(', ') }}</strong>.
                El resto quedó actualizado. Volvé a apretar Actualizar para reintentar
                (se vuelve a escribir todo, así que repetirlo es seguro).
              </div>
            </template>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" :disabled="dupReparto.saving" @click="closeDupReparto">Cancelar</button>

            <button
              v-if="dupReparto.step === 'confirm'"
              class="btn-confirm"
              :disabled="!dupCodigoOptions.length"
              @click="irAElegirCampanas"
            >Sí, elegir campañas</button>

            <button
              v-else
              class="btn-confirm"
              :disabled="!dupReparto.seleccion.length || dupReparto.saving"
              @click="aplicarDuplicado"
            >
              {{ dupReparto.saving
                ? `Actualizando ${dupReparto.hechos}/${dupReparto.seleccion.length + 1}...`
                : `Actualizar (${dupReparto.seleccion.length})` }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ══ MÓDULO ESTADÍSTICAS (independiente, no afecta el dashboard) ══ -->
    <EstadisticasIntakes :show="showStats" :filtros-dashboard="statsFiltros" @close="closeStats" />

    <!-- ══ PARTNERS OFICIALES (lista que ofrece el formulario) ══ -->
    <PartnersIntake :show="showPartners" @close="showPartners = false" />

  </div>
</template>

<script>
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import axios from 'axios'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import EstadisticasIntakes from './EstadisticasIntakes.vue'
import PartnersIntake from './PartnersIntake.vue'
import { initializePusher, subscribeToChannel, unsubscribeFromChannel } from '@/Utils/PusherUtils'

const API_BASE = process.env.VUE_APP_API_URL
const BOT_SHORTLIST_ENDPOINT = `${API_BASE}bot/shortlist`
// Filas por página. Una fila es una PERSONA, no un intake: el backend pagina
// con este mismo tamaño y ya agrupa, así que esta página trae exactamente 10
// filas. La tabla muestra SOLO lo que vino en la página, nunca toda la tabla.
const PER_PAGE = 10
// Dígitos mínimos para juntar dos intakes por teléfono. Lo espeja
// DashboardIntakeController::MIN_GROUP_PHONE_DIGITS y tiene que coincidir: si
// acá se agrupara más que allá, dos filas de la página se fusionarían y la
// página quedaría con menos de PER_PAGE filas. No confundir con
// MIN_PHONE_DIGITS, que es otra cosa (cuándo un número no sirve para escribir).
const MIN_GROUP_PHONE_DIGITS = 6
// Tamaño de página al recorrer todo (Exportar / Estadísticas). Es el tope que
// acepta el backend por request.
const BULK_PER_PAGE = 100
// Espera antes de mandar la búsqueda al backend, para no pedir una página por
// cada tecla.
const SEARCH_DEBOUNCE_MS = 400
// Valor sentinela del filtro de códigos para "intakes sin codigo_ads".
const SIN_CODIGO = '__sin_codigo__'
// Códigos de campaña que genera el propio sistema. Se ofrecen siempre en el
// modal de reparto, aunque todavía no haya ningún lead con ese código, para no
// tener que escribirlos a mano (un tipeo = un reparto que nunca se aplica).
const CODIGOS_ADS_CONOCIDOS = [
  { value: 'web-directa',    label: 'web-directa (formulario de la web)' },
  { value: 'correo-erasmus', label: 'correo-erasmus (ErasmusPlay)' },
  { value: 'tercera-campaña', label: 'tercera-campaña (webhook de ads)' },
]

// Días de la semana del reparto, en ISO (1 = lunes … 7 = domingo), que es la
// clave con la que el backend guarda y evalúa el horario de cada broker.
const DIAS_REPARTO = [
  { dia: 1, label: 'Lun', corto: 'L' },
  { dia: 2, label: 'Mar', corto: 'M' },
  { dia: 3, label: 'Mié', corto: 'X' },
  { dia: 4, label: 'Jue', corto: 'J' },
  { dia: 5, label: 'Vie', corto: 'V' },
  { dia: 6, label: 'Sáb', corto: 'S' },
  { dia: 7, label: 'Dom', corto: 'D' },
]
// Franja por defecto al marcar un día en el modal de reparto.
const HORARIO_DEFAULT = { desde: '09:00', hasta: '19:00' }
// Tope del % de una fila. Espejo de LeadAdminRepartoController::PORCENTAJE_MAX,
// que es lo que entra en la columna (decimal(5,2)). Pasarse de 100 es válido: un
// broker de medio turno necesita un número alto para pesar dentro de su franja.
const PORCENTAJE_MAX = 999.99
// El % más chico que se puede cargar sin que la fila quede en cero (y cero es
// "no recibe"). Con dos decimales, es 0,01.
const PORCENTAJE_MIN = 0.01
// Paso con el que se busca cobertura en el modal: media hora es suficiente para
// detectar los huecos reales sin recorrer la semana minuto a minuto.
const COBERTURA_PASO_MIN = 30

// Sentinelas del filtro de etapas: el lead tiene chat pero todavía no está en
// ninguna columna del tablero, o directamente no tiene chat.
const SIN_ETAPA = '__sin_etapa__'
const SIN_CHAT  = '__sin_chat__'
// Mínimo de dígitos para que un WhatsApp sea utilizable (código de país +
// número). Espejo de IntakeIncompletePhoneNotifier::MIN_DIGITS en el backend.
const MIN_PHONE_DIGITS = 11

export default {
  name: 'HyFDashboard',

  components: { HyFLoader, EstadisticasIntakes, PartnersIntake },

  data() {
    return {
      // Intakes de la página actual (no todos: el backend pagina y filtra).
      intakes:        [],
      // Otros intakes de esas mismas personas (mismo teléfono/correo) que no
      // entraron en la página. No son resultados: solo completan los grupos.
      siblings:       [],
      total:          0,
      page:           1,
      serverTotalPages: 1,
      loading:        false,
      // Modal de partners oficiales del formulario.
      showPartners:   false,
      // Recorrido de TODAS las páginas, solo al Exportar o abrir Estadísticas.
      bulkLoading:    false,
      bulkLoaded:     0,
      bulkLabel:      '',
      bulkError:      '',
      // Descarta la respuesta de una carga anterior si se pidió otra en el medio.
      intakesSeq:     0,
      error:          null,
      // Opciones de los selectores, del endpoint de catálogo: ya no se deducen
      // de los intakes cargados porque solo tenemos una página.
      catalogCodigos:    [],
      catalogSinCodigo:  false,
      // Intakes con los que trabaja el modal de Estadísticas (se cargan al
      // abrirlo, con los filtros puestos).
      statsFiltros:   {},
      currentUserEmail: '',
      expandedId:     null,
      expandedGroupPhone: null,
      selectedGroupIntakeId: null,
      actionLoading:  null,
      actionFeedback: {},
      copyFeedback:   {},
      sharedId:       null,   // intake cuyo link se acaba de copiar (tilde en el botón)
      searchQuery:    '',
      filterTier:     '',
      filterEtapa:    '',
      filterUrgencia: '',
      filterCodigo:   '',
      filterAdmin:    '',
      filterFechaDesde: '',
      filterFechaHasta: '',
      showStats:      false,
      // Nombres de las columnas del tablero de Mensajería (conversation_stage),
      // para el filtro por etapa. Ver fetchChatStages.
      chatStages:     [],

      editingCommentId: null,
      commentBackup: null,

      // Panel inferior para asignar admin en celular (ver .admin-picker-btn).
      adminPicker: { open: false, intake: null },

      // Modal de borrado. `members` solo se llena cuando el intake pertenece a
      // un grupo (mismo teléfono/correo) para poder elegir el alcance.
      deleteModal: {
        open: false,
        intake: null,
        members: [],
        scope: 'one',      // 'one' = un solo intake (el elegido) | 'all' = todo el grupo
        selectedId: null,  // en modo 'one': cuál de los del grupo se borra
        deleting: false,
        error: '',
      },

      showAddAlojModal: false,
      newAlojId: null,
      addingAlojamiento: false,
      addAlojFeedback: null,
      addAlojIntakeId: null,
      // Edit modal
      showEditModal: false,
      editingIntake: null,
      editingIntakeBackup: null,
      savingEdit: false,
      editFeedback: null,
      // Generar link meta
      showGenerateModal: false,
      customMeta: null,
      metaParamName: 'meta',
      generatedMeta: null,
      generatedLink: null,
      generating: false,
      generateFeedback: null,
      // Usuarios de Discord
      showDiscordModal: false,
      discordUsers: [],
      loadingDiscord: false,
      savingDiscord: false,
      deletingDiscordId: null,
      discordFeedback: null,
      newDiscordUser: { nombre: '', correo: '', discord_id: '', handle: '' },
      editingDiscordId: null,
      // Reparto de leads por % y horario, por código de campaña (lead_admin_reparto)
      showRepartoModal: false,
      repartoGrupos: [],      // [{ codigo_ads, total, brokers, rows: [] }] tal como viene del back
      repartoCodigo: '',      // campaña que se está editando
      repartoRows: [],        // filas editables de esa campaña
      nuevoCodigoAds: '',     // para arrancar el reparto de un código que no está en la lista
      repartoAddPick: '',     // select de alta rápida de broker
      loadingReparto: false,
      savingReparto: false,
      repartoFeedback: null,
      // Duplicar la configuración del código actual en otras campañas.
      // step: 'confirm' pregunta si se quiere duplicar; 'pick' muestra las
      // campañas para tildar. Se aplica una por una con el mismo endpoint.
      dupReparto: {
        open: false,
        step: 'confirm',
        seleccion: [],
        saving: false,
        hechos: 0,
        errores: [],
      },
      // Pop-up cuando el admin asignado no tiene sección en Sophi.
      showSectionErrorModal: false,
      sectionErrorMsg: '',
      // Botón de chat: id del intake cuyo chat se está resolviendo + pop-up
      // para cuando el lead todavía no tiene conversación en Sophi.
      chatLoadingId: null,
      showNoChatModal: false,
      noChatMsg: '',
    }
  },

  computed: {
    // ── Reparto de leads por campaña ──
    // Suma de los % activos de la campaña que se está editando. NO tiene que dar
    // 100: el sorteo normaliza sobre los que estén en horario en cada momento,
    // así que el % de cada fila solo vale en relación al de los que coinciden
    // con su turno. Este total va de referencia, para ver de un vistazo si
    // alguien quedó en cero.
    repartoTotal() {
      const total = this.repartoRows
        .filter(r => r.activo)
        .reduce((acc, r) => acc + (parseFloat(r.porcentaje) || 0), 0)
      return Math.round(total * 100) / 100
    },

    // Un reparto vacío es válido (equivale a no repartir esa campaña). Lo que no
    // se deja guardar es un reparto CON brokers en el que nadie recibe: todos
    // apagados o todos en cero manda cada lead de la campaña a "sin asignar", y
    // sin avisar. Para no repartir está el botón de borrar, que sí se ve.
    repartoValido() {
      if (!this.repartoRows.length) return true
      if (this.repartoRows.some(r => !String(r.nombres || '').trim() || !String(r.correo || '').trim())) return false
      // Un día marcado sin sus dos horas se descartaría al guardar y el broker
      // terminaría con un horario distinto del que se ve en pantalla.
      if (this.repartoRows.some(r => this.horarioIncompleto(r))) return false
      if (this.repartoNombresRepetidos.length) return false
      return this.repartoTotal > 0
    },

    repartoTotalClass() {
      if (!this.repartoRows.length) return ''
      return this.repartoValido ? 'ok' : 'bad'
    },

    repartoTotalMsg() {
      if (!this.repartoRows.length) return ''
      if (this.repartoRows.some(r => !String(r.nombres || '').trim() || !String(r.correo || '').trim())) {
        return '✕ hay filas sin nombre o sin correo'
      }
      // Un día marcado al que le falta la hora de inicio o la de fin.
      if (this.repartoRows.some(r => this.horarioIncompleto(r))) {
        return '✕ hay días marcados sin horario completo'
      }
      if (this.repartoNombresRepetidos.length) {
        return `✕ hay dos brokers con el mismo nombre (${this.repartoNombresRepetidos.join(', ')}): para el sistema serían la misma persona`
      }
      if (this.repartoTotal <= 0) {
        return '✕ nadie recibe leads: ponele un % a algún broker activo'
      }
      return '✓ no tiene que dar 100 · listo para guardar'
    },

    // Tramos de la semana que no cubre NINGÚN broker activo de la campaña. Los
    // leads que entran ahí quedan sin asignar, así que conviene verlo antes de
    // guardar. Se recorre la semana de a media hora (COBERTURA_PASO_MIN).
    repartoHuecos() {
      const activos = this.repartoRows.filter(
        r => r.activo && (parseFloat(r.porcentaje) || 0) > 0
      )
      if (!activos.length) return []

      const mapas = activos.map(r => this.horarioAMapa(r))
      // Con que uno solo esté sin restricción, la semana entera está cubierta.
      if (mapas.some(m => !m)) return []

      const huecos = []
      for (const d of DIAS_REPARTO) {
        const libres = []
        for (let t = 0; t < 1440; t += COBERTURA_PASO_MIN) {
          if (!mapas.some(m => this.mapaCubre(m, d.dia, t))) libres.push(t)
        }
        if (!libres.length) continue

        huecos.push({
          dia: d.dia,
          label: d.label,
          detalle: libres.length * COBERTURA_PASO_MIN >= 1440
            ? 'sin cobertura en todo el día'
            : this.tramosDeSlots(libres),
        })
      }

      return huecos
    },

    // El horario de cada fila, ya en forma de mapa (null = sin restricción).
    // Se calcula una vez y lo reusan todas las simulaciones de abajo.
    repartoMapas() {
      return this.repartoRows.map(r => this.horarioAMapa(r))
    },

    // El peso de cada fila en el sorteo: su %, o 0 si está fuera del reparto.
    repartoPesos() {
      return this.repartoRows.map(r => (r.activo ? (parseFloat(r.porcentaje) || 0) : 0))
    },

    // La semana simulada con los pesos y horarios que hay ahora en pantalla.
    // Devuelve { semana, franja } (ver simularSemana).
    repartoSimulacion() {
      return this.simularSemana(this.repartoPesos, this.repartoMapas)
    },

    // Cuánto se lleva DE VERDAD cada broker en una semana, en %.
    //
    // El % que se carga es su peso dentro del sorteo, y el sorteo solo corre
    // entre los que están en horario: un broker que trabaja un solo día no se
    // lleva su % de la semana, se lleva su % de ESE día.
    repartoCuotas() {
      return this.repartoSimulacion.semana
    },

    // Cuánto se lleva cada broker DENTRO DE SU PROPIA FRANJA, en %.
    //
    // Es el número que uno tiene en la cabeza al cargar el reparto ("quiero que
    // Hans se lleve la mitad de los leads que entran en su turno"), y NO es el %
    // que se carga: depende de con quién le toque compartir la franja. Cargando
    // 50, 50 y 100, el de 100 se lleva el 50% de su turno; si en vez de eso
    // tuviera 50, se llevaría 33%. El botón "Calcular %" despeja ese número.
    repartoCuotasFranja() {
      return this.repartoSimulacion.franja
    },

    // El TECHO de cada fila: lo máximo que podría llevarse de la semana con el
    // horario que tiene, aunque se le cargue el % más alto posible. Es la
    // proporción de la semana en la que está disponible, y es el número que
    // explica por qué alguien de pocas horas nunca llega a su objetivo: con 4 h
    // los sábados, el techo es 2,4% de la semana por más que se le cargue 999.
    repartoTechos() {
      return this.repartoRows.map((r, i) => {
        if (!r.activo) return 0
        return this.cuotaConPeso(i, PORCENTAJE_MAX, 'semana')
      })
    },

    // Nombres repetidos entre filas: dos brokers distintos no pueden llamarse
    // igual. Lo que se guarda en el lead es el NOMBRE (`admin_intake`), no el
    // correo, así que dos filas con el mismo nombre son la misma persona para el
    // resto del sistema: los leads de una le aparecen a la otra. El backend lo
    // rechaza con 422; acá se avisa antes de que llegue a guardar.
    repartoNombresRepetidos() {
      const vistos = new Set()
      const repes = new Set()
      for (const r of this.repartoRows) {
        const nombre = String(r.nombres || '').trim().toLowerCase()
        if (!nombre) continue
        if (vistos.has(nombre)) repes.add(nombre)
        vistos.add(nombre)
      }
      return Array.from(repes)
    },

    // Mapa de cobertura: 7 días × 24 horas con cuántos brokers activos hay
    // disponibles en cada hora. Una hora cuenta como cubierta solo si lo está
    // entera (se miran las dos medias horas), para que el mapa no muestre verde
    // donde la lista de huecos de abajo marca un tramo suelto.
    coberturaGrid() {
      const brokers = this.repartoRows
        .filter(r => r.activo && (parseFloat(r.porcentaje) || 0) > 0)
        .map(r => ({
          nombre: String(r.nombres || '—'),
          mapa: this.horarioAMapa(r),
        }))

      return DIAS_REPARTO.map(d => ({
        dia: d.dia,
        corto: d.corto,
        horas: Array.from({ length: 24 }, (_, h) => {
          const enHora = brokers.filter(
            b => this.mapaCubre(b.mapa, d.dia, h * 60) && this.mapaCubre(b.mapa, d.dia, h * 60 + 30)
          )
          const hora = `${String(h).padStart(2, '0')}:00`
          return {
            hora: h,
            nivel: Math.min(enHora.length, 3),
            titulo: enHora.length
              ? `${d.label} ${hora} · ${enHora.map(b => b.nombre).join(', ')}`
              : `${d.label} ${hora} · nadie disponible`,
          }
        }),
      }))
    },

    // Campañas elegibles: las que ya tienen reparto + las que aparecen en los
    // intakes cargados. Se marca cuáles ya tienen reparto para no perderse.
    repartoCodigoOptions() {
      const conReparto = new Set(this.repartoGrupos.map(g => String(g.codigo_ads).toLowerCase()))
      const seen = new Set()
      const opts = []

      for (const g of this.repartoGrupos) {
        const val = String(g.codigo_ads).toLowerCase()
        if (seen.has(val)) continue
        seen.add(val)
        // Cuántos brokers están recibiendo. Antes acá iba la suma de los %, que
        // con el 100 obligatorio era siempre el mismo número y ahora, sin esa
        // regla, no querría decir nada.
        const n = Number(g.brokers) || 0
        opts.push({ value: val, label: `${g.codigo_ads} · ${n} broker${n === 1 ? '' : 's'}` })
      }
      for (const opt of this.codigoOptions) {
        const val = String(opt.value).toLowerCase()
        if (val === SIN_CODIGO || seen.has(val)) continue
        seen.add(val)
        opts.push({ value: val, label: `${opt.label}${conReparto.has(val) ? '' : ' · sin reparto'}` })
      }
      // Los códigos que genera el sistema van siempre, aunque todavía no haya
      // ningún lead con ese código cargado en pantalla.
      for (const c of CODIGOS_ADS_CONOCIDOS) {
        if (seen.has(c.value)) continue
        seen.add(c.value)
        opts.push({ value: c.value, label: `${c.label} · sin reparto` })
      }
      return opts
    },

    // Brokers que se pueden agregar: los admins de Discord que todavía no están
    // en el reparto de esta campaña.
    repartoBrokerOptions() {
      const yaEstan = new Set(this.repartoRows.map(r => String(r.correo || '').toLowerCase()))
      return this.discordUsers
        .map(u => ({
          nombre: String(u.nombre || '').toLowerCase().trim(),
          correo: String(u.correo || '').toLowerCase().trim(),
        }))
        .filter(u => u.nombre && u.correo && !yaEstan.has(u.correo))
    },

    repartoTieneSophi() {
      return this.repartoRows.some(r => String(r.nombres || '').toLowerCase().trim() === 'sophi')
    },

    // ── Duplicar el reparto en otras campañas ──
    // Solo tiene sentido copiar una configuración que se pueda guardar.
    // repartoValido da true con la lista vacía, de ahí el length.
    puedeDuplicarReparto() {
      return !!this.repartoCodigo
        && this.repartoRows.length > 0
        && this.repartoValido
        && !this.savingReparto
    },

    // Campañas a las que se puede copiar: todas menos la que se está editando
    // (esa se guarda igual, es el origen).
    dupCodigoOptions() {
      const actual = String(this.repartoCodigo || '').toLowerCase()
      return this.repartoCodigoOptions.filter(o => String(o.value).toLowerCase() !== actual)
    },

    dupTodasSeleccionadas() {
      return this.dupCodigoOptions.length > 0
        && this.dupReparto.seleccion.length === this.dupCodigoOptions.length
    },

    // Filas a copiar, normalizadas igual que las que manda saveReparto.
    dupFilas() {
      return this.repartoRows.map(r => this.repartoRowPayload(r))
    },

    // Opciones de admin derivadas de la tabla de Discord (nombre -> label capitalizado)
    adminOptions() {
      const seen = new Set()
      const opts = []
      for (const u of this.discordUsers) {
        const val = String(u.nombre || '').toLowerCase().trim()
        if (!val || seen.has(val)) continue
        seen.add(val)
        opts.push({ value: val, label: this.capitalizeFirst(val) })
      }

      // Los admins con acceso total (cristian/matias/luca/dante) ven TODAS las
      // opciones. Un admin normal solo ve su propia opción: no puede asignar/
      // reasignar intakes a otros admins.
      if (this.isFullAccessAdmin) return opts
      const mine = this.myAdminNombre
      // Si aún no se pudo resolver su nombre (discordUsers sin cargar), no
      // restringimos para no dejar el selector vacío; el backend igual valida.
      if (!mine) return opts
      return opts.filter(o => o.value === mine)
    },

    // Códigos de campaña (codigo_ads) que existen en la tabla. Vienen del
    // endpoint de catálogo (fetchFilterOptions): la página cargada no alcanza
    // para deducirlos, y así cada código nuevo aparece solo en el filtro.
    codigoOptions() {
      const opts = this.catalogCodigos.map(code => ({ value: code, label: code }))
      if (this.catalogSinCodigo) opts.push({ value: SIN_CODIGO, label: 'Sin código' })
      return opts
    },

    // Etapas del tablero de Mensajería para el filtro. Salen del catálogo del
    // backend (todas las columnas activas, aunque hoy no haya ningún lead en
    // ellas) más las que traigan los intakes de la página, por si alguna quedó
    // desactivada. Se juntan por nombre: una misma columna que existe en varias
    // secciones (por ejemplo "Primer contacto") es una sola opción.
    etapaOptions() {
      const seen = new Set()
      const opts = []
      for (const nombre of this.chatStages) {
        const key = String(nombre).toLowerCase()
        if (!key || seen.has(key)) continue
        seen.add(key)
        opts.push({ value: nombre, label: nombre })
      }
      for (const intake of this.visibleIntakes) {
        const nombre = String((intake && intake.chat_stage_nombre) || '').trim()
        if (!nombre) continue
        const key = nombre.toLowerCase()
        if (seen.has(key)) continue
        seen.add(key)
        opts.push({ value: nombre, label: nombre })
      }
      opts.sort((a, b) => a.label.localeCompare(b.label))
      // Van siempre: el filtro se resuelve en el backend, así que no depende de
      // que la página cargada tenga algún lead sin etapa o sin chat.
      opts.push({ value: SIN_ETAPA, label: 'Sin etapa' })
      opts.push({ value: SIN_CHAT,  label: 'Sin chat' })
      return opts
    },

    // Admin asignado al intake que se está editando en el panel inferior.
    adminPickerCurrent() {
      const i = this.adminPicker.intake
      return (i && i.admin_intake) ? String(i.admin_intake) : ''
    },

    // Texto del botón de confirmación del modal de borrado.
    deleteConfirmLabel() {
      const m = this.deleteModal
      if (m.members.length > 1) {
        return m.scope === 'all' ? `Eliminar los ${m.members.length}` : `Eliminar #${m.selectedId}`
      }
      return 'Eliminar intake'
    },

    // ¿El usuario logueado tiene acceso total? El rol de super admin se asigna
    // desde /admin/permisos (ya no es una lista de correos hardcodeada).
    isFullAccessAdmin() {
      return !!this.$store.getters.isSuperAdmin
    },

    // Correos que SOLO pueden ver todos los intakes, sin otros permisos extra.
    // Ven todos los intakes pero NO acceden al resto (Erasmus, selector de
    // admins, etc.), que sigue gateado por isFullAccessAdmin. Espeja
    // DashboardIntakeController::VIEW_ALL_INTAKES_EMAILS del backend.
    canSeeAllIntakes() {
      if (this.isFullAccessAdmin) return true
      const email = String(this.currentUserEmail || '').trim().toLowerCase()
      return ['anna@houseandflats.com', 'property@houseandflats.com'].includes(email)
    },

    // Nombre (discord_hyf.nombre) del admin logueado, resuelto por su correo.
    myAdminNombre() {
      const email = String(this.currentUserEmail || '').trim().toLowerCase()
      if (!email) return null
      const match = this.discordUsers.find(u => String(u.correo || '').trim().toLowerCase() === email)
      return match ? String(match.nombre || '').trim().toLowerCase() : null
    },

    // Intakes visibles según permisos (defensa en el front; el backend ya filtra).
    // Si es acceso total => todos. Si es admin normal con nombre resuelto => solo
    // los suyos. Si no se pudo resolver el nombre => no filtra (confía en backend).
    visibleIntakes() {
      if (this.canSeeAllIntakes) return this.intakes
      const nombre = this.myAdminNombre
      if (!nombre) return this.intakes
      return this.intakes.filter(i => String(i.admin_intake || '').trim().toLowerCase() === nombre)
    },

    // Todos los intakes que hay en memoria: los de la página más los hermanos
    // que mandó el backend para completar los grupos. Es sobre esto que
    // trabajan las acciones que tocan "todos los intakes de esta persona".
    allGroupIntakes() {
      const seen = new Set()
      const list = []
      for (const intake of this.visibleIntakes.concat(this.siblings)) {
        const id = String(intake.id)
        if (seen.has(id)) continue
        seen.add(id)
        list.push(intake)
      }
      return list
    },

    groupedIntakes() {
      // La página ya viene filtrada Y agrupada del backend: trae un intake por
      // fila (`data`) y el resto de los intakes de esas personas en `siblings`.
      // Acá se rehace el grupo para tener los miembros juntos; los hermanos
      // entran al union-find pero NO abren fila propia.
      const list = this.allGroupIntakes

      // Union-Find: los intakes se acumulan si comparten el mismo número
      // (normalizado) O el mismo correo (normalizado). La relación es transitiva:
      // si A y B comparten teléfono, y B y C comparten correo, los tres quedan
      // en el mismo grupo. Teléfonos/correos vacíos no acumulan (cada intake
      // sin esos datos queda solo hasta que coincida por el otro campo).
      const parent = {}
      const ensure = (k) => { if (!(k in parent)) parent[k] = k }
      const find = (x) => {
        while (parent[x] !== x) {
          parent[x] = parent[parent[x]]
          x = parent[x]
        }
        return x
      }
      const union = (a, b) => {
        const ra = find(a)
        const rb = find(b)
        if (ra !== rb) parent[ra] = rb
      }

      for (const intake of list) {
        const node = `i_${intake.id}`
        ensure(node)
        const phone = this.normalizePhone(intake.whatsapp)
        if (phone && phone.length >= MIN_GROUP_PHONE_DIGITS) { const pk = `p_${phone}`; ensure(pk); union(node, pk) }
        const email = this.normalizeEmail(intake.email)
        if (email) { const ek = `e_${email}`; ensure(ek); union(node, ek) }
      }

      const groupsMap = {}
      for (const intake of list) {
        const root = find(`i_${intake.id}`)
        if (!groupsMap[root]) groupsMap[root] = []
        groupsMap[root].push(intake)
      }

      // El orden y la cantidad de filas los manda la página: se recorre en el
      // orden que devolvió el backend y cada grupo se emite una sola vez. Con
      // el backend agrupando, los intakes de `data` ya son de personas
      // distintas, así que salen PER_PAGE filas.
      const order = []
      const emitted = new Set()
      for (const intake of this.visibleIntakes) {
        const root = find(`i_${intake.id}`)
        if (!root || emitted.has(root) || !groupsMap[root]) continue
        emitted.add(root)
        order.push(root)
      }

      return order.map(root => {
        const members = groupsMap[root].slice().sort((a, b) => b.id - a.id)
        const representative = members[0]
        return {
          key: representative.id, // identificador estable del grupo (único)
          representative,
          members,
          isGroup: members.length > 1,
          selectedMember: null,
        }
      })
    },

    // Lo que se pinta en la tabla. Ya es la página: el recorte lo hizo el
    // backend, acá no se corta nada.
    pagedIntakes() {
      return this.groupedIntakes
    },

    totalPages() {
      return this.serverTotalPages || 1
    },

    visiblePages() {
      const t = this.totalPages
      const c = this.page
      const maxButtons = 10

      if (t <= maxButtons) {
        const all = []
        for (let i = 1; i <= t; i++) all.push(i)
        return all
      }

      // Calculate 0-based window index for groups of `maxButtons` pages.
      // Example: pages 1..10 => windowIndex 0, pages 11..20 => windowIndex 1, etc.
      const windowIndex = Math.floor((c - 1) / maxButtons)
      const start = windowIndex * maxButtons + 1
      const end = Math.min(start + maxButtons - 1, t)
      const result = []
      for (let i = start; i <= end; i++) result.push(i)
      return result
    },
  },

  watch: {
    // La búsqueda se manda al backend: se espera a que el usuario deje de
    // tipear para no pedir una página por cada tecla.
    searchQuery() {
      if (this._searchTimer) clearTimeout(this._searchTimer)
      this._searchTimer = setTimeout(() => { this.applyFilterChange() }, SEARCH_DEBOUNCE_MS)
    },

    filterTier() {
      this.applyFilterChange()
    },

    filterEtapa() {
      this.applyFilterChange()
    },

    filterUrgencia() {
      this.applyFilterChange()
    },

    filterCodigo() {
      this.applyFilterChange()
    },

    filterAdmin() {
      this.applyFilterChange()
    },

    filterFechaDesde() {
      this.applyFilterChange()
    },

    filterFechaHasta() {
      this.applyFilterChange()
    },
  },

  mounted() {
    this.loadCurrentUserEmail()
    this.syncStickyOffset()
    window.addEventListener('resize', this.syncStickyOffset)
    // Si venimos con ?intake=ID (deep-link desde Discord), el backend resuelve
    // en qué página cayó ese intake y devuelve esa; acá se expande y se hace
    // scroll hasta él.
    const focusId = Number(this.$route && this.$route.query ? this.$route.query.intake : 0) || null
    this.fetchIntakes({ focusId }).then(() => this.focusDeepLinkedIntake())
    this.fetchFilterOptions()
    this.fetchDiscordUsers()
    this.fetchChatStages()

    // Tiempo real: cuando un intake se asigna (manual o automáticamente), el
    // backend emite 'intake.assigned' y acá lo subimos arriba sin recargar.
    try {
      initializePusher()
      subscribeToChannel('intakes', 'intake.assigned', this.onIntakeAssigned)
      // Y cuando el chat del lead cambia de columna/sección en Mensajería,
      // refrescamos la columna "Etapa" sin recargar.
      subscribeToChannel('intakes', 'intake.stage_changed', this.onChatStageChanged)
    } catch (e) {
      console.warn('[Dashboard] no se pudo suscribir a Pusher (intakes):', e)
    }
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.syncStickyOffset)
    try { unsubscribeFromChannel('intakes') } catch (e) { /* ignore */ }
    if (this._intakeRefetchTimer) clearTimeout(this._intakeRefetchTimer)
    if (this._searchTimer) clearTimeout(this._searchTimer)
    if (this._shareTimer) clearTimeout(this._shareTimer)
  },

  methods: {

    // Correo del usuario logueado desde localStorage (igual que otros componentes).
    loadCurrentUserEmail() {
      let email = localStorage.getItem('correo') || ''
      if (!email) {
        try {
          const usuario = JSON.parse(localStorage.getItem('usuario') || '{}')
          email = usuario.correo || usuario.email || ''
        } catch (e) {
          email = ''
        }
      }
      this.currentUserEmail = String(email || '').trim().toLowerCase()
    },

    // Extrae el array de items de una respuesta del endpoint, tolerando las
    // distintas formas posibles: paginador Laravel { data:[...] }, respuesta
    // envuelta { data: { data:[...] } } o array plano [...].
    extractIntakeItems(resData) {
      let wrapper = resData
      if (wrapper && wrapper.data && wrapper.data.data && Array.isArray(wrapper.data.data)) {
        wrapper = wrapper.data
      }
      if (wrapper && Array.isArray(wrapper.data)) return wrapper.data
      if (Array.isArray(wrapper)) return wrapper
      if (resData && Array.isArray(resData.data)) return resData.data
      return []
    },

    // Filtros que se le mandan al backend. Son los mismos que antes se
    // aplicaban en memoria; los nombres los espeja DashboardIntakeController.
    filterParams() {
      const params = {}
      const q = String(this.searchQuery || '').trim()
      if (q)                     params.search       = q
      if (this.filterTier)       params.tier         = this.filterTier
      if (this.filterEtapa)      params.etapa        = this.filterEtapa
      if (this.filterUrgencia)   params.urgencia     = this.filterUrgencia
      if (this.filterCodigo)     params.codigo       = this.filterCodigo
      if (this.filterAdmin)      params.admin        = this.filterAdmin
      if (this.filterFechaDesde) params.fecha_desde  = this.filterFechaDesde
      if (this.filterFechaHasta) params.fecha_hasta  = this.filterFechaHasta
      return params
    },

    // Trae UNA página de intakes ya filtrada por el backend. El dashboard nunca
    // se descarga la tabla entera: antes lo hacía (páginas de 100 en tandas
    // hasta tener todo) y con varios miles de intakes tumbaba el browser.
    //
    // `focusId` es para el deep-link ?intake=ID: el backend responde con la
    // página en la que está ese intake en vez de la pedida.
    async fetchIntakes(options) {
      const opts = options || {}
      const seq = ++this.intakesSeq
      this.loading = true
      this.error = null

      const params = Object.assign({ per_page: PER_PAGE, page: this.page }, this.filterParams())
      if (opts.focusId) params.focus_id = opts.focusId

      try {
        const res = await axios.get(`${API_BASE}dashboard/intakes`, { params })
        // Se pidió otra página/filtro en el medio: esta respuesta ya no vale.
        if (seq !== this.intakesSeq) return

        const body = (res && res.data) ? res.data : {}
        this.intakes = this.extractIntakeItems(body)
        this.siblings = Array.isArray(body.siblings) ? body.siblings : []
        this.total = Number(body.total) || 0
        this.serverTotalPages = Number(body.last_page) || 1
        this.page = Number(body.current_page) || this.page

        // Se borraron intakes y la página en la que estábamos ya no existe.
        if (!this.intakes.length && this.page > this.serverTotalPages) {
          this.page = this.serverTotalPages
          this.loading = false
          return this.fetchIntakes()
        }
      } catch (e) {
        console.error('[Dashboard] fetchIntakes:', e)
        if (seq === this.intakesSeq) {
          this.error = 'No se pudieron cargar los intakes.'
          this.intakes = []
          this.siblings = []
        }
      } finally {
        if (seq === this.intakesSeq) this.loading = false
      }
    },

    // Recarga la página actual conservando filtros (botón Actualizar, y después
    // de acciones que cambian el listado).
    reloadIntakes() {
      return this.fetchIntakes()
    },

    // Cambió un filtro o la búsqueda: se vuelve a la página 1 y se pide de nuevo.
    applyFilterChange() {
      this.resetListPage()
      return this.fetchIntakes()
    },

    // Opciones de los selectores (códigos de campaña, urgencias). Con el
    // listado paginado ya no se pueden deducir de los intakes cargados.
    async fetchFilterOptions() {
      try {
        const res = await axios.get(`${API_BASE}dashboard/intakes/filtros`)
        const data = (res && res.data && res.data.data) ? res.data.data : {}
        this.catalogCodigos   = Array.isArray(data.codigos) ? data.codigos : []
        this.catalogSinCodigo = !!data.tiene_sin_codigo
      } catch (e) {
        console.error('[Dashboard] fetchFilterOptions:', e)
        this.catalogCodigos = []
        this.catalogSinCodigo = false
      }
    },

    // Recorre TODAS las páginas con los filtros puestos. Solo lo usan Exportar
    // y Estadísticas, a pedido: el listado nunca hace esto.
    async fetchAllIntakes(label) {
      this.bulkLoading = true
      this.bulkLabel = label || 'Cargando'
      this.bulkLoaded = 0
      this.bulkError = ''

      const LOTE = 4 // páginas en paralelo, para no dispararle 20 requests de una
      // Los filtros se congelan al empezar: si el usuario toca uno mientras
      // carga, el Excel igual sale con un criterio consistente. Y `siblings=0`
      // porque acá no se agrupa nada: se recorre todo de todas formas.
      const filtros = Object.assign({ siblings: 0 }, this.filterParams())
      const pedirPagina = (page) => axios.get(`${API_BASE}dashboard/intakes`, {
        params: Object.assign({ per_page: BULK_PER_PAGE, page }, filtros),
      })

      try {
        const first = await pedirPagina(1)
        const body = (first && first.data) ? first.data : {}
        let todos = this.extractIntakeItems(body)
        const lastPage = Number(body.last_page) || 1
        this.bulkLoaded = todos.length

        for (let page = 2; page <= lastPage; page += LOTE) {
          const tanda = []
          for (let p = page; p < page + LOTE && p <= lastPage; p++) {
            tanda.push(pedirPagina(p).catch(e => {
              console.error('[Dashboard] fetchAllIntakes página', p, e)
              return null
            }))
          }
          const responses = await Promise.all(tanda)
          for (const res of responses) {
            if (res) todos = todos.concat(this.extractIntakeItems(res.data))
          }
          this.bulkLoaded = todos.length
        }

        return todos
      } finally {
        this.bulkLoading = false
        this.bulkLabel = ''
        this.bulkLoaded = 0
      }
    },

    resetListPage() {
      this.page = 1
      this.expandedId = null
      this.expandedGroupPhone = null
      this.selectedGroupIntakeId = null
    },

    clearFechaRegistro() {
      this.filterFechaDesde = ''
      this.filterFechaHasta = ''
    },

    // Normaliza un WhatsApp a solo dígitos para comparar/agrupar: así el mismo
    // número con o sin "+" (o con espacios/guiones) cuenta como el mismo.
    normalizePhone(w) {
      if (!w) return null
      const digits = String(w).replace(/\D+/g, '')
      return digits || null
    },

    // Un número con menos de MIN_PHONE_DIGITS dígitos no sirve para escribirle
    // por WhatsApp (ej. "90645", "28040645"). El backend usa el mismo umbral en
    // IntakeIncompletePhoneNotifier::MIN_DIGITS: si se cambia allá, cambiarlo acá.
    hasIncompletePhone(intake) {
      if (!intake) return false
      const digits = String(intake.whatsapp || '').replace(/\D+/g, '')
      return digits.length < MIN_PHONE_DIGITS
    },

    // El correo pidiendo el número lo dispara el bot cuando detecta que el
    // número no sirve, una sola vez por intake. Acá solo mostramos qué pasó;
    // desde el dashboard no se envía ni se reenvía.
    incompletePhoneTitle(intake) {
      const sentAt = intake && intake.telefono_incompleto_email_at
      return sentAt
        ? `Número incompleto. Se le pidió el número por correo el ${this.formatDate(sentAt)}.`
        : 'Número incompleto. No se envió el correo de solicitud.'
    },

    // Lead esperando que conteste el correo del número: el bot vio que el
    // WhatsApp no servía, se le pidió uno bueno por mail y por eso NO tiene
    // conversación. No es un lead abandonado: cuando conteste, el webhook
    // /webhook/intake-telefono le pisa el número y dispara el primer contacto.
    //
    // La condición NO mira los dígitos a propósito: el bot también manda el
    // correo cuando el número está bien formado pero la línea no existe en
    // WhatsApp, y ese caso hay que mostrarlo igual.
    awaitingPhoneByEmail(intake) {
      if (!intake) return false
      return !!intake.telefono_incompleto_email_at && !intake.chat_conversation_id
    },

    // Lo poco que entra en la celda: cuándo se le pidió el número. El resto de
    // la explicación va en el tooltip (phoneEmailFullText).
    phoneEmailBadge(intake) {
      const sentAt = intake && intake.telefono_incompleto_email_at
      return sentAt ? `correo enviado ${this.formatDate(sentAt)}` : 'correo enviado'
    },

    // Texto completo del estado, para el tooltip del badge y el de la etapa.
    phoneEmailFullText(intake) {
      const sentAt = intake && intake.telefono_incompleto_email_at
      const cuando = sentAt ? ` el ${this.formatDate(sentAt)}` : ''
      return 'No se le creó la conversación porque mandaron mal el número. ' +
             `Se le envió un email${cuando} para pedírselo y cuando lo conteste ` +
             'se le mandará el mensaje de bienvenida.'
    },

    // Normaliza un correo (trim + minúsculas) para comparar/agrupar. Vacío => null.
    normalizeEmail(e) {
      if (!e) return null
      const s = String(e).trim().toLowerCase()
      return s || null
    },

    // Cada página es un request: no hay más intakes en memoria que los de la
    // página que se está viendo.
    goToPage(p) {
      const target = Math.min(Math.max(Number(p) || 1, 1), this.totalPages)
      if (target === this.page || this.loading) return
      this.page = target
      this.expandedId = null
      this.expandedGroupPhone = null
      this.selectedGroupIntakeId = null
      this.fetchIntakes()
    },

    toggleExpand(id) { this.expandedId = this.expandedId === id ? null : id },

    // Deep-link: abre directo el intake indicado en ?intake=ID (link/botón de Discord).
    // La página en la que cae la resolvió el backend (parámetro focus_id), así
    // que acá solo queda ubicar su grupo, expandirlo y hacer scroll.
    focusDeepLinkedIntake() {
      const raw = this.$route && this.$route.query ? this.$route.query.intake : null
      if (!raw) return
      const targetId = Number(raw)
      if (!targetId) return

      // Ubicar el grupo (representante o miembro) que contiene el intake.
      const groups = this.groupedIntakes
      const groupIndex = groups.findIndex(g =>
        g.representative.id === targetId || g.members.some(m => m.id === targetId)
      )
      // Si un filtro activo lo oculta, no forzamos nada (el usuario mantiene su filtro).
      if (groupIndex === -1) return

      const group = groups[groupIndex]

      // Si el intake es miembro de un grupo (y no el representante), abrimos el grupo
      // y lo seleccionamos para que su fila sea visible.
      if (group.isGroup && group.representative.id !== targetId) {
        const member = group.members.find(m => m.id === targetId)
        if (member) this.selectGroupMember(member, group)
      }

      // Expandir el detalle del intake.
      this.expandedId = targetId

      // Scroll a la fila del grupo una vez renderizada.
      this.$nextTick(() => {
        try {
          const el = document.getElementById(`intake-row-${group.representative.id}`)
          if (el && el.scrollIntoView) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        } catch (e) { /* noop */ }
      })
    },

    toggleGroup(phone) {
      this.expandedGroupPhone = this.expandedGroupPhone === phone ? null : phone
      this.expandedId = null
    },

    selectGroupMember(intake, group) {
      group.selectedMember = intake
      this.selectedGroupIntakeId = intake.id
      this.expandedId = intake.id
      this.expandedGroupPhone = null
    },

    getActiveIntake(group) {
      return group.selectedMember || group.representative
    },

    getMemberColor(memberIndex) {
      const colors = [
        { bg: '#FFF1E5', border: '#FFD4AE' },
        { bg: '#EAF4FF', border: '#BDD8FF' },
        { bg: '#EAFBF1', border: '#BFE8CF' },
        { bg: '#F3ECFF', border: '#D9C6FF' },
        { bg: '#FCEAF4', border: '#F5BDD9' },
      ]
      return colors[memberIndex % colors.length]
    },

    getGroupRowStyle(group) {
      if (!group.isGroup || !group.selectedMember) return {}
      const memberIndex = group.members.findIndex(m => m.id === group.selectedMember.id)
      if (memberIndex === -1) return {}
      const color = this.getMemberColor(memberIndex)
      return {
        backgroundColor: color.bg,
        borderLeftColor: color.border,
        borderLeftWidth: '4px',
      }
    },

    async updateAdminIntake(intake, newAdmin) {
      // Aplica el admin a TODOS los intakes del mismo número (ignorando el "+").
      // Si el número es único, afecta solo a este intake.
      // Se busca entre los intakes de la página MÁS los hermanos que mandó el
      // backend: si no, al asignar solo se actualizarían los del mismo número
      // que hubieran caído en esta página.
      const key = this.normalizePhone(intake.whatsapp)
      const targets = key
        ? this.allGroupIntakes.filter(i => this.normalizePhone(i.whatsapp) === key)
        : [intake]

      // Guardar valores previos para poder revertir si falla.
      const previous = targets.map(t => ({ intake: t, admin: t.admin_intake }))
      targets.forEach(t => { t.admin_intake = newAdmin })

      try {
        await Promise.all(targets.map(t =>
          axios.patch(`${API_BASE}dashboard/intakes/${t.id}`, { admin_intake: newAdmin })
        ))

        // Marcar la asignación local y subir el/los intake(s) arriba de todo,
        // replicando el orden del backend: COALESCE(assigned_at, created_at) DESC.
        const nowIso = new Date().toISOString()
        targets.forEach(t => { this.$set(t, 'assigned_at', nowIso) })
        this.sortIntakesByAssignment()
      } catch (e) {
        console.error('[Dashboard] updateAdminIntake:', e)
        // Revertir el cambio en el selector (no se asignó).
        previous.forEach(p => { p.intake.admin_intake = p.admin })

        // Si el backend bloqueó la asignación porque el admin no tiene sección
        // en Sophi, mostramos el pop-up explicándolo.
        const resp = e.response && e.response.data ? e.response.data : null
        if (e.response && e.response.status === 422 && resp && resp.code === 'section_not_found') {
          this.sectionErrorMsg = resp.error || resp.message ||
            `La sección de ${this.capitalizeFirst(newAdmin)} no existe en Sophi. No se puede asignar este intake.`
          this.showSectionErrorModal = true
        }
      }
    },

    closeSectionErrorModal() {
      this.showSectionErrorModal = false
      this.sectionErrorMsg = ''
    },

    // ── Etapa del chat (columna del tablero de Mensajería) ──────────────────
    // El backend resuelve la conversación del lead y adjunta su etapa a cada
    // intake; acá solo se pinta. Se actualiza sola por Pusher al mover la
    // tarjeta de columna en Mensajería (ver onChatStageChanged).
    chatStageLabel(intake) {
      if (!intake) return 'Sin chat'
      if (intake.chat_stage_nombre) return intake.chat_stage_nombre
      // Sin chat, pero por un motivo conocido: se le pidió el número por
      // correo y se está esperando la respuesta. Decir "Sin chat" acá hace
      // parecer que el lead se perdió, cuando en realidad está en curso.
      if (this.awaitingPhoneByEmail(intake)) return 'Contacto por email'
      // El chat existe pero todavía no está en ninguna columna del tablero.
      return intake.chat_conversation_id ? 'Sin etapa' : 'Sin chat'
    },

    // Catálogo de columnas del tablero de Mensajería (conversation_stage, ya
    // agrupadas por slug en el backend). Alimenta el filtro por etapa para que
    // estén TODAS las que existen, no solo las que hoy tienen algún lead. Si la
    // llamada falla, el filtro se arma igual con las etapas de los intakes.
    async fetchChatStages() {
      try {
        const res = await axios.get(`${API_BASE}support/stages`)
        const rows = (res && res.data && Array.isArray(res.data.data)) ? res.data.data : []
        this.chatStages = rows
          .map(s => String((s && s.nombre) || '').trim())
          .filter(Boolean)
      } catch (e) {
        this.chatStages = []
      }
    },

    chatStageTitle(intake) {
      if (this.awaitingPhoneByEmail(intake)) {
        return this.phoneEmailFullText(intake)
      }
      if (!intake || !intake.chat_conversation_id) {
        return 'Este lead todavía no tiene chat en Sophi'
      }
      const etapa = intake.chat_stage_nombre || 'sin etapa asignada'
      return intake.chat_section_nombre
        ? `Sección ${intake.chat_section_nombre} · ${etapa}`
        : `Etapa: ${etapa}`
    },

    // Pusher: el chat de un lead se movió de columna (o de sección) en
    // Mensajería. Actualizamos la etapa de todos los intakes de esa persona.
    onChatStageChanged(payload) {
      if (!payload) return
      const convId = payload.conversation_id ? String(payload.conversation_id) : ''
      const phone = this.normalizePhone(payload.telefono || payload.client_identifier || '')

      this.allGroupIntakes.forEach(intake => {
        const sameConv = convId && String(intake.chat_conversation_id || '') === convId
        const samePhone = phone && this.normalizePhone(intake.whatsapp) === phone
        if (!sameConv && !samePhone) return

        this.$set(intake, 'chat_conversation_id', convId || intake.chat_conversation_id)
        this.$set(intake, 'chat_stage_id', payload.stage_id != null ? payload.stage_id : null)
        this.$set(intake, 'chat_stage_nombre', payload.stage_nombre || '')
        this.$set(intake, 'chat_section_id', payload.section_id != null ? payload.section_id : null)
        this.$set(intake, 'chat_section_slug', payload.section_slug || '')
        this.$set(intake, 'chat_section_nombre', payload.section_nombre || '')
      })
    },

    // Abre en Mensajería (Sophi) el chat del lead de este intake. La conversación
    // se crea al asignar el intake a un admin, así que un intake sin asignar
    // normalmente todavía no tiene chat: en ese caso avisamos con un pop-up.
    //
    // Va a la consola (`admin-mensajeria`), que lee ?conversation en
    // DashboardV2.openFromRoute. `from` es para que su botón "Salir" devuelva
    // acá y no al backoffice: la consola es a pantalla completa y sin eso no
    // hay vuelta.
    async openChat(intake) {
      if (!intake || this.chatLoadingId) return

      // El listado ya trae el chat del lead (es lo que alimenta la columna
      // "Etapa"): si lo tenemos, vamos directo sin pedirlo de nuevo.
      if (intake.chat_conversation_id) {
        const q = { conversation: String(intake.chat_conversation_id), from: 'intakes' }
        if (intake.chat_section_slug) q.section = intake.chat_section_slug
        this.$router.push({ name: 'admin-mensajeria', query: q })
        return
      }

      this.chatLoadingId = intake.id
      try {
        const { data } = await axios.get(`${API_BASE}dashboard/intakes/${intake.id}/conversation`)
        const conv = data && data.data ? data.data : null

        if (!conv || !conv.id) {
          this.noChatMsg = `El lead del intake #${intake.id} todavía no tiene chat en Sophi.`
          this.showNoChatModal = true
          return
        }

        // `section` le permite a Mensajería abrir el chat en su sección sin
        // tener que resolverla de nuevo.
        const query = { conversation: String(conv.id), from: 'intakes' }
        if (conv.section_slug) query.section = conv.section_slug
        this.$router.push({ name: 'admin-mensajeria', query })
      } catch (e) {
        console.error('[Dashboard] openChat:', e)
        this.noChatMsg = 'No se pudo abrir el chat de este lead. Intentá de nuevo en un momento.'
        this.showNoChatModal = true
      } finally {
        this.chatLoadingId = null
      }
    },

    closeNoChatModal() {
      this.showNoChatModal = false
      this.noChatMsg = ''
    },

    // Reordena la página igual que el backend: los asignados (por assigned_at)
    // primero, el resto por created_at. Así un intake recién asignado sube
    // arriba dentro de la página, sin tener que recargarla.
    sortIntakesByAssignment() {
      const ts = (i) => {
        const t = new Date(i.assigned_at || i.created_at).getTime()
        return Number.isNaN(t) ? 0 : t
      }
      this.intakes.sort((a, b) => ts(b) - ts(a))
    },

    // Pusher: un intake fue asignado (manual o automáticamente). Si está en la
    // página que se está viendo, lo actualizamos y lo subimos arriba sin red;
    // si no, se recarga la página (debounced).
    onIntakeAssigned(payload) {
      if (!payload || !payload.intake_id) return
      const targetId = Number(payload.intake_id)
      const existing = this.intakes.find(i => Number(i.id) === targetId)
      if (existing) {
        this.$set(existing, 'admin_intake', payload.admin_intake)
        this.$set(existing, 'assigned_at', payload.assigned_at || new Date().toISOString())
        this.sortIntakesByAssignment()
      } else {
        this.debouncedFetchIntakes()
      }
    },

    // Reagrupa varios eventos seguidos (ej. reparto de varios intakes) en un
    // solo fetch para no recargar la lista muchas veces.
    debouncedFetchIntakes() {
      if (this._intakeRefetchTimer) clearTimeout(this._intakeRefetchTimer)
      this._intakeRefetchTimer = setTimeout(() => { this.reloadIntakes() }, 800)
    },

    editComment(intake) {
      this.editingCommentId = intake.id
      this.commentBackup = intake.intake_comentario
    },

    async saveComment(intake) {
      try {
        await axios.patch(`${API_BASE}dashboard/intakes/${intake.id}`, {
          intake_comentario: intake.intake_comentario,
        })
        this.editingCommentId = null
        this.commentBackup = null
      } catch (e) {
        console.error('[Dashboard] saveComment:', e)
        intake.intake_comentario = this.commentBackup
        this.editingCommentId = null
      }
    },

    cancelComment() {
      if (this.editingCommentId) {
        const intake = this.allGroupIntakes.find(i => i.id === this.editingCommentId)
        if (intake && this.commentBackup !== null) {
          intake.intake_comentario = this.commentBackup
        }
        this.editingCommentId = null
        this.commentBackup = null
      }
    },

    copyMessageToClipboard(intake) {
      const adminName = intake.admin_intake
        ? this.capitalizeFirst(intake.admin_intake)
        : 'House and Flats'

      const nombreCompleto = intake.nombre || ''
      const firstName = nombreCompleto.split(' ')[0] || nombreCompleto

      const shortlistLinks = (intake.short_list || [])
        .filter(sl => sl.estado !== 'descartada')
        .map((sl, index) => `* Opción ${index + 1}: houseandflats.com/alojamiento/${sl.alojamiento_id}?estado=${encodeURIComponent(sl.id)}`)
        .join('\n')

      const message = `Hola ${firstName}¿cómo estás?\n\nTe escribe ${adminName} de House and Flats, recibimos tu solicitud de alojamiento y seré tu ejecutivo a cargo 🙌🏻\n\nAdjunto a continuación la lista de alternativas:\n${shortlistLinks}`

      navigator.clipboard.writeText(message).then(() => {
        this.$set(this.copyFeedback, intake.id, { type: 'ok', msg: '✓ Mensaje copiado al portapapeles' })
        setTimeout(() => {
          this.$delete(this.copyFeedback, intake.id)
        }, 3000)
      }).catch(err => {
        console.error('[Dashboard] Error copiando:', err)
        this.$set(this.copyFeedback, intake.id, { type: 'err', msg: '✕ Error al copiar' })
        setTimeout(() => {
          this.$delete(this.copyFeedback, intake.id)
        }, 3000)
      })
    },

    capitalizeFirst(str) {
      return str.charAt(0).toUpperCase() + str.slice(1)
    },

    // Link directo al intake (mismo deep-link que usan Discord y Reservas).
    intakeShareUrl(id) {
      const origin = typeof window !== 'undefined' && window.location ? window.location.origin : ''
      const routerBase = this.$router && this.$router.options && this.$router.options.base
        ? String(this.$router.options.base).replace(/\/$/, '')
        : ''
      const base = String(process.env.VUE_APP_SITE_URL || (origin + routerBase)).replace(/\/$/, '')
      return `${base}/dashboard-intakes?intake=${encodeURIComponent(id)}`
    },

    async shareIntake(intake) {
      if (!intake || !intake.id) return
      const link = this.intakeShareUrl(intake.id)
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(link)
        } else {
          // http / navegadores viejos: copia con un textarea temporal.
          const ta = document.createElement('textarea')
          ta.value = link
          ta.setAttribute('readonly', '')
          ta.style.position = 'fixed'
          ta.style.opacity = '0'
          document.body.appendChild(ta)
          ta.select()
          const ok = document.execCommand('copy')
          document.body.removeChild(ta)
          if (!ok) throw new Error('execCommand copy falló')
        }
        this.sharedId = intake.id
        clearTimeout(this._shareTimer)
        this._shareTimer = setTimeout(() => { this.sharedId = null }, 2000)
      } catch (e) {
        console.error('[Dashboard] No se pudo copiar el link del intake:', e)
        // Último recurso: que lo copie a mano.
        window.prompt('Copiá el link del intake:', link)
      }
    },

    // El detalle abierto puede ser un intake de la página o un hermano suyo
    // (al elegir otro intake dentro de un grupo).
    getExpandedIntake() {
      return this.allGroupIntakes.find(i => i.id === this.expandedId) || {}
    },

    // Aplica cambios sobre el intake que está en pantalla, esté en la página o
    // entre los hermanos. Devuelve false si no lo tenemos cargado (entonces el
    // que llama recarga la página).
    mergeIntakeInPlace(id, cambios) {
      if (!cambios) return false
      for (const lista of [this.intakes, this.siblings]) {
        const idx = lista.findIndex(i => Number(i.id) === Number(id))
        if (idx === -1) continue
        this.$set(lista, idx, Object.assign({}, lista[idx], cambios))
        return true
      }
      return false
    },

    openAddAlojamientoModal(intake) {
      this.addAlojIntakeId = intake.id
      this.showAddAlojModal = true
      this.newAlojId = null
      this.addAlojFeedback = null
    },

    closeAddAlojamientoModal() {
      this.showAddAlojModal = false
      this.newAlojId = null
      this.addAlojFeedback = null
      this.addAlojIntakeId = null
    },

    async addAlojamientoToShortlist() {
      if (!this.newAlojId || !this.addAlojIntakeId) return

      this.addingAlojamiento = true
      this.addAlojFeedback = null

      try {
        const res = await axios.post(`${API_BASE}dashboard/intakes/${this.addAlojIntakeId}/add-accommodation`, {
          alojamiento_id: this.newAlojId,
        })

        const payload = res.data && res.data.data ? res.data.data : res.data
        const alojId = payload && payload.alojamiento_id ? payload.alojamiento_id : this.newAlojId

        this.addAlojFeedback = {
          type: 'ok',
          msg: `✓ Alojamiento #${alojId} agregado a la shortlist`,
        }
        setTimeout(() => {
          this.reloadIntakes()
          this.closeAddAlojamientoModal()
        }, 1500)
      } catch (e) {
        console.error('[Dashboard] addAlojamientoToShortlist:', e)
        const errorMsg = (e.response && e.response.data && (e.response.data.message || e.response.data.error))
          ? (e.response.data.message || e.response.data.error)
          : 'Error al agregar alojamiento'
        this.addAlojFeedback = { type: 'err', msg: `✕ ${errorMsg}` }
      } finally {
        this.addingAlojamiento = false
      }
    },

    async discardShortlist(sl) {
      const key = `discard-${sl.id}`
      this.actionLoading = key
      this.$delete(this.actionFeedback, sl.id)
      try {
        await axios.patch(`${API_BASE}dashboard/short-list/${sl.id}/discard`)
        sl.estado = 'descartada'
        this.$set(this.actionFeedback, sl.id, { type: 'ok', msg: '✓ Shortlist descartada.' })
      } catch (e) {
        console.error('[Dashboard] discardShortlist:', e)
        this.$set(this.actionFeedback, sl.id, { type: 'err', msg: '✕ Error al descartar. Revisá el backend.' })
      } finally {
        this.actionLoading = null
        setTimeout(() => this.$delete(this.actionFeedback, sl.id), 4000)
      }
    },

    // ── Asignar admin en celular ──────────────────────────────────────────
    // Etiqueta legible del admin de un intake (la misma que muestra el select).
    adminLabel(intake) {
      const val = (intake && intake.admin_intake) ? String(intake.admin_intake) : ''
      if (!val) return 'Sin asignar'
      const opt = this.adminOptions.find(o => o.value === val)
      return opt ? opt.label : this.capitalizeFirst(val)
    },

    openAdminPicker(intake) {
      if (!intake || !intake.id) return
      this.adminPicker = { open: true, intake }
    },

    closeAdminPicker() {
      this.adminPicker.open = false
      this.adminPicker.intake = null
    },

    pickAdmin(value) {
      const intake = this.adminPicker.intake
      this.closeAdminPicker()
      if (!intake) return
      // Sin cambios: no se dispara el PATCH.
      if (String(intake.admin_intake || '') === String(value || '')) return
      this.updateAdminIntake(intake, value)
    },

    // Abre el modal de borrado. Si el intake viene de un grupo (varios intakes
    // del mismo contacto), el modal deja elegir entre borrar solo el que se está
    // viendo o todos los del grupo.
    deleteIntake(intake, group) {
      if (!intake) return
      const members = (group && group.isGroup && Array.isArray(group.members)) ? group.members.slice() : []
      this.deleteModal = {
        open: true,
        intake,
        members: members.length > 1 ? members : [],
        scope: 'one',
        selectedId: intake.id,
        deleting: false,
        error: '',
      }
    },

    closeDeleteModal() {
      if (this.deleteModal.deleting) return
      this.deleteModal.open = false
      this.deleteModal.intake = null
      this.deleteModal.members = []
      this.deleteModal.scope = 'one'
      this.deleteModal.selectedId = null
      this.deleteModal.error = ''
    },

    async confirmDeleteIntake() {
      const modal = this.deleteModal
      const target = modal.intake
      if (!target || modal.deleting) return

      // Alcance: el intake elegido en el select (o el de la fila si no hay
      // grupo), o todos los del grupo.
      const borrarTodos = modal.scope === 'all' && modal.members.length > 1
      const unicoId = modal.selectedId != null ? modal.selectedId : target.id
      const ids = borrarTodos ? modal.members.map(m => m.id) : [unicoId]

      modal.deleting = true
      modal.error = ''
      this.actionLoading = `delete-${target.id}`

      try {
        // El endpoint es por id: se marcan uno por uno y después se resuelve
        // qué pasó con cada uno (puede fallar alguno y no todos).
        const results = await Promise.all(ids.map(id =>
          axios.patch(`${API_BASE}dashboard/intakes/${id}`, { eliminado: 1 })
            .then(() => ({ id, ok: true }))
            .catch(e => {
              console.error('[Dashboard] deleteIntake:', e)
              return { id, ok: false, msg: this.errorMessageOf(e, 'Error al eliminar intake') }
            })
        ))

        const okIds  = results.filter(r => r.ok).map(r => String(r.id))
        const failed = results.filter(r => !r.ok)

        if (okIds.length) {
          const okSet = new Set(okIds)
          this.intakes  = this.intakes.filter(i => !okSet.has(String(i.id)))
          this.siblings = this.siblings.filter(i => !okSet.has(String(i.id)))
          // Si el detalle abierto era uno de los borrados, cerrarlo.
          if (this.expandedId && okSet.has(String(this.expandedId))) this.expandedId = null
          if (this.selectedGroupIntakeId && okSet.has(String(this.selectedGroupIntakeId))) {
            this.selectedGroupIntakeId = null
          }
          if (borrarTodos) this.expandedGroupPhone = null
          // La página quedó con menos filas: se recarga para completarla con
          // los intakes que ahora suben desde la página siguiente.
          this.debouncedFetchIntakes()
        }

        if (failed.length) {
          // Se deja el modal abierto para que el error quede a la vista.
          modal.error = (failed.length === ids.length)
            ? `✕ ${failed[0].msg}`
            : `✕ Se eliminaron ${okIds.length} de ${ids.length}. No se pudo con ${failed.map(f => '#' + f.id).join(', ')}.`
          return
        }

        modal.deleting = false
        this.closeDeleteModal()
      } finally {
        modal.deleting = false
        this.actionLoading = null
      }
    },

    // Mensaje de error legible de una respuesta de axios.
    errorMessageOf(e, fallback) {
      const data = e && e.response && e.response.data
      return (data && (data.message || data.error)) ? (data.message || data.error) : fallback
    },

    openEditModal(intake) {
      this.editingIntake = JSON.parse(JSON.stringify(intake))
      if (this.editingIntake && this.editingIntake.fecha_inicio) this.editingIntake.fecha_inicio = String(this.editingIntake.fecha_inicio).slice(0, 10)
      if (this.editingIntake && this.editingIntake.fecha_fin) this.editingIntake.fecha_fin = String(this.editingIntake.fecha_fin).slice(0, 10)
      this.showEditModal = true
      this.editingIntakeBackup = JSON.parse(JSON.stringify(intake))
      this.editFeedback = null
    },

    closeEditModal() {
      this.showEditModal = false
      this.editingIntake = null
      this.editingIntakeBackup = null
      this.savingEdit = false
      this.editFeedback = null
    },

    async saveEditIntake() {
      if (!this.editingIntake || !this.editingIntake.id) return
      const id = this.editingIntake.id
      this.savingEdit = true
      this.editFeedback = null
      try {
        const payload = {
          nombre: this.editingIntake.nombre,
          email: this.editingIntake.email,
          whatsapp: this.editingIntake.whatsapp,
          ciudad_destino: this.editingIntake.ciudad_destino,
          fecha_inicio: this.editingIntake.fecha_inicio,
          fecha_fin: this.editingIntake.fecha_fin,
          presupuesto_usd: this.editingIntake.presupuesto_usd,
          tipo_alojamiento: this.editingIntake.tipo_alojamiento,
          cantidad_personas: this.editingIntake.cantidad_personas,
          comentario_libre: this.editingIntake.comentario_libre,
        }

        const res = await axios.patch(`${API_BASE}dashboard/intakes/${id}`, payload)
        const updated = res.data && res.data.data ? res.data.data : res.data
        // El PATCH devuelve el intake sin relaciones: se mezcla sobre el que ya
        // está en pantalla para no perderle la shortlist ni la etapa del chat.
        const cambios = (updated && updated.id) ? updated : this.editingIntake
        if (!this.mergeIntakeInPlace(id, cambios)) this.reloadIntakes()

        this.editFeedback = { type: 'ok', msg: '✓ Cambios guardados' }
        setTimeout(() => this.closeEditModal(), 700)
      } catch (e) {
        console.error('[Dashboard] saveEditIntake:', e)
        const errorMsg = (e.response && e.response.data && (e.response.data.message || e.response.data.error))
          ? (e.response.data.message || e.response.data.error)
          : 'Error al guardar intake'
        this.editFeedback = { type: 'err', msg: `✕ ${errorMsg}` }
      } finally {
        this.savingEdit = false
      }
    },

    // ══ USUARIOS DE DISCORD ══
    openDiscordModal() {
      this.showDiscordModal = true
      this.discordFeedback = null
      this.editingDiscordId = null
      this.newDiscordUser = { nombre: '', correo: '', discord_id: '', handle: '' }
      this.fetchDiscordUsers()
    },

    closeDiscordModal() {
      this.showDiscordModal = false
      this.discordFeedback = null
      this.editingDiscordId = null
      this.newDiscordUser = { nombre: '', correo: '', discord_id: '', handle: '' }
    },

    // Carga los datos del usuario en el form de arriba y entra en modo edición.
    editDiscordUser(u) {
      this.editingDiscordId = u.id
      this.newDiscordUser = {
        nombre: u.nombre || '',
        correo: u.correo || '',
        discord_id: u.discord_id || '',
        handle: u.handle || '',
      }
      this.discordFeedback = null
    },

    // Sale del modo edición y limpia el form.
    cancelEditDiscord() {
      this.editingDiscordId = null
      this.newDiscordUser = { nombre: '', correo: '', discord_id: '', handle: '' }
      this.discordFeedback = null
    },

    async fetchDiscordUsers() {
      this.loadingDiscord = true
      try {
        const res = await axios.get(`${API_BASE}dashboard/discord-users`)
        const payload = res.data && res.data.data ? res.data.data : res.data
        this.discordUsers = Array.isArray(payload) ? payload : []
      } catch (e) {
        console.error('[Dashboard] fetchDiscordUsers:', e)
        this.discordFeedback = { type: 'err', msg: '✕ No se pudieron cargar los usuarios.' }
      } finally {
        this.loadingDiscord = false
      }
    },

    // Crea (POST) o edita (PATCH) un usuario de Discord según editingDiscordId.
    async saveDiscordUser() {
      if (!this.newDiscordUser.nombre || !this.newDiscordUser.correo || !this.newDiscordUser.discord_id) return
      this.savingDiscord = true
      this.discordFeedback = null

      const payload = {
        nombre: this.newDiscordUser.nombre.trim(),
        correo: this.newDiscordUser.correo.trim(),
        discord_id: String(this.newDiscordUser.discord_id).trim(),
        handle: this.newDiscordUser.handle ? this.newDiscordUser.handle.trim() : null,
      }

      try {
        if (this.editingDiscordId) {
          // Editar existente
          const res = await axios.patch(`${API_BASE}dashboard/discord-users/${this.editingDiscordId}`, payload)
          const updated = res.data && res.data.data ? res.data.data : null
          if (updated) {
            const idx = this.discordUsers.findIndex(u => u.id === this.editingDiscordId)
            if (idx !== -1) this.$set(this.discordUsers, idx, updated)
            else this.fetchDiscordUsers()
          } else {
            this.fetchDiscordUsers()
          }
          this.discordFeedback = { type: 'ok', msg: '✓ Usuario actualizado' }
        } else {
          // Crear nuevo
          const res = await axios.post(`${API_BASE}dashboard/discord-users`, payload)
          const created = res.data && res.data.data ? res.data.data : null
          if (created) this.discordUsers.push(created)
          else this.fetchDiscordUsers()
          this.discordFeedback = { type: 'ok', msg: '✓ Usuario agregado' }
        }

        this.newDiscordUser = { nombre: '', correo: '', discord_id: '', handle: '' }
        this.editingDiscordId = null
        setTimeout(() => { this.discordFeedback = null }, 3000)
      } catch (e) {
        console.error('[Dashboard] saveDiscordUser:', e)
        const errorMsg = (e.response && e.response.data && (e.response.data.message || e.response.data.error))
          ? (e.response.data.message || e.response.data.error)
          : 'Error al guardar usuario'
        this.discordFeedback = { type: 'err', msg: `✕ ${errorMsg}` }
      } finally {
        this.savingDiscord = false
      }
    },

    async deleteDiscordUser(user) {
      if (!confirm(`¿Eliminar a ${user.nombre} de la lista de Discord?`)) return
      this.deletingDiscordId = user.id
      try {
        await axios.delete(`${API_BASE}dashboard/discord-users/${user.id}`)
        this.discordUsers = this.discordUsers.filter(u => u.id !== user.id)
        this.discordFeedback = { type: 'ok', msg: '✓ Usuario eliminado' }
        setTimeout(() => { this.discordFeedback = null }, 3000)
      } catch (e) {
        console.error('[Dashboard] deleteDiscordUser:', e)
        this.discordFeedback = { type: 'err', msg: '✕ Error al eliminar usuario' }
      } finally {
        this.deletingDiscordId = null
      }
    },

    // ── Reparto de leads por % y horario, por código de campaña ──
    // El % que se carga por broker no es su tajada de los leads: es cuánto pesa
    // en el sorteo frente a los que estén en horario a la misma hora. La tajada
    // real la calcula repartoCuotasFranja / repartoCuotas.
    openRepartoModal() {
      this.showRepartoModal = true
      this.repartoFeedback = null
      this.nuevoCodigoAds = ''
      this.repartoAddPick = ''
      this.fetchReparto()
    },

    closeRepartoModal() {
      if (this.savingReparto || this.dupReparto.saving) return
      this.showRepartoModal = false
      this.repartoFeedback = null
      this.repartoCodigo = ''
      this.repartoRows = []
      this.nuevoCodigoAds = ''
      this.repartoAddPick = ''
      this.dupReparto.open = false
    },

    async fetchReparto() {
      this.loadingReparto = true
      try {
        const res = await axios.get(`${API_BASE}dashboard/lead-reparto`)
        const payload = res.data && res.data.data ? res.data.data : []
        this.repartoGrupos = Array.isArray(payload) ? payload : []
        // Al abrir, se muestra la primera campaña con reparto cargado.
        if (!this.repartoCodigo && this.repartoGrupos.length) {
          this.repartoCodigo = String(this.repartoGrupos[0].codigo_ads).toLowerCase()
        }
        this.cargarFilasDelCodigo()
      } catch (e) {
        console.error('[Dashboard] fetchReparto:', e)
        this.repartoFeedback = { type: 'err', msg: '✕ No se pudo cargar el reparto.' }
      } finally {
        this.loadingReparto = false
      }
    },

    // Vuelca a las filas editables el reparto guardado de la campaña elegida.
    cargarFilasDelCodigo() {
      const codigo = String(this.repartoCodigo || '').toLowerCase()
      const grupo = this.repartoGrupos.find(g => String(g.codigo_ads).toLowerCase() === codigo)
      this.repartoRows = grupo
        ? (grupo.rows || []).map(r => ({
            nombres: String(r.nombres || ''),
            correo: String(r.correo || ''),
            porcentaje: (r.porcentaje !== null && r.porcentaje !== undefined) ? parseFloat(r.porcentaje) : 0,
            activo: r.activo === undefined ? true : !!r.activo,
            // El horario viaja como mapa día -> franja; en pantalla se edita como
            // los 7 días fijos, que es lo único que Vue 2 sigue de forma reactiva.
            horarioDias: this.horarioDiasDesdeMapa(r.horario),
            horarioAbierto: false,
            // Calculadora de objetivo: no se guarda, es solo de la pantalla.
            // Declarados acá porque Vue 2 no sigue las propiedades que se
            // agregan después.
            objetivo: null,
            objetivoModo: 'semana',
            objetivoMsg: '',
            objetivoMsgTipo: '',
          }))
        : []
    },

    onRepartoCodigoChange() {
      this.repartoFeedback = null
      this.repartoAddPick = ''
      this.cargarFilasDelCodigo()
    },

    // Empieza el reparto de una campaña que todavía no aparece en la lista.
    crearCodigoReparto() {
      const codigo = String(this.nuevoCodigoAds || '').trim().toLowerCase()
      if (!codigo) return
      this.repartoCodigo = codigo
      this.nuevoCodigoAds = ''
      this.repartoFeedback = null
      this.cargarFilasDelCodigo()
    },

    addRepartoRow(row) {
      this.repartoRows.push(Object.assign(
        {
          nombres: '',
          correo: '',
          porcentaje: 0,
          activo: true,
          // Sin días marcados: recibe a cualquier hora, que es como venía
          // funcionando el reparto antes de los horarios.
          horarioDias: this.horarioDiasDesdeMapa(null),
          horarioAbierto: false,
          objetivo: null,
          objetivoModo: 'semana',
          objetivoMsg: '',
          objetivoMsgTipo: '',
        },
        row || {}
      ))
    },

    // Alta rápida desde el select: toma nombre y correo del admin elegido.
    addBrokerFromPick() {
      const correo = String(this.repartoAddPick || '').toLowerCase()
      this.repartoAddPick = ''
      if (!correo) return
      const u = this.repartoBrokerOptions.find(o => o.correo === correo)
        || this.discordUsers.find(d => String(d.correo || '').toLowerCase() === correo)
      if (!u) return
      this.addRepartoRow({
        nombres: String(u.nombre || u.nombres || '').toLowerCase(),
        correo: correo,
      })
    },

    // Sophi como broker: si le toca el sorteo, el lead queda en su sección con
    // el bot. El correo tiene que ser uno de un usuario existente.
    addSophiRow() {
      if (this.repartoTieneSophi) return
      const primero = this.discordUsers.length ? String(this.discordUsers[0].correo || '') : ''
      this.addRepartoRow({ nombres: 'sophi', correo: primero.toLowerCase() })
    },

    removeRepartoRow(i) {
      this.repartoRows.splice(i, 1)
    },

    // ── Horario de cada broker dentro del reparto (hora de Chile) ──
    //
    // En la base cada fila guarda un mapa { "1": {desde, hasta}, ... } con el
    // día ISO como clave y solo los días en que ese broker recibe leads; NULL
    // es "sin restricción" (24/7). En pantalla se edita como una lista fija de
    // los 7 días con un flag `on`: así Vue 2 sigue los cambios sin $set y el
    // usuario puede desmarcar un día sin perder las horas que ya había puesto.

    // Mapa guardado -> los 7 días editables.
    horarioDiasDesdeMapa(mapa) {
      const src = (mapa && typeof mapa === 'object') ? mapa : {}
      return DIAS_REPARTO.map(d => {
        const tramo = src[d.dia] || src[String(d.dia)] || null
        return {
          dia: d.dia,
          label: d.label,
          corto: d.corto,
          on: !!tramo,
          desde: this.normalizarHora((tramo && tramo.desde) || HORARIO_DEFAULT.desde),
          hasta: this.normalizarHora((tramo && tramo.hasta) || HORARIO_DEFAULT.hasta),
        }
      })
    },

    // Los 7 días editables -> mapa para el backend. Sin días marcados devuelve
    // null, que es como se guarda "sin restricción".
    horarioAMapa(row) {
      const dias = (row && row.horarioDias) || []
      const mapa = {}
      for (const d of dias) {
        if (!d.on) continue
        const desde = this.normalizarHora(d.desde)
        const hasta = this.normalizarHora(d.hasta)
        if (!desde || !hasta) continue
        mapa[String(d.dia)] = { desde, hasta }
      }
      return Object.keys(mapa).length ? mapa : null
    },

    horarioTieneDias(row) {
      return !!this.horarioAMapa(row)
    },

    // Solo los días prendidos: son los únicos que muestran horas en el modal.
    diasEncendidos(row) {
      return ((row && row.horarioDias) || []).filter(d => d.on)
    },

    esSophi(row) {
      return String((row && row.nombres) || '').toLowerCase().trim() === 'sophi'
    },

    // ── Simulación de la semana ──
    //
    // Se recorre la semana en tramos de media hora y en cada tramo se reparte un
    // lead entre los que están en horario, según su peso: es exactamente lo que
    // hace LeadRepartoService al entrar un lead, repetido sobre la semana.
    //
    // Supone que los leads entran parejo a toda hora, que es lo único que se
    // puede suponer sin mirar el histórico. Para cruzarlo con el tráfico real
    // está `php artisan reparto:probar <campaña> --dia=50`, que usa la curva
    // horaria de users_intake.
    //
    // Devuelve dos lecturas del mismo reparto:
    //   semana: % de TODOS los leads de la semana que se lleva cada fila.
    //   franja: % de los leads que entran MIENTRAS esa fila está en horario.
    // Para un broker 24/7 son el mismo número; para uno de medio turno, no, y
    // esa diferencia es justo lo que hay que mirar al cargarle el %.
    simularSemana(pesos, mapas) {
      const n = pesos.length
      const semana = new Array(n).fill(0)
      const propia = new Array(n).fill(0)
      const tramosPropios = new Array(n).fill(0)
      let tramos = 0

      for (const d of DIAS_REPARTO) {
        for (let t = 0; t < 1440; t += COBERTURA_PASO_MIN) {
          tramos++

          const disponibles = []
          for (let i = 0; i < n; i++) {
            if (pesos[i] > 0 && this.mapaCubre(mapas[i], d.dia, t)) disponibles.push(i)
          }

          // Prioridad: el que tiene horario cargado le gana al que está sin
          // restricción. Si a esta hora hay alguien con turno propio, los de
          // 24/7 no compiten — sus horas son para cubrir los huecos.
          const conTurno = disponibles.filter(i => !!mapas[i])
          const dentro = conTurno.length ? conTurno : disponibles

          let total = 0
          for (const i of dentro) total += pesos[i]

          // Los tramos sin nadie no suman para nadie: esos leads quedan en cola
          // hasta que alguien entra en turno (los reparte `reparto:pendientes`).
          // Por eso las cuotas pueden no llegar a 100 entre todos.
          if (total <= 0) continue

          for (const i of dentro) {
            const cuota = pesos[i] / total
            semana[i] += cuota
            propia[i] += cuota
            tramosPropios[i]++
          }
        }
      }

      return {
        semana: semana.map(s => (tramos ? (s / tramos) * 100 : 0)),
        franja: propia.map((s, i) => (tramosPropios[i] ? (s / tramosPropios[i]) * 100 : 0)),
      }
    },

    // Qué se llevaría la fila `i` si tuviera ese %, dejando a las demás como
    // están. `modo` es 'semana' o 'franja'.
    cuotaConPeso(i, peso, modo) {
      const pesos = this.repartoPesos.slice()
      pesos[i] = peso
      const sim = this.simularSemana(pesos, this.repartoMapas)
      return (modo === 'franja' ? sim.franja[i] : sim.semana[i]) || 0
    },

    // Despeja qué % hay que cargarle a la fila `i` para que se lleve `objetivo`.
    //
    // No hay fórmula cerrada: lo que se lleva una fila depende de con quién
    // comparte cada tramo de la semana. Se busca por bisección sobre la
    // simulación, que es creciente en el peso, y eso alcanza.
    //
    // El resultado incluye por qué NO se pudo, que es lo que más se necesita
    // cuando el broker trabaja pocas horas: ahí el techo no lo pone el número
    // sino el horario, y subirle el % no cambia nada.
    resolverObjetivo(i, objetivo, modo) {
      const f = peso => this.cuotaConPeso(i, peso, modo)
      // Medio punto de tolerancia: por debajo de eso es ruido de la simulación
      // (la semana se recorre de a media hora, no minuto a minuto).
      const TOL = 0.05

      if (!(objetivo > 0)) return { estado: 'cero' }

      const conMin = f(PORCENTAJE_MIN)
      const conMax = f(PORCENTAJE_MAX)

      if (conMax <= 0) return { estado: 'nunca' }

      // Está solo en toda su franja: se lleva lo mismo con cualquier %.
      if (conMax - conMin < TOL) return { estado: 'unico', cuota: conMax }

      // Ni con el % más alto llega. Es el caso de los turnos cortos: con 4 h los
      // sábados no hay número que dé el 20% de la semana.
      if (objetivo > conMax + TOL) return { estado: 'tope', cuota: conMax }

      // Ya con el % más bajo se pasa: para darle menos hay que subirles el % a
      // los que comparten su franja.
      if (objetivo < conMin - TOL) return { estado: 'minimo', peso: PORCENTAJE_MIN, cuota: conMin }

      let lo = PORCENTAJE_MIN
      let hi = PORCENTAJE_MAX
      for (let k = 0; k < 40; k++) {
        const mid = (lo + hi) / 2
        if (f(mid) < objetivo) lo = mid
        else hi = mid
      }

      const peso = Math.min(PORCENTAJE_MAX, Math.round(hi * 100) / 100)
      return { estado: 'ok', peso, cuota: f(peso) }
    },

    // ¿Se puede calcular el % de esta fila? Hace falta que esté activa y que el
    // objetivo sea un porcentaje con sentido.
    objetivoListo(row) {
      const v = parseFloat(row && row.objetivo)
      return !!(row && row.activo) && v > 0 && v <= 100
    },

    // Escribe en la fila el % que hace falta para llegar a su objetivo, o
    // explica por qué no se puede.
    aplicarObjetivo(row, i) {
      if (!this.objetivoListo(row)) return

      const objetivo = parseFloat(row.objetivo)
      const modo  = row.objetivoModo === 'franja' ? 'franja' : 'semana'
      const donde = modo === 'franja' ? 'de los leads de su franja' : 'de los leads de la semana'
      const res   = this.resolverObjetivo(i, objetivo, modo)

      switch (res.estado) {
        case 'ok':
          row.porcentaje = res.peso
          row.objetivoMsg = `✓ Cargado ${this.numeroEs(res.peso)}%: le toca ${this.pctReparto(res.cuota)} ${donde}`
          row.objetivoMsgTipo = 'ok'
          break

        case 'unico':
          row.objetivoMsg = `Es el único en su franja: se lleva ${this.pctReparto(res.cuota)} ${donde} `
            + 'con cualquier %. El número no cambia nada mientras esté solo.'
          row.objetivoMsgTipo = ''
          break

        case 'tope':
          row.objetivoMsg = `✕ Con este horario no pasa de ${this.pctReparto(res.cuota)} ${donde}, `
            + `ni cargándole el máximo (${this.numeroEs(PORCENTAJE_MAX)}%). Para llegar a `
            + `${this.pctReparto(objetivo)} tiene que cubrir más horas, o los que comparten su `
            + 'franja tienen que bajar su %.'
          row.objetivoMsgTipo = 'err'
          break

        case 'minimo':
          row.porcentaje = res.peso
          row.objetivoMsg = `Con el mínimo (${this.numeroEs(PORCENTAJE_MIN)}%) ya se lleva ${this.pctReparto(res.cuota)} ${donde}, `
            + 'más que lo pedido: para bajarlo hay que subirles el % a los que comparten su franja.'
          row.objetivoMsgTipo = 'err'
          break

        case 'nunca':
          row.objetivoMsg = '✕ Esta fila no recibe leads: revisá que esté activa y que su horario tenga días marcados.'
          row.objetivoMsgTipo = 'err'
          break

        default:
          row.objetivoMsg = ''
          row.objetivoMsgTipo = ''
      }
    },

    // Lo que se dibuja en la barrita de cada fila: la tajada REAL de la semana,
    // no el % cargado. Para un broker que atiende siempre son lo mismo; para uno
    // que trabaja un solo día, no: su % es su peso en el sorteo de ESE día.
    repartoShare(row, i) {
      if (!row || !row.activo) return 0
      return this.repartoCuotas[i] || 0
    },

    repartoShareTexto(row, i) {
      if (!row.activo) return 'fuera del reparto'

      const cuota = this.repartoShare(row, i)
      if (!cuota) return 'no recibe leads'

      // Con horario restringido son dos números distintos y los dos importan:
      // cuánto se lleva mientras trabaja (el que se ajusta subiendo o bajando su
      // número) y cuánto termina siendo eso sobre la semana entera.
      if (this.horarioTieneDias(row)) {
        return `${this.pctReparto(this.repartoCuotasFranja[i] || 0)} de los leads de su franja`
          + ` · ≈ ${this.pctReparto(cuota)} de la semana`
      }
      return `≈ ${this.pctReparto(cuota)} de los leads`
    },

    // Un número suelto con coma decimal y sin ceros de relleno: 999,99 / 0,01 / 12
    numeroEs(valor) {
      const v = Number(valor) || 0
      return String(Math.round(v * 100) / 100).replace('.', ',')
    },

    // % del reparto, redondeado para leerse de un vistazo: '33%', '2,5%'.
    pctReparto(valor) {
      const v = Number(valor) || 0
      return (v >= 10 ? String(Math.round(v)) : v.toFixed(1).replace('.', ',')) + '%'
    },

    // Explicación larga, al pasar el mouse por la barra.
    repartoShareTitle(row, i) {
      if (!row.activo) return 'Está fuera del reparto: no recibe ningún lead.'
      if (!this.horarioTieneDias(row)) {
        return 'Sin restricción: cubre las horas que no reservó nadie. En las horas en que hay '
          + 'alguien con horario propio, ese lead es del otro y esta fila no compite. '
          + 'Este número ya cuenta con eso.'
      }
      return 'Tiene turno propio: en su horario, los que están sin restricción (24/7) no compiten, '
        + 'así que se lleva TODO lo que entra en su franja salvo que otro con horario propio se le '
        + 'superponga. El primero es eso; el segundo es lo mismo medido sobre la semana entera, y da '
        + 'menos porque los días que no trabaja no participa (suponiendo que los leads entran parejo '
        + 'a toda hora). La barra dibuja el segundo. '
        + `Con este horario no puede pasar de ${this.pctReparto(this.repartoTechos[i] || 0)} de la `
        + 'semana por más % que se le cargue: ese techo lo pone la cantidad de horas, no el número.'
    },

    // Días marcados a los que les falta una de las dos horas.
    horarioIncompleto(row) {
      return ((row && row.horarioDias) || []).some(
        d => d.on && (!this.normalizarHora(d.desde) || !this.normalizarHora(d.hasta))
      )
    },

    // 'H:MM' o 'HH:MM:SS' -> 'HH:MM'. Devuelve '' si no se entiende.
    normalizarHora(valor) {
      const m = String(valor == null ? '' : valor).trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
      if (!m) return ''
      const h = parseInt(m[1], 10)
      const i = parseInt(m[2], 10)
      if (h > 23 || i > 59) return ''
      return `${String(h).padStart(2, '0')}:${String(i).padStart(2, '0')}`
    },

    minutosDeHora(valor) {
      const hora = this.normalizarHora(valor)
      if (!hora) return null
      const [h, i] = hora.split(':')
      return parseInt(h, 10) * 60 + parseInt(i, 10)
    },

    horaDeMinutos(min) {
      const m = ((min % 1440) + 1440) % 1440
      return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
    },

    // Un turno que termina antes de empezar cruza la medianoche (20:00 → 02:00).
    cruzaMedianoche(d) {
      const desde = this.minutosDeHora(d.desde)
      const hasta = this.minutosDeHora(d.hasta)
      return desde !== null && hasta !== null && desde > hasta
    },

    // ¿El mapa cubre ese minuto de ese día? Misma convención que
    // LeadRepartoService::horarioCubre en el backend: desde == hasta es el día
    // completo, y un turno que cruza la medianoche sigue cubriendo la madrugada
    // del día siguiente.
    mapaCubre(mapa, dia, minutos) {
      if (!mapa) return true

      const tramo = mapa[String(dia)]
      if (tramo) {
        const desde = this.minutosDeHora(tramo.desde)
        const hasta = this.minutosDeHora(tramo.hasta)
        if (desde !== null && hasta !== null) {
          if (desde === hasta) return true
          if (desde < hasta) {
            if (minutos >= desde && minutos < hasta) return true
          } else if (minutos >= desde) {
            return true
          }
        }
      }

      const ayer = dia === 1 ? 7 : dia - 1
      const tramoAyer = mapa[String(ayer)]
      if (tramoAyer) {
        const desde = this.minutosDeHora(tramoAyer.desde)
        const hasta = this.minutosDeHora(tramoAyer.hasta)
        if (desde !== null && hasta !== null && desde > hasta && minutos < hasta) return true
      }

      return false
    },

    // Slots sueltos -> tramos legibles: [0,30,60, 1140] -> '00:00–01:30 · 19:00–19:30'
    tramosDeSlots(slots) {
      const tramos = []
      let ini = null
      let prev = null

      for (const t of slots) {
        if (ini === null) {
          ini = t
        } else if (t !== prev + COBERTURA_PASO_MIN) {
          tramos.push([ini, prev + COBERTURA_PASO_MIN])
          ini = t
        }
        prev = t
      }
      if (ini !== null) tramos.push([ini, prev + COBERTURA_PASO_MIN])

      return tramos
        .map(([a, b]) => `${this.horaDeMinutos(a)}–${b >= 1440 ? '24:00' : this.horaDeMinutos(b)}`)
        .join(' · ')
    },

    // Resumen de una línea: 'L–V 09:00–19:00 · S 10:00–14:00'. Los días
    // consecutivos con la misma franja se agrupan para que se lea de un vistazo.
    horarioResumen(row) {
      const mapa = this.horarioAMapa(row)
      if (!mapa) return 'Sin restricción · recibe a cualquier hora'

      const grupos = []
      for (const d of DIAS_REPARTO) {
        const tramo = mapa[String(d.dia)]
        if (!tramo) continue

        const franja = tramo.desde === tramo.hasta
          ? '24 h'
          : `${tramo.desde}–${tramo.hasta}`
        const ultimo = grupos[grupos.length - 1]

        if (ultimo && ultimo.franja === franja && ultimo.hastaDia === d.dia - 1) {
          ultimo.hastaDia = d.dia
          ultimo.hastaCorto = d.corto
        } else {
          grupos.push({
            franja,
            desdeCorto: d.corto,
            hastaCorto: d.corto,
            hastaDia: d.dia,
          })
        }
      }

      return grupos
        .map(g => {
          const dias = g.desdeCorto === g.hastaCorto
            ? g.desdeCorto
            : `${g.desdeCorto}–${g.hastaCorto}`
          return `${dias} ${g.franja}`
        })
        .join(' · ')
    },

    presetHorario(row, tipo) {
      // 'full' no toca qué días están prendidos: les pone el día entero. Es lo
      // que hace falta para un broker que atiende un solo día pero las 24 h
      // (00:00 → 00:00 es la forma de escribir el día completo).
      if (tipo === 'full') {
        for (const d of row.horarioDias) {
          if (!d.on) continue
          d.desde = '00:00'
          d.hasta = '00:00'
        }
        return
      }

      for (const d of row.horarioDias) {
        if (tipo === 'libre') {
          d.on = false
          continue
        }
        d.on = tipo === 'todos' ? true : d.dia <= 5
        if (d.on) {
          d.desde = HORARIO_DEFAULT.desde
          d.hasta = HORARIO_DEFAULT.hasta
        }
      }
    },

    // Toma las horas del primer día marcado y las repite en los demás días
    // marcados. No cambia qué días están activos.
    copiarHorarioAlResto(row) {
      const base = row.horarioDias.find(d => d.on)
      if (!base) return
      for (const d of row.horarioDias) {
        if (!d.on || d.dia === base.dia) continue
        d.desde = base.desde
        d.hasta = base.hasta
      }
    },

    // Fila lista para el backend: sin el estado que es solo de la pantalla.
    repartoRowPayload(r) {
      return {
        nombres: String(r.nombres || '').trim().toLowerCase(),
        correo: String(r.correo || '').trim().toLowerCase(),
        porcentaje: parseFloat(r.porcentaje) || 0,
        horario: this.horarioAMapa(r),
        activo: !!r.activo,
      }
    },

    async saveReparto() {
      if (!this.repartoCodigo || this.savingReparto || !this.repartoValido) return
      this.savingReparto = true
      this.repartoFeedback = null

      const payload = {
        codigo_ads: String(this.repartoCodigo).trim().toLowerCase(),
        rows: this.repartoRows.map(r => this.repartoRowPayload(r)),
      }

      try {
        const res = await axios.put(`${API_BASE}dashboard/lead-reparto`, payload)
        // El backend avisa si guardó el reparto pero no los horarios (la base
        // todavía no tiene la columna). Ese aviso se deja fijo en pantalla.
        const aviso = res.data && res.data.aviso
        this.repartoFeedback = aviso
          ? { type: 'err', msg: `⚠ Reparto guardado, pero ${aviso}` }
          : { type: 'ok', msg: '✓ Reparto guardado' }
        await this.fetchReparto()
        if (!aviso) setTimeout(() => { this.repartoFeedback = null }, 3000)
      } catch (e) {
        console.error('[Dashboard] saveReparto:', e)
        this.repartoFeedback = { type: 'err', msg: `✕ ${this.errorMessageOf(e, 'Error al guardar el reparto')}` }
      } finally {
        this.savingReparto = false
      }
    },

    // ── Duplicar el reparto en otras campañas ──
    openDupReparto() {
      if (!this.puedeDuplicarReparto) return
      this.dupReparto = {
        open: true,
        step: 'confirm',
        seleccion: [],
        saving: false,
        hechos: 0,
        errores: [],
      }
    },

    closeDupReparto() {
      if (this.dupReparto.saving) return
      this.dupReparto.open = false
    },

    // "Sí, quiero duplicar" → pasa a tildar las campañas destino.
    irAElegirCampanas() {
      this.dupReparto.step = 'pick'
      this.dupReparto.errores = []
    },

    toggleDupTodas() {
      this.dupReparto.seleccion = this.dupTodasSeleccionadas
        ? []
        : this.dupCodigoOptions.map(o => o.value)
    },

    // Aplica la misma configuración (brokers y %) a cada campaña tildada, más
    // la que se está editando: al terminar, todas quedan iguales.
    async aplicarDuplicado() {
      if (this.dupReparto.saving) return
      const destinos = this.dupReparto.seleccion.slice()
      if (!destinos.length) return

      const rows = this.dupFilas
      const actual = String(this.repartoCodigo || '').trim().toLowerCase()
      const codigos = [actual].concat(destinos.filter(c => c !== actual))

      this.dupReparto.saving = true
      this.dupReparto.hechos = 0
      this.dupReparto.errores = []

      // Secuencial a propósito: son pocas campañas y así, si alguna falla, se
      // sabe exactamente cuál quedó sin aplicar.
      for (const codigo of codigos) {
        try {
          await axios.put(`${API_BASE}dashboard/lead-reparto`, { codigo_ads: codigo, rows })
          this.dupReparto.hechos++
        } catch (e) {
          console.error('[Dashboard] aplicarDuplicado:', codigo, e)
          this.dupReparto.errores.push({
            codigo,
            msg: this.errorMessageOf(e, 'Error al guardar'),
          })
        }
      }

      this.dupReparto.saving = false

      if (this.dupReparto.errores.length) {
        // El panel queda abierto con el detalle de lo que falló, para reintentar
        // (el endpoint reemplaza el reparto entero, así que repetir es seguro).
        // No se recarga desde el server: cargarFilasDelCodigo() pisaría las filas
        // en pantalla y se perderían las ediciones que todavía no se guardaron.
        this.repartoFeedback = {
          type: 'err',
          msg: `✕ ${this.dupReparto.hechos} de ${codigos.length} campañas actualizadas.`,
        }
        return
      }

      await this.fetchReparto()
      this.dupReparto.open = false
      this.repartoFeedback = {
        type: 'ok',
        msg: `✓ Configuración aplicada a ${codigos.length} campaña${codigos.length !== 1 ? 's' : ''}`,
      }
      setTimeout(() => { this.repartoFeedback = null }, 4000)
    },

    async deleteReparto() {
      const codigo = String(this.repartoCodigo || '').trim().toLowerCase()
      if (!codigo || this.savingReparto) return
      if (!confirm(`¿Borrar el reparto de "${codigo}"? Los leads de esa campaña van a quedar sin asignar.`)) return

      this.savingReparto = true
      this.repartoFeedback = null
      try {
        await axios.delete(`${API_BASE}dashboard/lead-reparto/${encodeURIComponent(codigo)}`)
        this.repartoRows = []
        this.repartoFeedback = { type: 'ok', msg: '✓ Reparto borrado' }
        await this.fetchReparto()
      } catch (e) {
        console.error('[Dashboard] deleteReparto:', e)
        this.repartoFeedback = { type: 'err', msg: `✕ ${this.errorMessageOf(e, 'Error al borrar el reparto')}` }
      } finally {
        this.savingReparto = false
      }
    },

    leadTier(intake)     { return intake.lead_score && intake.lead_score.lead_tier ? intake.lead_score.lead_tier : '' },
    leadScoreVal(intake) {
      return intake.lead_score && intake.lead_score.lead_score_total !== null && intake.lead_score.lead_score_total !== undefined
        ? intake.lead_score.lead_score_total
        : '—'
    },
    scoreArc(intake) {
      if (!intake.lead_score) return 0
      return Math.round((intake.lead_score.lead_score_total / 100) * 100)
    },
    tierColor(tier) {
      return { hot: '#ff5e5e', warm: '#ffb347', cool: '#94a3b8', cold: '#3b82f6' }[tier] || '#d1d1ce'
    },
    tierEmoji(tier) {
      return { hot: '🔥', warm: '⚡', cool: '🔎', cold: '❄️' }[tier] || ''
    },
    tierEmojiShortlist(tier) {
      return { barata: '🟢', media: '🏆', premium: '⭐' }[tier] || ''
    },

    getVisibleShortlists(intake) {
      return (intake.short_list || []).filter(item => item.estado !== 'descartada')
    },

    getSendableShortlists(intake) {
      return (intake.short_list || []).filter(item => item.estado !== 'reservada' && item.estado !== 'descartada')
    },

    hasSendableShortlists(intake) {
      return this.getSendableShortlists(intake).length > 0
    },

    isSendButtonVisible(shortlist, index) {
      return Array.isArray(shortlist) && shortlist.length > 0 && index === shortlist.length - 1
    },

    focusShortlistCard(event, intakeId) {
      const card = event.currentTarget
      const ref = this.$refs[`shortlist-scroll-${intakeId}`]
      const container = Array.isArray(ref) ? ref[0] : ref

      if (!card || !container || container.scrollWidth <= container.clientWidth) return

      const targetLeft = card.offsetLeft - ((container.clientWidth - card.clientWidth) / 2)
      const maxScrollLeft = container.scrollWidth - container.clientWidth

      container.scrollTo({
        left: Math.max(0, Math.min(targetLeft, maxScrollLeft)),
        behavior: 'smooth',
      })
    },

    scoreBreakdown(intake) {
      if (!intake.lead_score) return []
      const ls = intake.lead_score
      return [
        { icon: '🏙️', label: 'Ya en ciudad',       val: ls.pts_ciudad !== null && ls.pts_ciudad !== undefined ? ls.pts_ciudad : 0, max: 60 },
        { icon: '✈️', label: 'Pasajes confirmados', val: ls.pts_pasajes !== null && ls.pts_pasajes !== undefined ? ls.pts_pasajes : 0, max: 30 },
        { icon: '⏰', label: 'Urgencia',       val: ls.pts_urgencia !== null && ls.pts_urgencia !== undefined ? ls.pts_urgencia : 0, max: 30 },
        { icon: '🤝',  label: 'Referido',     val: ls.pts_referido !== null && ls.pts_referido !== undefined ? ls.pts_referido : 0, max: 10 },
      ].map(i => ({ ...i, pct: Math.round((i.val / i.max) * 100) + '%' }))
    },

    accommodationUrl(id, shortListId = null) {
      if (id === null || id === undefined || id === '') return ''
      const baseEnv = typeof process !== 'undefined' && process.env && process.env.VUE_APP_SITE_URL
        ? String(process.env.VUE_APP_SITE_URL).replace(/\/$/, '')
        : ''
      const origin = typeof window !== 'undefined' && window.location && window.location.origin
        ? window.location.origin
        : ''
      const routerBase = this.$router && this.$router.options && this.$router.options.base
        ? origin + String(this.$router.options.base).replace(/\/$/, '')
        : ''
      const base = baseEnv || routerBase || origin
      const urlBase = `${String(base).replace(/\/$/, '')}/alojamiento/${id}`
      return shortListId ? `${urlBase}?estado=${encodeURIComponent(shortListId)}` : urlBase
    },

    formatDate(d) {
      if (!d) return '—'
      return new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatDateTime(d) {
      if (!d) return '—'
      return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    },
    // Solo la hora: en la tabla va debajo de la fecha, así la columna Registro
    // necesita la mitad de ancho y la fecha se lee sola.
    formatTime(d) {
      if (!d) return ''
      return new Date(d).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })
    },

    // ¿Va la tercera línea de la celda Lead? Sin esto queda un div vacío con su
    // margen cuando el lead no tiene ni teléfono ni origen ni avisos.
    tieneLineaContacto(intake) {
      if (!intake) return false
      return !!(intake.whatsapp || this.leadOrigen(intake) ||
        this.hasIncompletePhone(intake) || this.awaitingPhoneByEmail(intake))
    },

    // "Chile · 78 años" para la línea de abajo del nombre.
    leadOrigen(intake) {
      if (!intake) return ''
      const partes = []
      if (intake.pais_origen) partes.push(String(intake.pais_origen))
      if (intake.edad) partes.push(`${intake.edad} años`)
      return partes.join(' · ')
    },

    // Zonas preferidas para la celda de destino. Devuelve '' cuando no hay, así
    // el template no pinta la línea vacía. El recorte lo hace el CSS (.cell-clip)
    // y no un truncate() a una cantidad fija de caracteres: así se aprovecha
    // todo el ancho disponible y el valor entero queda en el tooltip.
    zonasTexto(intake) {
      const val = this.formatArray(intake ? intake.zonas_preferidas : null)
      return val === '—' ? '' : val
    },

    // "depto_2d · 4 personas": el tipo de alojamiento y cuántos van.
    requerimientoTexto(intake) {
      if (!intake) return '—'
      const partes = []
      if (intake.tipo_alojamiento) partes.push(String(intake.tipo_alojamiento))
      if (intake.cantidad_personas) {
        partes.push(`${intake.cantidad_personas} persona${intake.cantidad_personas > 1 ? 's' : ''}`)
      }
      return partes.length ? partes.join(' · ') : '—'
    },

    // Separadores de miles. Hay intakes con presupuestos absurdos cargados desde
    // la web (notación científica incluida): en ese caso se muestra el valor tal
    // cual y el CSS lo recorta, en vez de generar un número de 80 dígitos.
    formatMoney(val) {
      const num = Number(val)
      if (!isFinite(num) || Math.abs(num) >= 1e12) return String(val)
      return num.toLocaleString('es-AR', { maximumFractionDigits: 0 })
    },

    presupuestoTitle(intake) {
      if (!intake || !intake.presupuesto_usd) return 'Sin presupuesto cargado'
      return `Presupuesto: USD ${intake.presupuesto_usd}`
    },

    // El encabezado de la tabla queda fijo al desplazarse, pegado al borde de
    // abajo del header del sitio (que es sticky y no tiene un alto fijo: cambia
    // con el breakpoint). Se mide en vez de hardcodear los 60px.
    syncStickyOffset() {
      if (!this.$el || !this.$el.style) return
      const header = document.querySelector('.app-header')
      const alto = header ? Math.round(header.getBoundingClientRect().height) : 0
      this.$el.style.setProperty('--sticky-top', `${alto}px`)
    },
    formatArray(val) {
      if (!val) return '—'
      if (Array.isArray(val)) return val.join(', ') || '—'
      return val || '—'
    },
    truncate(text, max = 40) {
      if (!text) return '—'
      return text.length > max ? text.slice(0, max) + '…' : text
    },

    // Exporta TODOS los intakes que cumplen los filtros puestos, no solo la
    // página visible: se recorren las páginas en el momento (fetchAllIntakes).
    async exportToExcel() {
      if (this.bulkLoading) return

      let intakes = []
      try {
        intakes = await this.fetchAllIntakes('Preparando el Excel')
      } catch (e) {
        console.error('[Dashboard] exportToExcel:', e)
        this.bulkError = 'No se pudo preparar el Excel. Probá de nuevo.'
        return
      }
      if (!intakes.length) return

      const rows = intakes.map(i => ({
        'ID':                  i.id,
        'Nombre':              i.nombre || '',
        'Email':               i.email || '',
        'WhatsApp':            i.whatsapp || '',
        'País origen':         i.pais_origen || '',
        'Edad':                i.edad || '',
        'Ciudad destino':      i.ciudad_destino || '',
        'Zonas preferidas':    this.formatArray(i.zonas_preferidas),
        'Zonas cercanas':      this.formatArray(i.zonas_cercanas),
        'Fecha inicio':        i.fecha_inicio ? this.formatDate(i.fecha_inicio) : '',
        'Fecha fin':           i.fecha_fin ? this.formatDate(i.fecha_fin) : '',
        'Presupuesto USD':     i.presupuesto_usd || '',
        'Tipo alojamiento':    i.tipo_alojamiento || '',
        'Cantidad personas':   i.cantidad_personas || '',
        'Urgencia':            i.urgencia || '',
        'Universidad origen':  i.universidad || '',
        'Universidad destino': i.universidad_destino || '',
        'Tipo programa':       i.tipo_programa || '',
        'Va en pareja':        i.va_en_pareja === true ? 'Sí' : i.va_en_pareja === false ? 'No' : '',
        'Partner':             i.partner_nombre || '',
        'Mensaje huésped':     i.comentario_libre || '',
        'Lead Score':          this.leadScoreVal(i),
        'Lead Tier':           this.leadTier(i),
        'Estado intake':       i.estado_intake || '',
        'Admin intake':        i.admin_intake || '',
        'Anotación admin':     i.intake_comentario || '',
        'Meta':                i.meta || '',
        'Registrado':          i.created_at ? this.formatDateTime(i.created_at) : '',
      }))

      const ws = XLSX.utils.json_to_sheet(rows)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Intakes')

      const today = new Date().toISOString().slice(0, 10)
      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), `intakes-${today}.xlsx`)
    },

    closeStats() {
      this.showStats = false
    },

    // Las estadísticas se calculan en la API (dashboard/intakes/estadisticas):
    // ya no se bajan todos los intakes. Se abren con los filtros del dashboard
    // que tienen equivalente (admin, código, fechas).
    openStats() {
      this.statsFiltros = this.filterParams()
      this.showStats = true
    },

    openGenerateModal() {
      this.showGenerateModal = true
      this.customMeta = null
      this.metaParamName = 'meta'
      this.generatedLink = null
      this.generatedMeta = null
      this.generateFeedback = null
    },

    closeGenerateModal() {
      this.showGenerateModal = false
      this.customMeta = null
      this.metaParamName = 'meta'
      this.generatedLink = null
      this.generatedMeta = null
      this.generateFeedback = null
      this.generating = false
    },

    randomMetaCode() {
      return Math.random().toString(36).substring(2, 8).toUpperCase()
    },

    async generateMeta() {
      this.generating = true
      this.generateFeedback = null
      try {
        const code = this.customMeta && String(this.customMeta).trim() ? String(this.customMeta).trim() : this.randomMetaCode()
        const paramName = this.metaParamName && String(this.metaParamName).trim() ? String(this.metaParamName).trim() : 'meta'
        const baseEnv = process.env.VUE_APP_SITE_URL || (typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : '')
        const base = String(baseEnv).replace(/\/$/, '')
        // Build combined meta as "variable-code" (stored in DB), but emit link as ?variable=code
        const combined = `${paramName}-${code}`
        const link = `${base}/formulario?${encodeURIComponent(paramName)}=${encodeURIComponent(code)}&page=input`
        this.generatedMeta = combined
        this.generatedLink = link
        try {
          await navigator.clipboard.writeText(link)
          this.generateFeedback = 'Link copiado al portapapeles'
        } catch (e) {
          this.generateFeedback = 'Link generado (no se pudo copiar automáticamente)'
        }
      } finally {
        this.generating = false
      }
    },

    async copyGeneratedLink() {
      if (!this.generatedLink) return
      try {
        await navigator.clipboard.writeText(this.generatedLink)
        this.generateFeedback = 'Copiado al portapapeles'
      } catch (e) {
        this.generateFeedback = 'Error al copiar'
      }
    },
    // codigo_ads del intake. El backend lo expone como `meta` (accessor
    // appendeado), así que toleramos ambos nombres.
    codigoAdsOf(intake) {
      const raw = intake && (intake.codigo_ads != null ? intake.codigo_ads : intake.meta)
      return raw == null ? '' : String(raw).trim()
    },

    parseMetaParts(raw) {
      if (!raw && raw !== 0) return { variable: null, code: null, combined: null }
      const s = String(raw)
      const idx = s.indexOf('-')
      if (idx === -1) return { variable: 'meta', code: s, combined: s }
      return { variable: s.slice(0, idx), code: s.slice(idx + 1), combined: s }
    },
  },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');

.dash-root {
  --orange: #FC7010;
  --orange-bright: #FF8533;
  --orange-soft: #FFF1E5;
  --orange-line: #FFD4AE;
  --orange-text: #B84800;
  --magenta: #B91E7C;
  --magenta-bright: #D62E96;
  --magenta-soft: #FCEAF4;
  --magenta-line: #F5BDD9;
  --magenta-text: #8C1660;
  --bg: #FFFFFF;
  --bg-subtle: #FAFAF9;
  --bg-muted: #F4F3F0;
  --border: #ECEAE4;
  --border-strong: #DBD9D2;
  --text: #0A0A0B;
  --text-mid: #383840;
  --text-dim: #6E6E76;
  --text-faint: #A0A0A8;
  /* Se usa en los textos de ayuda y en los placeholders de los modales. Faltaba
     definirla, así que esos textos venían heredando el negro del texto normal y
     no se distinguían de lo principal. */
  --muted: #6E6E76;
  --danger: #e53935;
  --ok: #16a34a;
  --hot: #ff5e5e;
  --warm: #ffb347;
  --cool: #94a3b8;
  --cold: #3b82f6;
  --radius: 12px;

  font-family: 'Geist', sans-serif;
  background: transparent; /* fondo de marca del sitio (HyFFondo) */
  color: var(--text);
  min-height: 100vh;
  padding: 28px 32px 80px;
  font-size: 13px;
}

/* ── Utilities ── */
.text-xs   { font-size: 12px; }
.text-muted{ color: var(--muted); }
.mt1       { margin-top: 2px; }

/* ── Header ── */
.dash-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 20px;
}
.dash-header-left { display: flex; align-items: center; gap: 14px; }
.dash-logo {
  width: 42px; height: 42px; border-radius: 999px;
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%); color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 600; flex-shrink: 0; font-family: 'Geist', sans-serif;
}
h1 { font-size: 20px; font-weight: 600; letter-spacing: -.03em; margin: 0 0 2px; line-height: 1.1; font-family: 'Geist', sans-serif; }
.dash-count {
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%); color: #fff; border-radius: 999px;
  font-size: 11px; font-weight: 500; padding: 2px 10px;
  margin-left: 6px; vertical-align: middle;
}
.dash-sub { font-size: 12px; color: var(--text-dim); margin: 0; }

/* Contador de la carga en segundo plano (el resto de las páginas) */
.dash-loading-more {
  display: inline-flex; align-items: center; gap: 6px;
  margin-left: 8px; padding: 2px 9px;
  border: 0.5px solid var(--border); border-radius: 999px;
  background: var(--bg-subtle); color: var(--text-dim);
  font-size: 10px; font-weight: 500; vertical-align: middle;
  font-family: 'Geist Mono', monospace;
}
/* Falló el recorrido de páginas del Exportar / Estadísticas. Se avisa acá y no
   en el banner de error, que reemplaza la tabla entera. */
.dash-bulk-error { color: #b91c1c; border-color: #fca5a5; background: #fef2f2; }
.dash-loading-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: var(--orange); flex-shrink: 0;
  animation: pulseDot 1s ease-in-out infinite;
}
@keyframes pulseDot {
  0%, 100% { opacity: 1; }
  50%      { opacity: .25; }
}

.header-actions { display: flex; align-items: center; gap: 8px; }

.btn-icon {
  width: 34px; height: 34px; border: 0.5px solid var(--border);
  border-radius: 10px; background: var(--bg);
  color: var(--text-dim); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: all .15s;
}
.btn-icon:hover { border-color: var(--orange); color: var(--orange); box-shadow: 0 2px 8px rgba(252, 112, 16, 0.12); }
.btn-icon.spinning svg { animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

.btn-export {
  height: 34px; padding: 0 14px; border: 0.5px solid var(--border);
  border-radius: 999px; background: var(--bg);
  color: var(--text); cursor: pointer; font-family: 'Geist', sans-serif;
  font-size: 13px; font-weight: 500;
  display: flex; align-items: center; gap: 6px;
  transition: all .15s;
}
.btn-export:hover:not(:disabled) { border-color: var(--ok); color: var(--ok); box-shadow: 0 2px 8px rgba(22, 163, 74, 0.12); }
.btn-export:disabled { opacity: .4; cursor: not-allowed; }

.btn-generate {
  height: 34px; padding: 0 14px; border: 0.5px solid var(--border);
  border-radius: 999px; background: var(--bg);
  color: var(--text); cursor: pointer; font-family: 'Geist', sans-serif;
  font-size: 13px; font-weight: 500;
  display: flex; align-items: center; gap: 6px;
  transition: all .15s;
}
.btn-generate:hover:not(:disabled) { border-color: var(--orange); color: var(--orange); box-shadow: 0 2px 8px rgba(252, 112, 16, 0.12); }
.btn-generate:disabled { opacity: .4; cursor: not-allowed; }

.btn-delete {
  width: 34px; height: 34px; border: 0.5px solid var(--border);
  border-radius: 10px; background: var(--bg);
  color: var(--danger); cursor: pointer; display: inline-flex;
  align-items: center; justify-content: center; margin-right: 6px;
  transition: all .12s;
}
.btn-delete:hover:not(:disabled) { border-color: var(--danger); background: rgba(229, 57, 53, 0.04); }
.btn-delete:disabled { opacity: .5; cursor: not-allowed; }

/* Botón que lleva al chat del lead en Mensajería */
.btn-chat {
  width: 34px; height: 34px; border: 0.5px solid var(--border);
  border-radius: 10px; background: var(--bg);
  color: var(--ok); cursor: pointer; display: inline-flex;
  align-items: center; justify-content: center; margin-right: 6px;
  transition: all .12s;
}
.btn-chat:hover:not(:disabled) { border-color: var(--ok); background: rgba(22, 163, 74, 0.06); }
.btn-chat:disabled { opacity: .5; cursor: not-allowed; }

/* Copiar link del intake para compartirlo */
.btn-share {
  width: 34px; height: 34px; border: 0.5px solid var(--border);
  border-radius: 10px; background: var(--bg);
  color: #2563EB; cursor: pointer; display: inline-flex;
  align-items: center; justify-content: center; margin-right: 6px;
  transition: all .12s;
}
.btn-share:hover { border-color: #2563EB; background: rgba(37, 99, 235, 0.06); }
.btn-share.is-ok { color: var(--ok); border-color: var(--ok); background: rgba(22, 163, 74, 0.06); }
.btn-share:focus-visible { outline: 2px solid #2563EB; outline-offset: 2px; }

.btn-edit {
  width: 34px; height: 34px; border: 0.5px solid var(--border);
  border-radius: 10px; background: var(--bg);
  color: var(--orange); cursor: pointer; display: inline-flex;
  align-items: center; justify-content: center; margin-right: 6px;
  transition: all .12s;
}
.btn-edit:hover:not(:disabled) { border-color: var(--orange); background: rgba(252, 112, 16, 0.04); }
.btn-edit:disabled { opacity: .5; cursor: not-allowed; }

.btn-expand {
  width: 26px; height: 26px; background: none;
  border: 0.5px solid var(--border); border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--text-dim); transition: all .15s;
}
.btn-expand:hover { border-color: var(--orange); color: var(--orange); }

.meta-pill {
  display: inline-block; margin-left: 8px; padding: 2px 8px; border-radius: 8px;
  background: #f3f4f6; color: #374151; font-size: 11px; font-weight: 600;
  vertical-align: middle;
}
.meta-pill.normal { background: #fff; color: var(--muted); border: 1px solid var(--border); font-weight: 500; }

/* ── Search ── */
.search-bar { display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap; }
.search-input-wrap { flex: 1; min-width: 240px; position: relative; display: flex; align-items: center; }
.search-icon { position: absolute; left: 11px; color: var(--text-dim); pointer-events: none; }
.search-input {
  width: 100%; background: var(--bg); border: 0.5px solid var(--border);
  border-radius: 12px; padding: 9px 32px 9px 34px;
  font-family: 'Geist', sans-serif; font-size: 13px; color: var(--text);
  outline: none; transition: border-color .15s;
}
.search-input:focus { border-color: var(--orange); }
.search-input::placeholder { color: var(--text-dim); }
.search-clear {
  position: absolute; right: 9px; background: none; border: none;
  color: var(--text-dim); font-size: 16px; cursor: pointer; padding: 0 3px;
}
.search-clear:hover { color: var(--text); }
.search-filters { display: flex; gap: 8px; flex-wrap: wrap; }
.filter-select {
  background: var(--bg); border: 0.5px solid var(--border); border-radius: 999px;
  padding: 9px 30px 9px 14px; font-family: 'Geist', sans-serif;
  font-size: 12px; color: var(--text); cursor: pointer; outline: none;
  appearance: none; -webkit-appearance: none; min-width: 130px;
  transition: border-color .15s;
  /* Con appearance:none el select pierde la flecha del sistema y queda igual a
     una etiqueta: sin esto no se lee como desplegable. */
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A0A0A8' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
}
.filter-select:hover { border-color: var(--border-strong); }
.filter-select:focus { border-color: var(--orange); }

/* Filtro por fecha de registro */
.filter-date-group {
  display: inline-flex; align-items: center; gap: 6px;
  background: var(--bg); border: 0.5px solid var(--border);
  border-radius: 999px; padding: 4px 12px;
  transition: border-color .15s;
}
.filter-date-group:focus-within { border-color: var(--orange); }
.filter-date-label {
  font-size: 11px; color: var(--text-dim); font-weight: 600;
  text-transform: uppercase; letter-spacing: .04em;
}
.filter-date {
  background: transparent; border: none; outline: none;
  font-family: 'Geist', sans-serif; font-size: 12px; color: var(--text);
  cursor: pointer; padding: 3px 2px;
}
.filter-date-sep { color: var(--text-dim); font-size: 11px; }
.filter-date-clear {
  background: none; border: none; color: var(--text-dim);
  font-size: 15px; line-height: 1; cursor: pointer; padding: 0 2px;
}
.filter-date-clear:hover { color: var(--text); }

/* ── States ── */
.state-box {
  display: flex; align-items: center; gap: 12px;
  padding: 48px 0; color: var(--muted); font-size: 13px;
}
.spinner {
  width: 20px; height: 20px; border: 2px solid var(--border);
  border-top-color: var(--accent); border-radius: 50%;
  animation: spin .7s linear infinite; flex-shrink: 0;
}
.error-banner {
  display: flex; align-items: center; gap: 10px;
  background: #fff5f5; border: 1px solid #fecaca;
  border-radius: var(--radius); padding: 13px 16px;
  color: var(--danger); font-size: 13px;
}

/* ══════════════ TABLA ══════════════
   Regla de oro de esta tabla: es table-layout:fixed, así que el ancho lo manda
   el <th> y el contenido NO puede empujar la celda. Todo lo que sea texto libre
   (nombre, mail, zonas, tipo de alojamiento) va con .cell-clip, que lo recorta
   con puntos suspensivos. Sin eso el texto largo se dibuja por encima de la
   columna de al lado, que es lo que se veía en producción. */
.table-wrap {
  background: var(--bg); border: 0.5px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
  /* clip recorta igual que hidden pero NO crea un contenedor de scroll, así el
     thead sticky se sigue pegando al viewport. Donde no exista, queda el hidden
     de arriba y el encabezado simplemente no se fija. */
  overflow: clip;
  box-shadow: 0 1px 2px rgba(10, 10, 11, .03);
}
/* Cambio de página o de filtro: la tabla se queda a la vista atenuada en vez
   de desaparecer y dejar la pantalla en blanco. */
.table-wrap.is-refreshing { opacity: .5; pointer-events: none; transition: opacity .15s; }
.dash-table { width: 100%; border-collapse: collapse; font-size: 13px; table-layout: fixed; }
.dash-table thead tr { background: var(--bg); }
.dash-table th {
  padding: 11px 14px; text-align: left;
  font-size: 10px; font-weight: 600; letter-spacing: .06em;
  text-transform: uppercase; color: var(--text-faint); white-space: nowrap; font-family: 'Geist Mono', monospace;
  /* El encabezado acompaña el scroll, pegado abajo del header del sitio.
     --sticky-top lo mide syncStickyOffset() al montar y al redimensionar. */
  position: sticky; top: var(--sticky-top, 0px); z-index: 3;
  background: var(--bg);
  box-shadow: inset 0 -0.5px 0 var(--border);
}
.dash-table th strong {
  color: var(--text);
  font-weight: 700;
}
.dash-table td { padding: 12px 14px; vertical-align: middle; white-space: nowrap; }

/* Anchos de columna en porcentaje (suman 100). Así el espacio de más de una
   pantalla grande se reparte entre todas y no queda una columna gigante al lado
   de otras apretadas, que era el otro problema del ancho fijo. Abajo de 1320px
   la tabla pasa a scroll horizontal (ver el bloque responsive): con menos de
   ~1260px estos porcentajes ya no alcanzan para el contenido. */
.th-id       { width: 6%;    }
.th-lead     { width: 20.5%; }
.th-destino  { width: 11%;   }
.th-fechas   { width: 8.5%;  }
.th-req      { width: 9.5%;  }
.th-etapa    { width: 10.5%; }
.th-score    { width: 6%;    text-align: center; }
.th-admin    { width: 8.5%;  text-align: center; }
.th-registro { width: 7.5%;  }
.th-acciones { width: 12%;   }

/* Recorte con puntos suspensivos. El valor completo va siempre en un title. */
.cell-clip {
  display: block; min-width: 0; max-width: 100%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
/* Segunda línea de una celda: el dato de apoyo, más chico y apagado. */
.cell-sub { margin-top: 3px; font-size: 11.5px; color: var(--text-dim); line-height: 1.3; }

.intake-row { border-bottom: 0.5px solid var(--border); cursor: pointer; transition: background .12s; }
.intake-row:hover { background: var(--bg-subtle); }
.intake-row.expanded { background: var(--bg-subtle); }
.intake-row > td { overflow: hidden; }

/* Borde tier izq */
.intake-row.tier-row-hot  { border-left: 3px solid var(--hot);  }
.intake-row.tier-row-warm { border-left: 3px solid var(--warm); }
.intake-row.tier-row-cool { border-left: 3px solid var(--cool); }
.intake-row.tier-row-cold { border-left: 3px solid var(--cold); }

.id-badge {
  font-size: 11px; font-weight: 600; color: var(--text-faint);
  background: var(--bg-subtle); border: 0.5px solid var(--border); border-radius: 8px; padding: 2px 8px;
  font-family: 'Geist Mono', monospace; flex-shrink: 0;
}

/* ── Columna Lead: nombre, mail y teléfono apilados ── */
.td-lead { min-width: 0; }
.lead-head { display: flex; align-items: center; gap: 7px; min-width: 0; }
.lead-nombre {
  font-weight: 600; font-size: 13.5px; letter-spacing: -.01em;
  font-family: 'Geist', sans-serif; color: var(--text); line-height: 1.3;
}
.lead-mail { margin-top: 2px; font-size: 12px; color: var(--text-dim); line-height: 1.3; }
.lead-foot {
  display: flex; align-items: center; gap: 7px;
  margin-top: 3px; min-width: 0; overflow: hidden;
  font-size: 11px; color: var(--text-faint);
}
.lead-foot > span { flex-shrink: 0; }
/* Punto separador entre el teléfono y el origen. Los badges quedan afuera: el
   ::before se dibuja dentro de su pastilla y ahí el punto no va. */
.lead-foot > span + .lead-origen::before {
  content: '·'; margin-right: 7px; color: var(--border-strong);
}
.lead-phone { font-family: 'Geist Mono', monospace; letter-spacing: -.02em; }
.lead-origen { overflow: hidden; text-overflow: ellipsis; min-width: 0; flex-shrink: 1; }

/* ── Destino ── */
.td-destino { min-width: 0; }

/* ── Estadía ── */
.td-fechas { min-width: 0; }
.stay-in  { font-size: 12.5px; font-weight: 500; line-height: 1.3; }
.stay-out { margin-top: 2px; font-size: 11.5px; color: var(--text-dim); line-height: 1.3; }

/* ── Requerimiento: presupuesto arriba, tipo y personas abajo ── */
.td-req { min-width: 0; }
.req-precio {
  font-weight: 600; font-size: 13px; color: var(--orange);
  font-family: 'Geist Mono', monospace; letter-spacing: -.02em; line-height: 1.3;
}
.req-precio--vacio { color: var(--text-faint); font-weight: 500; }

/* ── Registro ── */
.td-registro { min-width: 0; }
.reg-fecha { font-size: 12px; color: var(--text-dim); line-height: 1.3; font-family: 'Geist Mono', monospace; }
.reg-hora  { margin-top: 2px; font-size: 11px; color: var(--text-faint); line-height: 1.3; font-family: 'Geist Mono', monospace; }

/* ── Acciones: editar, chat y borrar ── */
.td-acciones { text-align: right; padding-left: 6px; }
.acciones-wrap { display: flex; align-items: center; justify-content: flex-end; gap: 6px; }
/* Dentro de la fila los botones son un poco más chicos para que los tres entren
   en la columna. El margen lateral lo pone el gap del contenedor. */
.intake-row .btn-expand,
.intake-row .btn-chat,
.intake-row .btn-delete {
  width: 28px; height: 28px; flex-shrink: 0;
  margin-right: 0; border-radius: 9px;
}
.intake-row .btn-expand:hover:not(:disabled) { border-color: var(--orange); color: var(--orange); background: rgba(252, 112, 16, .04); }
.intake-row .btn-expand:disabled { opacity: .5; cursor: not-allowed; }

.badge-ciudad {
  display: inline-block; max-width: 100%; background: var(--orange-soft);
  color: var(--orange-text); border-radius: 8px; padding: 2px 8px;
  font-size: 11px; font-weight: 600; font-family: 'Geist', sans-serif;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

/* Etapa del chat en el tablero de Mensajería */
.td-etapa { min-width: 0; }
.badge-etapa {
  display: inline-block; max-width: 100%;
  padding: 3px 9px; border-radius: 999px;
  background: var(--orange-soft); border: 0.5px solid var(--orange-line);
  color: var(--orange-text); font-size: 11px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.badge-etapa--none {
  background: var(--bg-muted); border-color: var(--border);
  color: var(--text-faint); font-weight: 500;
}
/* "Contacto por email": no hay chat, pero el lead sigue en curso esperando que
   conteste el correo del número. Se distingue del gris de "Sin chat". */
.badge-etapa--email {
  background: rgba(255,94,94,.1); border-color: rgba(255,94,94,.25);
  color: var(--hot); font-weight: 600;
  cursor: help;
}

/* ID: solo la chapita. El botón de editar se fue con el resto de las acciones. */
.td-id { white-space: nowrap; padding-right: 6px; }

.badge-urgencia {
  display: inline-block; border-radius: 8px;
  padding: 3px 8px; font-size: 11px; font-weight: 600; font-family: 'Geist Mono', monospace;
}
.badge-urgencia.urgente    { background: rgba(255,94,94,.1);   color: var(--hot); }
.badge-urgencia.pronto     { background: rgba(255,179,71,.12); color: var(--warm); }
.badge-urgencia.explorando { background: rgba(148,163,184,.1); color: var(--cool); }

/* Cuántos intakes tiene esa misma persona. Va al lado del nombre. */
.badge-group-count {
  display: inline-block; flex-shrink: 0; background: var(--magenta-soft);
  color: var(--magenta-text); border-radius: 8px; padding: 2px 8px;
  font-size: 10px; font-weight: 600; font-family: 'Geist Mono', monospace;
  cursor: help;
}

/* Lead cuyo WhatsApp llegó incompleto: se le pidió el número por correo. */
.badge-phone-bad {
  display: inline-block; background: rgba(255,94,94,.1);
  color: var(--hot); border-radius: 8px; padding: 1px 7px;
  font-size: 10px; font-weight: 600; font-family: 'Geist Mono', monospace;
  white-space: nowrap; cursor: help;
}

/* Se le pidió el número por correo y se espera la respuesta. En la celda va
   solo la fecha; el estado completo se lee en el tooltip (title). */
.badge-phone-mailed {
  display: inline-block; max-width: 100%;
  background: rgba(255,94,94,.1); border: 0.5px solid rgba(255,94,94,.22);
  color: var(--hot); border-radius: 8px; padding: 1px 7px;
  font-size: 10px; font-weight: 600; font-family: 'Geist Mono', monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  cursor: help;
}

/* ── Score ring ── */
.td-score { text-align: center; }
.score-wrap { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.score-ring-wrap { position: relative; width: 38px; height: 38px; }
.score-svg { width: 38px; height: 38px; transform: rotate(-90deg); }
.score-bg-circle {
  fill: none; stroke: var(--border); stroke-width: 2.8;
}
.score-fill-circle {
  fill: none; stroke-width: 2.8; stroke-linecap: round;
  stroke-dashoffset: 0;
  transition: stroke-dasharray .5s ease;
}
.score-num {
  position: absolute; inset: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 700;
}
.score-tier-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .04em; }
.score-tier-label.hot  { color: var(--hot);  }
.score-tier-label.warm { color: var(--warm); }
.score-tier-label.cool { color: var(--cool); }
.score-tier-label.cold { color: var(--cold); }

/* ── Admin badge en tabla ── */
.td-admin { text-align: center; }
.admin-badge {
  display: inline-block;
  padding: 3px 8px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 600;
  background: rgba(148,163,184,.1);
  color: var(--cool);
}
.admin-badge.filled {
  background: rgba(255,122,0,.1);
  color: var(--accent);
}

/* Select para cambiar el admin desde la tabla */
.admin-select {
  width: 100%;
  max-width: 100%;
  padding: 5px 8px;
  border-radius: 8px;
  border: 0.5px solid var(--border);
  font-family: 'Geist', sans-serif;
  font-size: 11px;
  font-weight: 600;
  background: rgba(148,163,184,.1);
  color: var(--cool);
  cursor: pointer;
  outline: none;
  transition: border-color .15s, background .15s, color .15s;
}
.admin-select.filled {
  background: rgba(255,122,0,.1);
  color: var(--accent);
  border-color: rgba(255,122,0,.35);
}
.admin-select:hover { border-color: var(--accent); }
.admin-select:focus { border-color: var(--accent); box-shadow: 0 0 0 2px rgba(255,122,0,.15); }

/* Equivalente táctil del select de admin: solo se muestra en celular, donde el
   <select> nativo se abre como un diálogo a pantalla completa del sistema. */
.admin-picker-btn { display: none; }

/* ── Panel inferior (bottom sheet) ── */
.sheet-overlay {
  position: fixed; inset: 0; z-index: 2020;
  background: rgba(0, 0, 0, .4);
  display: flex; align-items: flex-end; justify-content: center;
}
.sheet {
  width: 100%; max-width: 520px;
  background: var(--bg);
  border-radius: 20px 20px 0 0;
  padding: 6px 12px 12px;
  padding-bottom: calc(12px + env(safe-area-inset-bottom, 0px));
  max-height: 76vh; max-height: 76dvh;
  display: flex; flex-direction: column;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, .18);
}
.sheet::before {
  content: ''; display: block;
  width: 38px; height: 4px; margin: 6px auto 10px;
  border-radius: 999px; background: var(--border-strong);
}
.sheet-head { padding: 0 6px 10px; display: flex; align-items: baseline; gap: 8px; }
.sheet-title { font-size: 14px; font-weight: 600; color: var(--text); font-family: 'Geist', sans-serif; }
.sheet-sub { font-size: 11px; color: var(--text-dim); font-family: 'Geist Mono', monospace; }
.sheet-list { overflow-y: auto; display: flex; flex-direction: column; gap: 6px; }
.sheet-opt {
  width: 100%; text-align: left;
  padding: 13px 14px; min-height: 46px;
  border: 0.5px solid var(--border); border-radius: 12px;
  background: var(--bg); color: var(--text);
  font-family: 'Geist', sans-serif; font-size: 14px; cursor: pointer;
}
.sheet-opt.active {
  border-color: var(--orange); background: var(--orange-soft); color: var(--orange-text);
  font-weight: 600;
}
.sheet-cancel {
  margin-top: 10px; width: 100%; min-height: 46px;
  border: 0.5px solid var(--border); border-radius: 12px;
  background: var(--bg-subtle); color: var(--text-dim);
  font-family: 'Geist', sans-serif; font-size: 14px; font-weight: 500; cursor: pointer;
}
.sheet-fade-enter-active, .sheet-fade-leave-active { transition: opacity .18s ease; }
.sheet-fade-enter-active .sheet, .sheet-fade-leave-active .sheet { transition: transform .18s ease; }
.sheet-fade-enter, .sheet-fade-leave-to { opacity: 0; }
.sheet-fade-enter .sheet, .sheet-fade-leave-to .sheet { transform: translateY(100%); }

/* ── Expand btn ── */
.btn-expand {
  width: 26px; height: 26px; background: none;
  border: 1px solid var(--border); border-radius: 6px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; color: var(--muted); transition: all .15s;
}
.btn-expand:hover { border-color: var(--accent); color: var(--accent); }

.td-empty { text-align: center; color: var(--muted); padding: 40px !important; }

/* ── Shortlist panel ── */
.shortlist-row td { padding: 0; background: var(--bg-subtle); border-top: 2px solid transparent; border-image: linear-gradient(90deg, #FC7010, #B91E7C) 1; }
.shortlist-panel { padding: 20px 24px 24px; animation: fadeIn .2s ease; }

/* ── Group selector panel ── */
.group-selector-row td { padding: 0; background: var(--bg-subtle); border-bottom: 0.5px solid var(--border); }
.group-selector-panel {
  display: flex; flex-wrap: wrap; gap: 12px;
  background: var(--bg-subtle); border: 0.5px solid var(--border); border-radius: 16px;
  padding: 16px; margin: 12px; animation: fadeIn .2s ease;
}
.group-member-card {
  width: 200px; border: 0.5px solid var(--border); border-radius: 12px;
  padding: 12px 14px; background: var(--bg); cursor: pointer;
  transition: all .15s; display: flex; flex-direction: column; gap: 6px;
}
.group-member-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(10, 10, 11, 0.08);
}
.group-member-card.active {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(10, 10, 11, 0.08);
  border-width: 1.5px;
}

/* ══ INTAKE CONTROLS ══ */
.intake-controls {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid var(--border);
  flex-wrap: wrap;
  align-items: flex-start;
}

.control-group {
  position: relative;
}

.control-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: .07em;
  text-transform: uppercase;
  color: var(--text-faint);
  margin-bottom: 7px;
  font-family: 'Geist Mono', monospace;
  position: relative;
  padding-left: 10px;
}
.control-label::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 5px;
  height: 5px;
  background: var(--orange);
  border-radius: 50%;
}

/* ── Control input (select original) ── */
.control-input {
  padding: 8px 12px;
  background: var(--bg);
  border: 0.5px solid var(--border);
  border-radius: 10px;
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  color: var(--text);
  cursor: pointer;
  outline: none;
  appearance: none;
  -webkit-appearance: none;
  transition: border-color .15s;
}
.control-input:hover,
.control-input:focus { border-color: var(--orange); }

/* ── Comment trigger ── */
.control-group-wide { min-width: 260px; }
.comment-trigger {
  display: flex; align-items: center; gap: 8px;
  padding: 7px 10px; background: var(--bg);
  border: 0.5px solid var(--border); border-radius: 10px;
  min-height: 34px; cursor: pointer;
  transition: border-color .15s, background .15s;
  min-width: 240px; max-width: 360px;
}
.comment-trigger:hover { border-color: var(--orange); background: var(--bg-subtle); }
.comment-trigger-icon { color: var(--text-dim); flex-shrink: 0; }
.comment-trigger-text {
  flex: 1; font-size: 12px; color: var(--text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.comment-trigger-text.empty { color: var(--text-dim); font-style: italic; }
.comment-trigger-edit { flex-shrink: 0; color: var(--text-dim); opacity: 0; transition: opacity .15s; }
.comment-trigger:hover .comment-trigger-edit { opacity: 1; }

/* ── Comment popup ── */
.comment-popup-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.35);
  display: flex; align-items: center; justify-content: center; z-index: 2010;
}
.comment-popup {
  background: var(--bg); border-radius: 20px;
  box-shadow: 0 16px 48px rgba(0,0,0,.14);
  width: 420px; max-width: 92vw; overflow: hidden;
}
.comment-popup-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 0.5px solid var(--border);
}
.comment-popup-title { font-size: 13px; font-weight: 600; color: var(--text); font-family: 'Geist', sans-serif; }
.comment-popup-close {
  background: none; border: none; color: var(--text-dim);
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: color .15s; padding: 0;
}
.comment-popup-close:hover { color: var(--text); }
.comment-popup-textarea {
  display: block; width: 100%; padding: 14px 18px;
  border: none; outline: none; resize: none;
  font-family: 'Geist', sans-serif; font-size: 13px;
  color: var(--text); line-height: 1.6; background: var(--bg); box-sizing: border-box;
}
.comment-popup-textarea::placeholder { color: var(--text-dim); font-style: italic; }
.comment-popup-footer {
  display: flex; justify-content: flex-end; gap: 8px;
  padding: 12px 18px; border-top: 0.5px solid var(--border); background: var(--bg-subtle);
}
.comment-popup-cancel {
  padding: 7px 14px; border-radius: 10px; border: 0.5px solid var(--border);
  background: var(--bg); font-family: 'Geist', sans-serif;
  font-size: 12px; font-weight: 500; color: var(--text-dim); cursor: pointer; transition: all .15s;
}
.comment-popup-cancel:hover { border-color: var(--text); color: var(--text); }
.comment-popup-save {
  padding: 7px 16px; border-radius: 10px; border: none; background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%);
  font-family: 'Instrument Sans', sans-serif; font-size: 12px; font-weight: 600;
  color: #fff; cursor: pointer; transition: opacity .15s;
}
.comment-popup-save:hover { opacity: .88; }
.popup-fade-enter-active, .popup-fade-leave-active { transition: opacity .18s ease; }
.popup-fade-enter-active .comment-popup, .popup-fade-leave-active .comment-popup { transition: transform .18s ease; }
.popup-fade-enter, .popup-fade-leave-to { opacity: 0; }
.popup-fade-enter .comment-popup, .popup-fade-leave-to .comment-popup { transform: translateY(12px); }

/* ── Detail grid ── */
.detail-grid { display: flex; flex-wrap: wrap; gap: 14px 18px; margin-bottom: 20px; padding-bottom: 18px; border-bottom: 0.5px solid var(--border); }
.detail-block { min-width: 150px; }
.detail-full  { width: 100%; }
.detail-label {
  font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--text-faint);
  margin-bottom: 2px; font-family: 'Geist Mono', monospace;
}
.detail-label-strong { color: var(--text); font-weight: 700; }
.detail-val   { font-size: 13px; color: var(--text); white-space: normal; }

/* ── Score breakdown ── */
.score-breakdown {
  background: var(--bg); border: 0.5px solid var(--border);
  border-radius: 16px; padding: 14px 18px; margin-bottom: 20px;
  width: 50%; max-width: 560px; min-width: 320px;
}
.sb-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; }
.sb-title  { font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--text-faint); font-family: 'Geist Mono', monospace; }
.sb-total  { font-size: 12px; font-weight: 700; }
.sb-total.hot  { color: var(--hot);  }
.sb-total.warm { color: var(--warm); }
.sb-total.cool { color: var(--cool); }
.sb-total.cold { color: var(--cold); }

.sb-bars { display: flex; flex-direction: column; gap: 8px; }
.sb-item { display: flex; align-items: center; gap: 10px; }
.sb-label { font-size: 12px; width: 115px; flex-shrink: 0; }
.sb-track { flex: 1; height: 5px; background: var(--border); border-radius: 100px; overflow: hidden; }
.sb-fill  { height: 100%; border-radius: 100px; transition: width .5s ease; }
.sb-pts   { font-size: 11px; font-weight: 600; color: var(--muted); width: 38px; text-align: right; flex-shrink: 0; }

/* ── Shortlist section ── */
.sl-header-row { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.sl-section-title {
  font-size: 10px; font-weight: 600; letter-spacing: .07em; text-transform: uppercase; color: var(--text-faint);
  font-family: 'Geist Mono', monospace; position: relative; padding-left: 10px;
}
.sl-section-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  width: 5px;
  height: 5px;
  background: var(--orange);
  border-radius: 50%;
}
.sl-count-badge {
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%); color: #fff;
  border-radius: 100px; font-size: 10px; font-weight: 600;
  padding: 0 6px; font-family: 'Geist Mono', monospace;
}
.sl-empty { color: var(--muted); font-size: 13px; }

/* ══ SCROLL CONTAINER — CORREGIDO PARA ZOOM ══
   El padding-right en un overflow-x:auto container no forma parte
   del scrollWidth en muchos browsers. La solución es:
   1. Quitar padding-right del contenedor
   2. Poner el espacio al final como un elemento hijo (.sl-cards-spacer)
      o como padding en .sl-cards (que sí entra en el scrollWidth)
*/
.sl-cards-outer {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 10px;
  /* Sin padding-right aquí — se maneja en .sl-cards */
  scrollbar-width: thin;
  scrollbar-color: var(--accent) transparent;
  /* Evita que el contenedor padre recorte el scroll */
  box-sizing: border-box;
}
.sl-cards-outer::-webkit-scrollbar { height: 6px; }
.sl-cards-outer::-webkit-scrollbar-track { background: transparent; }
.sl-cards-outer::-webkit-scrollbar-thumb { background-color: var(--accent); border-radius: 999px; }

.sl-cards {
  display: inline-flex;
  gap: 12px;
  flex-wrap: nowrap;
  scroll-behavior: smooth;
  /* El padding en el hijo sí es incluido en el scrollWidth */
  padding-right: 24px;
  box-sizing: content-box;
  /* Asegura que el contenedor no fuerce un ancho menor al contenido */
  min-width: 100%;
}

/* Spacer invisible al final para garantizar scroll completo en todos los niveles de zoom */
.sl-cards-spacer {
  width: 1px;
  flex-shrink: 0;
  pointer-events: none;
}

.sl-card {
  background: var(--bg); border: 0.5px solid var(--border);
  border-radius: 16px; width: 260px; flex-shrink: 0; overflow: hidden;
  cursor: pointer; transition: all .15s;
}
.sl-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(10, 10, 11, 0.08);
  border-color: var(--border-strong);
}
.sl-card.tier-barata  { border-top: 3px solid #4ade80; }
.sl-card.tier-media   { border-top: 3px solid var(--orange); }
.sl-card.tier-premium { border-top: 3px solid #ffb347; }

/* Alojamiento seleccionado de la campaña (correo-erasmus, meta-*, tercera-campaña):
   tarjeta NARANJA para diferenciarlo de las alternativas de la shortlist. */
.sl-card.sl-card-elegido {
  border: 1.5px solid var(--orange);
  border-top: 3px solid var(--orange);
  background: var(--orange-soft);
  box-shadow: 0 0 0 1px rgba(252, 112, 16, 0.12);
}
.sl-card.sl-card-elegido .sl-card-header {
  background: rgba(252, 112, 16, 0.10);
  border-bottom-color: rgba(252, 112, 16, 0.22);
}
.sl-card.sl-card-elegido:hover {
  border-color: var(--orange);
  box-shadow: 0 4px 12px rgba(252, 112, 16, 0.18);
}
/* Badge naranja "ALOJAMIENTO ERASMUS" */
.sl-erasmus-badge {
  flex: 1;
  font-size: 10px; font-weight: 700; letter-spacing: .04em;
  text-transform: uppercase; border-radius: 5px; padding: 3px 7px;
  background: var(--orange); color: #fff;
  font-family: 'Geist Mono', monospace; white-space: nowrap;
}
/* Link al alojamiento en color normal, al lado del badge */
.sl-erasmus-link {
  white-space: nowrap;
}

.sl-card-header {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 13px; border-bottom: 0.5px solid var(--border);
  background: var(--bg-subtle);
}
.sl-tier-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; flex: 1; font-family: 'Geist Mono', monospace; }
.sl-match-badge { font-size: 10px; background: var(--orange-soft); color: var(--orange-text); border-radius: 100px; padding: 1px 7px; font-weight: 600; font-family: 'Geist Mono', monospace; }
.sl-estado-badge {
  font-size: 9px; font-weight: 700; letter-spacing: .05em;
  text-transform: uppercase; border-radius: 5px; padding: 2px 6px;
}
.sl-estado-badge.pendiente  { background: #f0f0ee; color: var(--muted); }
.sl-estado-badge.enviada    { background: rgba(59,130,246,.1); color: #3b82f6; }
.sl-estado-badge.abierta    { background: rgba(255,179,71,.12); color: var(--warm); }
.sl-estado-badge.reservada  { background: rgba(22,163,74,.1); color: var(--ok); }
.sl-estado-badge.descartada { background: rgba(229,57,53,.08); color: var(--danger); }

.sl-card-body { padding: 11px 13px; display: flex; flex-direction: column; gap: 6px; }
.sl-aloj-row  { display: flex; align-items: center; gap: 8px; }
.sl-aloj-label{ font-size: 11px; color: var(--muted); }
.sl-aloj-link { color: var(--accent); font-size: 13px; font-weight: 700; text-decoration: none; }
.sl-aloj-link:hover { opacity: .75; }
.sl-data-row  { display: flex; justify-content: space-between; gap: 8px; font-size: 11px; }
.sl-data-row span   { color: var(--muted); }
.sl-data-row strong { font-weight: 500; }

.sl-card-actions {
  display: flex; gap: 7px;
  padding: 9px 13px; border-top: 0.5px solid var(--border);
}

.btn-discard {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
  background: none; border: 0.5px solid var(--border);
  border-radius: 10px; padding: 7px 10px;
  font-family: 'Geist', sans-serif; font-size: 11px; font-weight: 500;
  color: var(--text-dim); cursor: pointer; transition: all .15s;
}
.btn-discard:hover:not(:disabled) { border-color: var(--danger); color: var(--danger); }
.btn-discard:disabled { opacity: .35; cursor: not-allowed; }

.sl-feedback {
  margin: 0 13px 10px; padding: 6px 10px;
  border-radius: 6px; font-size: 11px; font-weight: 500;
}
.sl-feedback.ok  { background: rgba(22,163,74,.08); color: var(--ok); }
.sl-feedback.err { background: rgba(229,57,53,.08); color: var(--danger); }

/* ── Botones copiar y agregar ── */
.sl-actions {
  display: flex;
  gap: 10px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.btn-copy,
.btn-add {
  flex: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  font-family: 'Geist', sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all .15s;
  border: 0.5px solid var(--border);
}

.btn-copy {
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%);
  color: #fff;
  border: none;
}

.btn-copy:hover {
  opacity: 0.9;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(185, 30, 124, 0.2);
}

.btn-add {
  background: var(--orange-soft);
  color: var(--orange-text);
  border-color: var(--orange-line);
}

.btn-add:hover {
  background: rgba(252, 112, 16, 0.12);
}

.copy-feedback {
  margin-top: 10px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.copy-feedback.ok {
  background: rgba(22, 163, 74, 0.1);
  color: var(--ok);
}

.copy-feedback.err {
  background: rgba(229, 57, 53, 0.1);
  color: var(--danger);
}

/* ── Modal ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  box-sizing: border-box;
  overflow-y: auto;
  /* Por encima del banner/header del layout (z-index 999–1039). Con un z-index
     menor el modal quedaba tapado por el banner, sobre todo en celular. */
  z-index: 2000;
  animation: fadeIn .15s ease;
}

.modal-box {
  background: var(--bg);
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  max-width: 420px;
  width: 90%;
  /* Modal alto: scroll interno en vez de desbordar la pantalla. El dvh es para
     el celular, donde la barra del navegador cambia de alto (se ignora si el
     navegador no lo soporta y queda el vh). */
  max-height: calc(100vh - 48px);
  max-height: calc(100dvh - 48px);
  overflow-y: auto;
  animation: slideUp .25s ease;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 0.5px solid var(--border);
}

.modal-header h2 {
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  letter-spacing: -0.01em;
  font-family: 'Geist', sans-serif;
}

.modal-close {
  background: none;
  border: none;
  color: var(--text-dim);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color .15s;
}

.modal-close:hover {
  color: var(--text);
}

.modal-body {
  padding: 20px;
}

.modal-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .07em;
  text-transform: uppercase;
  color: var(--text-faint);
  margin-bottom: 6px;
  font-family: 'Geist Mono', monospace;
}

.modal-input {
  width: 100%;
  padding: 10px 12px;
  background: var(--bg);
  border: 0.5px solid var(--border);
  border-radius: 10px;
  font-family: 'Geist', sans-serif;
  font-size: 14px;
  color: var(--text);
  outline: none;
  transition: border-color .15s;
}

.modal-input:focus {
  border-color: var(--orange);
  background: var(--bg-subtle);
}

.modal-input::placeholder {
  color: var(--text-faint);
}

.modal-hint {
  font-size: 11px;
  color: var(--muted);
  margin: 6px 0 0;
}

.modal-footer {
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  border-top: 0.5px solid var(--border);
  justify-content: flex-end;
  background: var(--bg-subtle);
}

.btn-cancel,
.btn-confirm {
  padding: 9px 16px;
  border-radius: 10px;
  font-family: 'Geist', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border: 0.5px solid var(--border);
  transition: all .15s;
}

.btn-cancel {
  background: var(--bg);
  color: var(--text-dim);
}

.btn-cancel:hover {
  border-color: var(--text);
  color: var(--text);
}

.btn-confirm {
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%);
  color: #fff;
  border: none;
}

.btn-confirm:hover:not(:disabled) {
  opacity: 0.9;
  transform: translateY(-1px);
}

.btn-confirm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-feedback {
  padding: 10px 16px;
  margin: 10px 20px 0;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  text-align: center;
}

.modal-feedback.ok {
  background: rgba(22, 163, 74, 0.1);
  color: var(--ok);
}

.modal-feedback.err {
  background: rgba(229, 57, 53, 0.1);
  color: var(--danger);
}

/* ── Modal de reparto de leads por campaña ──────────────────────────────────
   La pantalla tiene tres niveles de superficie para que se entienda dónde
   empieza y termina cada cosa: el fondo del modal es el gris más oscuro
   (--bg-muted), cada sección es una tarjeta blanca sobre ese fondo, y adentro
   de cada broker el horario vuelve a hundirse en gris. Blanco = editable. */
.modal-box--wide { max-width: 620px; }
/* El reparto necesita más ancho: entran los tres campos, el horario y el mapa
   de cobertura de 24 columnas. */
.modal-box--reparto { max-width: 780px; }

.rep-body { background: var(--bg-muted); padding: 16px; }

.rep-sec {
  border: 0.5px solid var(--border); border-radius: 14px;
  background: var(--bg); overflow: hidden;
  box-shadow: 0 1px 2px rgba(10, 10, 11, .03);
}
.rep-sec + .rep-sec { margin-top: 14px; }

.rep-sec-head {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 11px 14px;
  background: var(--bg-subtle); border-bottom: 0.5px solid var(--border);
}
.rep-sec-num {
  flex-shrink: 0; width: 20px; height: 20px; border-radius: 999px;
  display: flex; align-items: center; justify-content: center;
  background: var(--orange-soft); color: var(--orange-text);
  border: 0.5px solid var(--orange-line);
  font-family: 'Geist Mono', monospace; font-size: 10px; font-weight: 600;
  margin-top: 1px;
}
.rep-sec-titles { flex: 1; min-width: 0; }
.rep-sec-title {
  margin: 0; font-size: 13px; font-weight: 600; color: var(--text);
  letter-spacing: -.01em;
}
.rep-sec-sub { margin: 2px 0 0; font-size: 11px; color: var(--text-dim); line-height: 1.45; }
.rep-sec-body { padding: 14px; }

/* Contador de % en la cabecera de la sección de brokers */
.rep-sec-badge {
  flex-shrink: 0; padding: 3px 9px; border-radius: 999px;
  border: 0.5px solid var(--border); background: var(--bg);
  font-family: 'Geist Mono', monospace; font-size: 12px; font-weight: 600;
  color: var(--text-dim);
}
.rep-sec-badge.ok  { border-color: rgba(22, 163, 74, .45);  background: rgba(22, 163, 74, .08);  color: var(--ok); }
.rep-sec-badge.bad { border-color: rgba(229, 57, 53, .45); background: rgba(229, 57, 53, .07); color: var(--danger); }

.rep-codigo-row { display: flex; gap: 8px; align-items: center; }
.rep-codigo-row .modal-input { flex: 1; min-width: 0; }
.rep-codigo-row .btn-cancel { white-space: nowrap; }

.rep-vacio {
  padding: 18px 14px; border: 1px dashed var(--border-strong); border-radius: 12px;
  background: var(--bg-subtle); color: var(--text-dim);
  font-size: 12px; text-align: center; line-height: 1.6;
}

/* ── Tarjeta de un broker ── */
.rep-rows { display: flex; flex-direction: column; gap: 10px; }
.rep-row {
  display: flex; flex-direction: column; gap: 9px;
  padding: 11px 12px; border: 0.5px solid var(--border);
  border-left: 3px solid var(--orange-line);
  border-radius: 12px; background: var(--bg);
  transition: border-color .15s, box-shadow .15s;
}
/* Se ve cuál se está editando: la fila con el foco adentro se enciende. */
.rep-row:focus-within {
  border-color: var(--orange-line); border-left-color: var(--orange);
  box-shadow: 0 0 0 3px rgba(252, 112, 16, .1);
}
.rep-row.sophi { border-left-color: var(--magenta-line); }
.rep-row.sophi:focus-within { border-left-color: var(--magenta); box-shadow: 0 0 0 3px rgba(185, 30, 124, .1); }
/* Fuera del reparto: gris y en diagonal, para que no se confunda con uno activo */
.rep-row.inactive {
  border-left-color: var(--border-strong);
  background:
    var(--bg-subtle)
    repeating-linear-gradient(135deg, transparent 0 7px, rgba(10, 10, 11, .022) 7px 14px);
}
.rep-row.inactive .rep-row-main { opacity: .6; }

.rep-row-top { display: flex; align-items: center; gap: 9px; }
.rep-ava {
  flex-shrink: 0; width: 28px; height: 28px; border-radius: 999px;
  display: flex; align-items: center; justify-content: center;
  background: var(--orange-soft); color: var(--orange-text);
  border: 0.5px solid var(--orange-line);
  font-size: 12px; font-weight: 700; text-transform: uppercase;
}
.rep-row.sophi .rep-ava {
  background: var(--magenta-soft); color: var(--magenta-text);
  border-color: var(--magenta-line);
}
.rep-row.inactive .rep-ava {
  background: var(--bg-muted); color: var(--text-faint); border-color: var(--border-strong);
}
.rep-row-main { display: flex; gap: 8px; flex: 1; min-width: 0; align-items: center; }
.rep-input-name { flex: 0 0 120px; min-width: 0; font-weight: 600; }
.rep-input-mail { flex: 1; min-width: 0; }
.rep-pct-wrap { position: relative; flex: 0 0 82px; }
.rep-input-pct {
  width: 100%; padding-right: 24px; text-align: right;
  font-family: 'Geist Mono', monospace; font-weight: 600;
}
.rep-pct-sign {
  position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
  font-size: 11px; color: var(--text-faint); pointer-events: none;
  font-family: 'Geist Mono', monospace;
}
.rep-row-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }

/* Activo / fuera del reparto como interruptor: se lee de un vistazo */
.rep-activo {
  position: relative; display: inline-flex; align-items: center; gap: 6px;
  cursor: pointer; user-select: none;
}
.rep-activo-txt {
  font-size: 10px; font-weight: 600; color: var(--text-dim);
  font-family: 'Geist Mono', monospace; width: 34px;
}
/* El checkbox real se esconde detrás del interruptor, pero sigue siendo el que
   recibe el foco y el click: el label entero es su área. */
.rep-activo input { position: absolute; inset: 0; opacity: 0; margin: 0; cursor: pointer; }
.rep-activo-track {
  width: 34px; height: 20px; border-radius: 999px;
  background: var(--bg-muted); border: 0.5px solid var(--border-strong);
  display: flex; align-items: center; padding: 0 2px;
  transition: background .15s, border-color .15s;
}
.rep-activo-dot {
  width: 14px; height: 14px; border-radius: 999px; background: #fff;
  box-shadow: 0 1px 2px rgba(10, 10, 11, .25);
  transition: transform .15s;
}
.rep-activo input:checked + .rep-activo-track { background: var(--orange); border-color: var(--orange); }
.rep-activo input:checked + .rep-activo-track .rep-activo-dot { transform: translateX(14px); }
.rep-activo input:focus-visible + .rep-activo-track { box-shadow: 0 0 0 3px rgba(252, 112, 16, .2); }
.rep-row.sophi .rep-activo input:checked + .rep-activo-track { background: var(--magenta); border-color: var(--magenta); }

/* Proporción real dentro del reparto */
.rep-share { display: flex; align-items: center; gap: 9px; padding-left: 37px; }
.rep-share-bar {
  flex: 1; height: 5px; border-radius: 999px;
  background: var(--bg-muted); overflow: hidden;
}
.rep-share-fill {
  display: block; height: 100%; border-radius: 999px;
  background: var(--orange); transition: width .2s;
}
.rep-row.sophi .rep-share-fill { background: var(--magenta); }
.rep-row.inactive .rep-share-fill { background: var(--border-strong); }
.rep-share-txt {
  flex-shrink: 0; font-size: 10.5px; color: var(--text-dim);
  font-family: 'Geist Mono', monospace;
}

/* ── Calculadora de objetivo ──
   Va alineada con la barra de arriba (mismo padding izquierdo) porque es la
   misma cuenta al revés: la barra dice qué se lleva, esto dice qué cargar. */
.rep-objetivo {
  display: flex; align-items: center; gap: 7px; flex-wrap: wrap;
  padding-left: 37px;
}
.rep-objetivo-label {
  font-family: 'Geist Mono', monospace; font-size: 9.5px; font-weight: 600;
  letter-spacing: .06em; text-transform: uppercase; color: var(--text-faint);
}
.rep-objetivo-num { position: relative; }
.rep-objetivo-input {
  width: 68px; padding-right: 18px; font-size: 11px;
  font-family: 'Geist Mono', monospace;
}
.rep-objetivo-sign {
  position: absolute; right: 7px; top: 50%; transform: translateY(-50%);
  font-size: 10px; color: var(--text-faint); pointer-events: none;
}
.rep-objetivo-modo { width: auto; font-size: 11px; padding: 5px 8px; }
.rep-objetivo-msg {
  flex-basis: 100%; font-size: 10.5px; line-height: 1.45;
  color: var(--text-dim);
}
.rep-objetivo-msg.ok { color: var(--ok); }
.rep-objetivo-msg.err { color: var(--danger); }
.rep-row.inactive .rep-objetivo { opacity: .5; }

/* ── Horario del broker ── */
.rep-horario { border-radius: 10px; background: var(--bg-subtle); border: 0.5px solid var(--border); }
.rep-horario.abierto { border-color: var(--border-strong); }
.rep-horario-head { display: flex; align-items: center; gap: 8px; padding: 7px 9px; }
.rep-horario-ico { flex-shrink: 0; font-size: 12px; }
.rep-horario-resumen {
  flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-family: 'Geist Mono', monospace; font-size: 11px; color: var(--text);
}
.rep-horario-resumen.libre { color: var(--text-dim); font-style: italic; font-family: 'Geist', sans-serif; }
.rep-horario-btn {
  flex-shrink: 0; padding: 3px 10px; border-radius: 999px;
  border: 0.5px solid var(--border-strong); background: var(--bg);
  color: var(--text-dim); font-family: 'Geist', sans-serif;
  font-size: 10.5px; font-weight: 600; cursor: pointer; transition: all .12s;
}
.rep-horario-btn:hover { border-color: var(--orange); color: var(--orange-text); background: var(--orange-soft); }

.rep-horario-body {
  padding: 10px; border-top: 0.5px solid var(--border);
  background: var(--bg-muted); border-radius: 0 0 10px 10px;
}
.rep-mini-label {
  font-family: 'Geist Mono', monospace; font-size: 9.5px; font-weight: 600;
  letter-spacing: .08em; text-transform: uppercase; color: var(--text-faint);
}
.rep-chips-wrap { display: flex; align-items: center; gap: 10px; }
.rep-chips { display: flex; gap: 5px; flex-wrap: wrap; }
.rep-chip {
  width: 30px; height: 28px; border-radius: 8px;
  border: 0.5px solid var(--border-strong); background: var(--bg);
  color: var(--text-faint); font-family: 'Geist Mono', monospace;
  font-size: 11px; font-weight: 600; cursor: pointer; transition: all .12s;
}
.rep-chip:hover { border-color: var(--text-dim); color: var(--text); }
.rep-chip.on {
  background: var(--orange); border-color: var(--orange); color: #fff;
  box-shadow: 0 1px 3px rgba(252, 112, 16, .3);
}
.rep-row.sophi .rep-chip.on { background: var(--magenta); border-color: var(--magenta); box-shadow: 0 1px 3px rgba(185, 30, 124, .3); }

.rep-dias { display: flex; flex-direction: column; gap: 5px; margin-top: 10px; }
.rep-dia {
  display: flex; align-items: center; gap: 8px;
  padding: 5px 8px; border-radius: 8px;
  background: var(--bg); border: 0.5px solid var(--border);
}
.rep-dia-label {
  flex: 0 0 34px; font-size: 11.5px; font-weight: 600; color: var(--text);
}
.rep-hora {
  flex: 0 0 104px; padding: 4px 8px; font-size: 11.5px;
  font-family: 'Geist Mono', monospace; background: var(--bg-subtle);
}
.rep-hora:focus { background: var(--bg); }
.rep-dia-sep { font-size: 11px; color: var(--text-faint); }
.rep-dia-nota {
  font-size: 9.5px; color: var(--text-dim); padding: 2px 7px;
  background: var(--bg-subtle); border: 0.5px solid var(--border); border-radius: 999px;
  font-family: 'Geist Mono', monospace;
}
.rep-dia-x {
  margin-left: auto; width: 22px; height: 22px; border-radius: 6px;
  border: 0; background: none; color: var(--text-faint);
  font-size: 11px; cursor: pointer; transition: all .12s;
}
.rep-dia-x:hover { background: rgba(229, 57, 53, .08); color: var(--danger); }
.rep-dias-vacio {
  margin: 0; padding: 9px 10px; border-radius: 8px;
  background: var(--bg); border: 1px dashed var(--border-strong);
  font-size: 11px; color: var(--text-dim); text-align: center;
}

.rep-horario-presets {
  display: flex; flex-wrap: wrap; gap: 6px;
  margin-top: 10px; padding-top: 9px; border-top: 0.5px solid var(--border);
}

.rep-add-row { display: flex; gap: 8px; margin-top: 12px; flex-wrap: wrap; }
.rep-add-row .modal-input { flex: 1; min-width: 160px; }
.rep-add-row .btn-cancel { white-space: nowrap; }

.rep-total {
  display: flex; align-items: center; gap: 8px;
  margin-top: 12px; padding: 10px 14px;
  border: 0.5px solid var(--border); border-radius: 12px;
  background: var(--bg-subtle);
  font-size: 12px; color: var(--text-dim);
}
.rep-total strong { font-size: 15px; color: var(--text); font-family: 'Geist Mono', monospace; }
.rep-total.ok  { border-color: rgba(22, 163, 74, .4);  background: rgba(22, 163, 74, .06);  }
.rep-total.ok strong { color: var(--ok); }
.rep-total.bad { border-color: rgba(229, 57, 53, .4); background: rgba(229, 57, 53, .05); }
.rep-total.bad strong { color: var(--danger); }
.rep-total-msg { margin-left: auto; font-weight: 600; }

/* ── Mapa de cobertura: 7 días × 24 h ──
   Una sola escala (el naranja de la marca) para "cuántos brokers hay", y las
   horas sin nadie van rayadas además de grises: el hueco se distingue sin
   depender del color. El detalle en texto va abajo, con los mismos datos. */
.rep-heat {
  border: 0.5px solid var(--border); border-radius: 12px;
  background: var(--bg-subtle); padding: 10px 12px; overflow-x: auto;
}
.rep-heat-hours, .rep-heat-row { display: flex; align-items: center; gap: 2px; min-width: 420px; }
.rep-heat-row + .rep-heat-row { margin-top: 2px; }
.rep-heat-daylabel {
  flex: 0 0 22px; font-family: 'Geist Mono', monospace;
  font-size: 10px; font-weight: 600; color: var(--text-dim); text-align: right;
  padding-right: 4px;
}
.rep-heat-hours { margin-bottom: 4px; }
.rep-heat-hour {
  flex: 1; font-family: 'Geist Mono', monospace; font-size: 9px;
  color: var(--text-faint); text-align: left;
}
.rep-heat-cell {
  flex: 1; height: 15px; border-radius: 3px; min-width: 9px;
  background: var(--bg-muted);
}
/* Sin cobertura: gris + rayado diagonal */
.rep-heat-cell.n0 {
  background:
    repeating-linear-gradient(45deg,
      var(--bg-muted) 0 3px,
      var(--border-strong) 3px 4px);
  box-shadow: inset 0 0 0 0.5px var(--border-strong);
}
.rep-heat-cell.n1 { background: var(--orange-soft); }
.rep-heat-cell.n2 { background: var(--orange-line); }
.rep-heat-cell.n3 { background: var(--orange); }
.rep-heat-leyenda {
  display: flex; flex-wrap: wrap; gap: 12px; margin-top: 9px;
  padding-top: 8px; border-top: 0.5px solid var(--border);
  font-size: 10px; color: var(--text-dim);
}
.rep-heat-key { display: inline-flex; align-items: center; gap: 5px; }
.rep-heat-key .rep-heat-cell { flex: 0 0 14px; width: 14px; height: 11px; }

/* Tramos de la semana que no cubre nadie */
.rep-cobertura {
  margin-top: 10px; padding: 10px 14px;
  border: 0.5px solid rgba(217, 119, 6, .4); border-radius: 12px;
  background: rgba(217, 119, 6, .07);
  font-size: 11.5px; color: var(--text);
}
.rep-cobertura.ok {
  border-color: rgba(22, 163, 74, .35); background: rgba(22, 163, 74, .06);
  color: var(--text-dim);
}
.rep-cobertura-head { font-weight: 600; margin-bottom: 6px; }
.rep-cobertura-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 3px; }
.rep-cobertura-list li { display: flex; gap: 8px; }
.rep-cobertura-list strong { flex: 0 0 34px; }
.rep-cobertura-list span { font-family: 'Geist Mono', monospace; color: var(--text-dim); }

/* ── Duplicar el reparto en otras campañas ── */
/* Por encima del modal de reparto, que ya está en z-index 2000. */
.modal-overlay--top { z-index: 2100; }

.btn-cancel--mini { padding: 5px 10px; font-size: 11px; }

/* Resumen de lo que se va a copiar */
.dup-preview {
  border: 0.5px solid var(--border);
  border-radius: 12px;
  background: var(--bg-subtle);
  padding: 10px 14px;
}
.dup-preview__head {
  display: flex; align-items: baseline; gap: 8px;
  padding-bottom: 8px; margin-bottom: 8px;
  border-bottom: 0.5px solid var(--border);
}
.dup-preview__codigo {
  font-family: 'Geist Mono', monospace;
  font-size: 12px; font-weight: 600; color: var(--text);
  overflow-wrap: anywhere;
}
.dup-preview__total {
  margin-left: auto;
  font-family: 'Geist Mono', monospace;
  font-size: 13px; font-weight: 700; color: var(--ok);
}
.dup-preview__list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; }
.dup-preview__list li { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--text-dim); }
.dup-preview__list li.inactive { opacity: .5; }
.dup-preview__name { text-transform: capitalize; }
.dup-preview__horario {
  margin-left: auto; max-width: 55%;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  font-size: 10.5px; color: var(--text-dim); font-family: 'Geist Mono', monospace;
}
.dup-preview__pct { font-family: 'Geist Mono', monospace; color: var(--text); }
.dup-preview__off { font-size: 10px; color: var(--muted); }

.dup-pick-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; margin: 16px 0 8px;
}

.dup-list {
  display: flex; flex-direction: column; gap: 2px;
  max-height: 260px; overflow-y: auto;
  border: 0.5px solid var(--border); border-radius: 12px;
  padding: 6px;
}
.dup-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 10px; border-radius: 8px;
  cursor: pointer; font-size: 13px; color: var(--text);
  transition: background .12s;
}
.dup-item:hover { background: var(--bg-subtle); }
.dup-item input { accent-color: var(--orange); flex-shrink: 0; cursor: pointer; }
.dup-item__label { min-width: 0; overflow-wrap: anywhere; }

@media (max-width: 560px) {
  .dup-pick-head { flex-wrap: wrap; }
  .dup-list { max-height: 46vh; }
}

/* ── Modal de borrado: elegir si se borra un intake o todo el grupo ── */
.del-question { margin: 0 0 14px; font-size: 13px; line-height: 1.55; color: var(--text); }
.del-opt {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 11px 12px; margin-bottom: 8px;
  border: 0.5px solid var(--border); border-radius: 12px;
  background: var(--bg); cursor: pointer;
  transition: border-color .15s, background .15s;
}
.del-opt:hover { border-color: var(--border-strong); }
.del-opt.active { border-color: var(--danger); background: rgba(229, 57, 53, .04); }
.del-opt input[type="radio"] { margin-top: 2px; accent-color: var(--danger); flex-shrink: 0; }

/* Opción "solo un intake": radio arriba y el selector de cuál debajo */
.del-opt--pick { flex-direction: column; align-items: stretch; gap: 9px; cursor: default; }
.del-opt-head { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; }
.del-opt-select {
  width: 100%; padding: 9px 12px;
  background: var(--bg); border: 0.5px solid var(--border);
  border-radius: 10px; font-family: 'Geist', sans-serif;
  font-size: 12px; color: var(--text); cursor: pointer; outline: none;
}
.del-opt-select:focus { border-color: var(--danger); }
.del-opt-body { display: flex; flex-direction: column; gap: 3px; min-width: 0; }
.del-opt-title { font-size: 13px; font-weight: 600; color: var(--text); }
.del-opt-sub {
  font-size: 11px; color: var(--text-dim);
  display: flex; flex-wrap: wrap; gap: 4px;
  font-family: 'Geist Mono', monospace; overflow-wrap: anywhere;
}
.del-opt-chip {
  background: var(--bg-subtle); border: 0.5px solid var(--border);
  border-radius: 6px; padding: 1px 6px;
}

.btn-confirm--danger { background: var(--danger); }

/* ── Usuarios de Discord ── */
.discord-add-row { display: flex; flex-direction: column; gap: 8px; }
.discord-add-row .btn-confirm { align-self: flex-end; }
.discord-list { max-height: 300px; overflow-y: auto; display: flex; flex-direction: column; gap: 8px; }
.discord-list-item {
  display: flex; align-items: center; justify-content: space-between;
  padding: 8px 12px; border: 0.5px solid var(--border);
  border-radius: 8px; background: var(--bg);
}
.discord-list-item.editing { border-color: var(--orange); background: var(--orange-soft); }
.discord-list-actions { display: flex; align-items: center; gap: 6px; flex-shrink: 0; }
.discord-list-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.discord-list-name { font-size: 13px; font-weight: 600; color: var(--text); }
.discord-list-handle { font-size: 11px; color: var(--orange); }
.discord-list-id { font-size: 11px; color: var(--text-dim); font-family: monospace; }

/* ── Pagination ── */
.pagination { display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 24px; }
.btn-page {
  background: var(--bg); border: 0.5px solid var(--border);
  border-radius: 10px; padding: 7px 16px;
  font-family: 'Geist', sans-serif; font-size: 12px;
  color: var(--text); cursor: pointer; transition: all .15s;
}
.btn-page:hover:not(:disabled) { border-color: var(--orange); color: var(--orange); }
.btn-page:disabled { opacity: .3; cursor: not-allowed; }
.page-dots { display: flex; gap: 3px; }
.page-dot {
  width: 30px; height: 30px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; cursor: pointer; color: var(--text-dim); transition: all .15s;
  font-family: 'Geist Mono', monospace;
}
.page-dot:hover:not(.ellipsis) { background: var(--bg-muted); color: var(--text); }
.page-dot.active  { background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%); color: #fff; font-weight: 600; cursor: default; }
.page-dot.ellipsis{ cursor: default; }
.page-dot.disabled{ opacity: .45; pointer-events: none; }

/* ── Animaciones ── */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: all .2s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter .modal-box,
.modal-fade-leave-to .modal-box {
  transform: translateY(20px);
}

/* ══════════════ RESPONSIVE: TABLET Y PANTALLA CHICA ══════════════ */
/* Desde 761px la tabla sigue siendo una tabla: se scrollea en horizontal en vez
   de comprimir las columnas hasta que el texto se pise. Abajo de eso pasa a
   tarjetas (ver bloque móvil). El corte está en 1320px porque abajo de ~1262px
   de tabla los porcentajes de las columnas ya no alcanzan para su contenido. */
@media (min-width: 761px) and (max-width: 1319px) {
  .dash-root { padding: 20px 18px 60px; }

  /* Header: permitir que el bloque de acciones baje de línea */
  .dash-header { flex-wrap: wrap; gap: 12px; }
  .header-actions { flex-wrap: wrap; }

  /* Panel expandido: apilar controles y desglose a ancho completo */
  .intake-controls { flex-direction: column; gap: 16px; }
  .score-breakdown { width: 100%; min-width: 0; }

  /* Scroll horizontal en vez de comprimir columnas. Esto vuelve al contenedor
     un scroller, así que acá el encabezado sticky no se fija: se acepta. */
  .table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
  .dash-table { min-width: 1262px; }
  .dash-table th { position: static; }
}

/* ══════════════ RESPONSIVE: MÓVIL ══════════════ */
/* En celular la tabla no se scrollea en horizontal: cada intake pasa a ser una
   tarjeta apilada. Los <td> se convierten en bloques y el nombre de la columna
   se pinta con ::before a partir del data-label del template. */
@media (max-width: 760px) {
  /* Nada debe generar scroll lateral en el body */
  .dash-root { padding: 14px 12px 44px; overflow-x: hidden; }

  /* ── Header ── */
  .dash-header { flex-direction: column; align-items: stretch; gap: 12px; }
  .dash-header-left { gap: 10px; }
  h1 { font-size: 18px; }
  .dash-logo { width: 36px; height: 36px; }
  .header-actions { flex-wrap: wrap; gap: 8px; }
  .header-actions .btn-generate,
  .header-actions .btn-export {
    flex: 1 1 calc(50% - 4px); justify-content: center; height: 40px;
  }
  .header-actions .btn-icon { width: 40px; height: 40px; flex: 0 0 40px; }

  /* ── Búsqueda y filtros ──
     16px en los campos: abajo de eso iOS hace zoom solo al enfocarlos y la
     pantalla queda corrida. */
  .search-bar { flex-direction: column; gap: 8px; }
  .search-input-wrap { min-width: 0; width: 100%; }
  .search-input { font-size: 16px; padding: 11px 34px; }
  .search-clear { width: 28px; height: 28px; font-size: 18px; }
  .search-filters {
    width: 100%; display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px;
  }
  .filter-select {
    width: 100%; min-width: 0; font-size: 16px; min-height: 42px;
    padding: 10px 30px 10px 14px;
    /* El select tiene appearance:none: sin esta flecha no se lee como desplegable */
    background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23A0A0A8' stroke-width='2.5' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
  }
  .filter-date-group { grid-column: 1 / -1; width: 100%; justify-content: space-between; padding: 6px 12px; }
  .filter-date { font-size: 15px; min-width: 0; flex: 1 1 auto; }

  /* ── Tabla → tarjetas ── */
  .table-wrap {
    background: transparent; border: 0; border-radius: 0;
    overflow: visible;
  }
  .dash-table {
    display: block; width: 100%; min-width: 0;
    table-layout: auto; font-size: 13px;
  }
  /* En tarjeta el encabezado no existe: el nombre de cada dato lo pinta el
     ::before del data-label. Y sin thead, tampoco hay sticky que valga. */
  .dash-table thead { display: none; }
  .dash-table tbody {
    display: block; margin-bottom: 10px;
    background: var(--bg); border: 0.5px solid var(--border);
    border-radius: 16px; overflow: hidden;
  }
  .dash-table tr { display: block; }
  .dash-table td {
    display: block; padding: 0;
    white-space: normal; overflow-wrap: anywhere;
  }
  /* En la tarjeta hay ancho de sobra: el texto baja de línea en vez de
     recortarse con puntos suspensivos como en la tabla. */
  .dash-table .cell-clip {
    white-space: normal; overflow: visible; text-overflow: clip;
  }

  .intake-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    padding: 14px;
    border-bottom: 0;
    /* Sin el flash azul del tap; el feedback lo da :active */
    -webkit-tap-highlight-color: transparent;
  }
  .intake-row:active { background: var(--bg-subtle); }
  /* El dato queda debajo del nombre de su columna */
  .intake-row td[data-label]::before {
    content: attr(data-label);
    display: block;
    font-family: 'Geist Mono', monospace;
    font-size: 9px; font-weight: 600; letter-spacing: .06em;
    text-transform: uppercase; color: var(--text-faint);
    margin-bottom: 4px;
  }

  /* Fila 1 de la tarjeta: ID a la izquierda, las tres acciones a la derecha */
  .intake-row .td-id {
    order: -2; display: flex; align-items: center; gap: 10px;
  }
  .intake-row .td-id::before { display: none; }
  .intake-row .id-badge { font-size: 12px; padding: 3px 9px; }
  .intake-row .td-acciones {
    order: -1; display: flex; align-items: center;
    justify-content: flex-end; gap: 10px; padding-left: 0;
  }
  .intake-row .acciones-wrap { gap: 10px; }

  /* Datos que ocupan el ancho completo de la tarjeta */
  .intake-row .td-lead,
  .intake-row .td-admin,
  .intake-row .td-registro { grid-column: 1 / -1; }

  .intake-row .td-lead { min-width: 0; }
  .intake-row .lead-nombre { font-size: 15px; font-weight: 600; }
  /* El teléfono, el origen y los badges se apilan si no entran en una línea */
  .intake-row .lead-foot { flex-wrap: wrap; row-gap: 4px; overflow: visible; }
  .intake-row .lead-origen { flex-shrink: 0; }
  .intake-row .td-fechas { min-width: 0; }
  .intake-row .td-etapa { max-width: none; }
  .intake-row .badge-etapa { max-width: 100%; }

  /* Registro: fecha y hora en una sola línea, que en la tarjeta hay lugar */
  .intake-row .td-registro { display: flex; align-items: baseline; gap: 6px; flex-wrap: wrap; }
  .intake-row .td-registro::before { flex: 0 0 100%; }
  .intake-row .reg-hora { margin-top: 0; }

  /* El score deja de estar centrado: alineado como el resto de los datos */
  .intake-row .td-score { text-align: left; }
  .intake-row .td-score .score-wrap { flex-direction: row; align-items: center; gap: 8px; }

  /* Asignar admin es la acción más usada: en celular el <select> nativo se
     reemplaza por un botón que abre el panel inferior (.sheet). */
  .intake-row .td-admin { text-align: left; }
  .intake-row .admin-select,
  .control-group .control-input.admin-select-desktop { display: none; }
  .admin-picker-btn {
    display: flex; align-items: center; justify-content: space-between; gap: 8px;
    width: 100%; min-height: 42px; padding: 9px 12px;
    border: 1px solid var(--border); border-radius: 10px;
    background: rgba(148,163,184,.1); color: var(--cool);
    font-family: 'Geist', sans-serif; font-size: 14px; font-weight: 600;
    cursor: pointer; text-align: left;
  }
  .admin-picker-btn.filled {
    background: rgba(255,122,0,.1); color: var(--accent);
    border-color: rgba(255,122,0,.35);
  }
  .admin-picker-val { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .admin-picker-btn svg { flex-shrink: 0; }

  .intake-row .td-registro { padding-top: 2px; }

  /* Objetivos táctiles: 36px mínimo en los botones de la tarjeta */
  .intake-row .btn-expand,
  .intake-row .btn-chat,
  .intake-row .btn-delete {
    width: 36px; height: 36px; flex-shrink: 0; margin-right: 0; border-radius: 10px;
  }

  /* Filas de detalle (grupo y panel expandido): ya no dependen del colspan */
  .group-selector-row td,
  .shortlist-row td { display: block; width: 100%; padding: 0; }
  .td-empty { display: block; padding: 30px 16px !important; }

  /* ── Panel expandido ── */
  .shortlist-panel { padding: 14px; }
  .detail-grid { gap: 12px 14px; }
  .detail-block { min-width: 120px; }
  .intake-controls { flex-direction: column; gap: 14px; }
  .control-group, .control-group-wide { width: 100%; min-width: 0; }
  .control-input { width: 100%; font-size: 16px; min-height: 42px; }
  .comment-trigger { min-width: 0; max-width: none; width: 100%; min-height: 42px; }
  .comment-trigger-edit { opacity: 1; }
  .score-breakdown { width: 100%; min-width: 0; padding: 14px; }
  .sb-label { width: 96px; font-size: 11px; }
  .group-selector-panel { padding: 12px; margin: 10px; }
  .group-member-card { width: 100%; }

  /* Shortlist: carrusel con enganche, una tarjeta por pantalla */
  .sl-cards-outer { scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; }
  .sl-card { width: 78vw; max-width: 280px; scroll-snap-align: start; }
  .sl-actions { flex-wrap: wrap; }

  /* ── Modales: casi a pantalla completa, con scroll interno ── */
  /* Centrado: el overlay va por encima del banner, así que no hace falta
     empujarlo hacia abajo y se aprovecha toda la pantalla. */
  .modal-overlay { padding: 16px 12px; align-items: center; }
  .modal-box {
    width: 100%; max-width: 100%; overflow-y: auto;
    max-height: calc(100vh - 32px);
    max-height: calc(100dvh - 32px);
  }
  .modal-input, .modal-box select, .modal-box textarea { font-size: 16px; }
  .modal-footer { flex-direction: column-reverse; }
  .btn-cancel, .btn-confirm { width: 100%; min-height: 44px; }
  .modal-close { width: 36px; height: 36px; }

  /* Reparto: cada fila se apila para que entren los tres campos */
  .rep-body { padding: 12px; }
  .rep-sec-body { padding: 12px; }
  .rep-codigo-row { flex-direction: column; align-items: stretch; }
  .rep-codigo-row .btn-cancel { width: 100%; min-height: 42px; }
  .rep-row-main { flex-direction: column; align-items: stretch; }
  .rep-input-name, .rep-pct-wrap { flex: 1 1 auto; }
  .rep-input-pct { text-align: left; }
  /* El avatar y el interruptor se quedan arriba, con los campos apilados en el
     medio: así la tarjeta sigue leyéndose como una sola unidad. */
  .rep-row-top { align-items: flex-start; }
  .rep-ava { margin-top: 4px; }
  .rep-row-actions { flex-direction: column; gap: 8px; margin-top: 4px; }
  .rep-share { padding-left: 0; }

  /* Horario: el día arriba y las dos horas abajo, para que se puedan tocar */
  .rep-chips-wrap { flex-direction: column; align-items: flex-start; gap: 6px; }
  .rep-chip { width: 38px; height: 34px; font-size: 12px; }
  .rep-dia { flex-wrap: wrap; row-gap: 6px; }
  .rep-dia-label { flex: 0 0 100%; }
  .rep-hora { flex: 1 1 40%; min-height: 36px; font-size: 16px; }
  /* width auto: se reparten de a dos por fila en vez de ocupar el ancho entero
     como el resto de los botones del modal en móvil. */
  .rep-horario-presets .btn-cancel { flex: 1 1 45%; width: auto; min-height: 38px; }
  .rep-horario-btn { min-height: 30px; }

  /* El mapa de cobertura no se comprime: se scrollea de costado */
  .rep-heat-hours, .rep-heat-row { min-width: 380px; }
  .rep-add-row { flex-direction: column; }
  .rep-add-row .modal-input, .rep-add-row .btn-cancel { width: 100%; min-height: 42px; }

  /* Opciones del modal de borrado: tocables sin apuntar al radio */
  .del-opt { padding: 13px 12px; }
  .del-opt input[type="radio"] { width: 20px; height: 20px; }
  .del-opt-select { min-height: 42px; }

  .comment-popup { width: 100%; max-width: 94vw; }
  .comment-popup-textarea { font-size: 16px; }
  .comment-popup-close { width: 36px; height: 36px; }
  .comment-popup-cancel, .comment-popup-save { min-height: 42px; flex: 1; }

  /* ── Paginación ── */
  .pagination { flex-wrap: wrap; gap: 8px; }
  .btn-page { min-height: 40px; }
  .page-dot { width: 34px; height: 34px; }
}
</style>
